import JobAnalyticsChart from './JobAnalyticsChart';
import useSidebarState from '../hooks/useSidebarState';
import { systemMessage, systemRecordText } from '../utils/localizedSystemMessages';
import { getUiLocale } from '../utils/uiTranslation';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import HomeFeedback, { useHomeFeedback } from './HomeFeedback';
import { waitForJobPublication } from '../utils/waitForJobPublication';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, set, push, remove, update, onValue } from 'firebase/database';
import { database } from '../firebase';
import './Home.css';
import LinkedInSetupModal from './LinkedIn/LinkedInSetupModal';
import LinkedInSetupBanner from './LinkedIn/LinkedInSetupBanner';
import { normalizeLinkedInCompanyId, getLinkedInCompanyId, parseLinkedInCompanyId } from '../utils/linkedInCompany';
import '../styles/tutorial.css';
import Sidebar from './Sidebar';
import { useAccessControl } from '../hooks/useAccessControl';
import SubscriptionPopup from './applicants/SubscriptionPopup';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  APP_LINK_ARCHIVE_FIELD,
  archiveCompanyApplicationTargets,
  restoreCompanyApplicationTargets,
  clearCompanyApplicationTargetArchive
} from '../utils/jobApplicationTargets';
import {
  buildWhatsAppApplicationTarget,
  getWhatsAppNumberFromTarget,
  hasHostedJobApplications,
  normalizeJobApplicationTarget
} from '../utils/jobPostingApplication';
import { countries } from '../utils/countries';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { translateValue, getLocalizedFromMultilang, translateDepartment } from '../utils/translationUtils';
import { useApprovalSettings } from '../hooks/useApprovalSettings';
import useTeamAccessProfile from '../hooks/useTeamAccessProfile';
import useTeamMembers from '../hooks/useTeamMembers';
import { emitAtsAnalyticsEvent } from '../services/atsAnalyticsService';
import { inferJobSeniority } from '../utils/jobFormDefaults';
import { buildRoleAnalyticsFields } from '../utils/analyticsEntityFields';

const UNGROUPED_JOB_TAG_KEY = '__ungrouped__';
const PUBLIC_APPLICATION_MAX_FIELDS = 20;
const PUBLIC_APPLICATION_FIELD_TYPES = ['text', 'textarea', 'url', 'file', 'video', 'boolean'];
const LEGACY_VIDEO_SUBMISSION_SYSTEM_KEY = 'videoSubmission';
const VIDEO_SUBMISSION_FIELD_LABEL = 'Video submission';
const PUBLIC_APPLICATION_FIXED_FIELDS = [
  { id: 'first_name', type: 'text', required: true, icon: 'fa-user' },
  { id: 'last_name', type: 'text', required: true, icon: 'fa-user' },
  { id: 'email', type: 'text', required: true, icon: 'fa-envelope' },
  { id: 'cv_upload', type: 'file', required: true, icon: 'fa-file-arrow-up' }
];
const PUBLIC_APPLICATION_PRESET_FIELDS = {
  coverLetter: {
    id: 'cover_letter_upload',
    label: 'Cover Letter',
    type: 'file',
    required: false,
    systemKey: 'coverLetter'
  },
  portfolioLink: {
    id: 'portfolio_link',
    label: 'Portfolio Link',
    type: 'url',
    required: false,
    systemKey: 'portfolioLink'
  },
  linkedinUrl: {
    id: 'linkedin_url',
    label: 'LinkedIn URL',
    type: 'url',
    required: false,
    systemKey: 'linkedinUrl'
  }
};

function normalizeDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildHiringPlan(targetHireDate = '') {
  return {
    targetHireDate: normalizeDateInputValue(targetHireDate)
  };
}

function getDefaultJobFormData(ownerRecruiterId = '') {
  return {
    jobOpening: '',
    salary: '',
    negotiability: 'Negotiable',
    jobCategory: '',
    mode1: 'Full-Time',
    mode2: 'In-Person',
    appWindow: '',
    applicationMethod: 'ats',
    enableFiltering: false,
    department: '',
    seniority: '',
    experienceYears: '',
    jobCity: '',
    jobCountry: '',
    postingCompanyName: '',
    linkedinCompanyId: '',
    targetHireDate: '',
    ownerRecruiterId
  };
}

function normalizeJobModalDescriptionText(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeJobModalDescriptionHtml(value = '') {
  const html = String(value || '').trim();
  if (!html || html === '<p><br></p>') return '';
  return html;
}

function normalizeTagLabel(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function buildAnalyticsKey(value) {
  const normalized = normalizeTagLabel(value).toLowerCase();
  if (!normalized) return '';
  return normalized
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

function normalizeTagObjects(values = []) {
  const seen = new Set();
  return values.reduce((acc, value) => {
    const label = normalizeTagLabel(value?.label ?? value);
    const key = buildAnalyticsKey(label);
    if (!label || !key || seen.has(key)) return acc;
    seen.add(key);
    acc.push({ key, label });
    return acc;
  }, []);
}

function buildJobTagTaxonomy({ tagGroups = [], ungroupedTags = [] } = {}) {
  const normalizedGroups = [];
  const seenGroupKeys = new Set();

  tagGroups.forEach((group) => {
    const label = normalizeTagLabel(group?.label ?? group);
    const key = buildAnalyticsKey(label);
    if (!label || !key || seenGroupKeys.has(key)) return;
    seenGroupKeys.add(key);
    normalizedGroups.push({
      key,
      label,
      tags: normalizeTagObjects(group?.tags || [])
    });
  });

  const normalizedUngroupedTags = normalizeTagObjects(ungroupedTags);
  const flattenedTags = [];
  const seenTagKeys = new Set();
  const pushTag = (tag) => {
    if (!tag?.key || seenTagKeys.has(tag.key)) return;
    seenTagKeys.add(tag.key);
    flattenedTags.push(tag);
  };

  normalizedGroups.forEach((group) => group.tags.forEach(pushTag));
  normalizedUngroupedTags.forEach(pushTag);

  return {
    analyticsTaxonomyVersion: 1,
    tagGroups: normalizedGroups,
    tagGroupKeys: normalizedGroups.map((group) => group.key),
    tagGroupLabels: normalizedGroups.map((group) => group.label),
    tags: flattenedTags.map((tag) => tag.label),
    tagKeys: flattenedTags.map((tag) => tag.key),
    tagGroupTagKeys: normalizedGroups.flatMap((group) =>
      group.tags.map((tag) => `${group.key}::${tag.key}`)
    ),
    ungroupedTags: normalizedUngroupedTags
  };
}

function hydrateJobTagTaxonomy(job = {}) {
  const normalizedGroups = Array.isArray(job?.tagGroups)
    ? job.tagGroups.map((group) => ({
      label: group?.label ?? group,
      tags: Array.isArray(group?.tags) ? group.tags : []
    }))
    : [];

  const normalizedUngrouped = Array.isArray(job?.ungroupedTags)
    ? job.ungroupedTags
    : (Array.isArray(job?.tags) ? job.tags : []);

  const taxonomy = buildJobTagTaxonomy({
    tagGroups: normalizedGroups,
    ungroupedTags: normalizedUngrouped
  });

  return {
    tagGroups: taxonomy.tagGroups,
    ungroupedTags: taxonomy.ungroupedTags
  };
}

function getDefaultPublicJobApplicationSettings() {
  return {
    coverLetterEnabled: false,
    customQuestions: [],
    fields: [],
    appearanceMode: 'dark'
  };
}

function buildPublicApplicationFieldId(label, fallback = 'field') {
  return `${buildAnalyticsKey(label || fallback) || fallback}_${Date.now()}`;
}

function normalizeApplicationFieldOptions(options) {
  const values = Array.isArray(options)
    ? options
    : String(options || '')
      .split(',')
      .map((value) => value.trim());

  return values
    .map((value) => normalizeTagLabel(value))
    .filter(Boolean)
    .filter((value, index, arr) => arr.indexOf(value) === index);
}

function normalizeApplicationField(field, index = 0) {
  if (!field || typeof field !== 'object') return null;

  const label = normalizeTagLabel(field.label || field.question || field.text);
  const rawSystemKey = ['coverLetter', LEGACY_VIDEO_SUBMISSION_SYSTEM_KEY, 'portfolioLink', 'linkedinUrl'].includes(field.systemKey)
    ? field.systemKey
    : '';
  const systemKey = rawSystemKey === LEGACY_VIDEO_SUBMISSION_SYSTEM_KEY ? '' : rawSystemKey;
  const fallbackLabel = rawSystemKey === 'coverLetter'
    ? PUBLIC_APPLICATION_PRESET_FIELDS.coverLetter.label
    : rawSystemKey === LEGACY_VIDEO_SUBMISSION_SYSTEM_KEY
      ? VIDEO_SUBMISSION_FIELD_LABEL
    : rawSystemKey === 'portfolioLink'
      ? PUBLIC_APPLICATION_PRESET_FIELDS.portfolioLink.label
      : rawSystemKey === 'linkedinUrl'
        ? PUBLIC_APPLICATION_PRESET_FIELDS.linkedinUrl.label
        : '';
  const normalizedLabel = label || fallbackLabel;

  if (!normalizedLabel) return null;

  const rawType = String(field.type || '');
  const nextType = rawType === 'select'
    ? 'text'
    : (PUBLIC_APPLICATION_FIELD_TYPES.includes(rawType)
      ? rawType
      : (rawSystemKey === 'coverLetter' ? 'file' : rawSystemKey === LEGACY_VIDEO_SUBMISSION_SYSTEM_KEY ? 'video' : ['portfolioLink', 'linkedinUrl'].includes(rawSystemKey) ? 'url' : 'text'));

  return {
    id: String(field.id || buildPublicApplicationFieldId(normalizedLabel, `field_${index + 1}`)),
    label: normalizedLabel,
    type: nextType,
    required: field.required === true,
    options: [],
    systemKey
  };
}

function normalizePublicJobSettings(settingsData) {
  const source = settingsData && typeof settingsData === 'object' ? settingsData : {};
  const hasExplicitFields = Array.isArray(source.fields);
  const customQuestions = Array.isArray(source.customQuestions)
    ? source.customQuestions
      .map((entry, index) => {
        if (typeof entry === 'string') {
          const label = entry.trim();
          if (!label) return null;
          return {
            id: `custom_q_${index + 1}`,
            label,
            type: 'text',
            required: false
          };
        }

        if (!entry || typeof entry !== 'object') return null;

        const label = (entry.label || entry.question || entry.text || '').trim();
        if (!label) return null;

        return {
          id: String(entry.id || `custom_q_${index + 1}`),
          label,
          type: String(entry.type || 'text') === 'select' ? 'text' : String(entry.type || 'text'),
          required: entry.required === true,
          options: []
        };
      })
      .filter(Boolean)
    : [];

  const existingFields = Array.isArray(source.fields)
    ? source.fields
      .map((entry, index) => normalizeApplicationField(entry, index))
      .filter(Boolean)
    : [];
  const derivedLegacyFields = customQuestions
    .map((entry, index) => normalizeApplicationField(entry, index))
    .filter(Boolean);

  const fields = existingFields.length > 0 ? existingFields : derivedLegacyFields;
  const nextFields = [...fields];
  const coverLetterEnabled = hasExplicitFields
    ? nextFields.some((field) => field.systemKey === 'coverLetter')
    : (source.coverLetter?.enabled === true || source.coverLetterEnabled === true);

  if (coverLetterEnabled && !nextFields.some((field) => field.systemKey === 'coverLetter')) {
    nextFields.push({ ...PUBLIC_APPLICATION_PRESET_FIELDS.coverLetter });
  }

  const normalizedQuestions = nextFields
    .filter((field) => field.systemKey !== 'coverLetter')
    .map((field) => ({
      id: field.id,
      label: field.label,
      type: field.type,
      required: field.required === true,
      options: []
    }));

  return {
    coverLetterEnabled: nextFields.some((field) => field.systemKey === 'coverLetter'),
    customQuestions: normalizedQuestions,
    fields: nextFields,
    appearanceMode: 'dark'
  };
}

function buildPublicJobSettingsPayload(settings) {
  const normalizedFields = Array.isArray(settings?.fields)
    ? settings.fields
      .map((entry, index) => normalizeApplicationField(entry, index))
      .filter(Boolean)
    : [];
  const questionFields = Array.isArray(settings?.customQuestions)
    ? settings.customQuestions
      .map((entry, index) => normalizeApplicationField(entry, index))
      .filter(Boolean)
    : [];

  let fields = normalizedFields.length > 0 ? normalizedFields : questionFields;

  if (settings?.coverLetterEnabled === true && !fields.some((field) => field.systemKey === 'coverLetter')) {
    fields = [...fields, { ...PUBLIC_APPLICATION_PRESET_FIELDS.coverLetter }];
  }

  if (settings?.coverLetterEnabled !== true) {
    fields = fields.filter((field) => field.systemKey !== 'coverLetter');
  }

  fields = fields.map((field, index) => ({
    ...field,
    order: index + 1
  }));

  const customQuestions = fields
    .filter((field) => field.systemKey !== 'coverLetter')
    .map((field, index) => ({
      id: field.id,
      label: field.label,
      type: field.type,
      required: field.required === true,
      options: [],
      order: index + 1,
      systemKey: field.systemKey || ''
    }));
  const coverLetterEnabled = fields.some((field) => field.systemKey === 'coverLetter');

  return {
    coverLetter: {
      enabled: coverLetterEnabled
    },
    version: 2,
    coverLetterEnabled,
    appearanceMode: 'dark',
    fields,
    customQuestions,
    updatedAt: Date.now()
  };
}

function getPublicJobSettingsSignature(settings) {
  const normalized = normalizePublicJobSettings(settings);
  return JSON.stringify({
    appearanceMode: 'dark',
    coverLetterEnabled: normalized.coverLetterEnabled === true,
    fields: (normalized.fields || []).map((field) => ({
      id: String(field.id || ''),
      label: String(field.label || ''),
      type: String(field.type || 'text'),
      required: field.required === true,
      systemKey: String(field.systemKey || '')
    }))
  });
}

function sanitizeDepartmentSettingsKey(value) {
  return buildAnalyticsKey(value);
}

/**
 * Job lifecycle analytics must carry the final job snapshot because the operational
 * post can be unpublished or deleted immediately after the action completes.
 */
function buildJobLifecycleAnalyticsPayload({ jobId, jobData, lifecycleStatus, deleted = false, closed = false }) {
  const taxonomy = buildJobTagTaxonomy({
    tagGroups: jobData?.tagGroups || [],
    ungroupedTags: jobData?.ungroupedTags || jobData?.tags || []
  });
  const hiringPlan = buildHiringPlan(jobData?.hiringPlan?.targetHireDate || jobData?.targetHireDate || '');

  return {
    jobId: jobId || '',
    jobTitle: jobData?.jobOpening || jobData?.title || '',
    department: jobData?.department || '',
    location: jobData?.companyLocation || jobData?.location || '',
    ownerRecruiterId: jobData?.ownerRecruiterId || '',
    ownerRecruiterName: jobData?.ownerRecruiterName || '',
    ownerRecruiterEmail: jobData?.ownerRecruiterEmail || '',
    targetHireDate: hiringPlan.targetHireDate,
    hiringPlan,
    analyticsTaxonomyVersion: taxonomy.analyticsTaxonomyVersion,
    tagGroups: taxonomy.tagGroups,
    tagGroupKeys: taxonomy.tagGroupKeys,
    tagGroupLabels: taxonomy.tagGroupLabels,
    tags: taxonomy.tags,
    tagKeys: taxonomy.tagKeys,
    tagGroupTagKeys: taxonomy.tagGroupTagKeys,
    ungroupedTags: taxonomy.ungroupedTags,
    lifecycleStatus,
    deleted,
    closed,
    published: lifecycleStatus === 'published'
  };
}

async function emitJobLifecycleAnalyticsEvent({
  companyUID,
  eventType,
  jobId,
  jobData,
  lifecycleStatus,
  occurredAt,
  context = {},
  deleted = false,
  closed = false
}) {
  if (!companyUID || !eventType || !jobId) return;
  await emitAtsAnalyticsEvent({
    company_uid: companyUID,
    event_type: eventType,
    occurred_at: occurredAt,
    dedupe_key: `${eventType}:${companyUID}:${jobId}:${occurredAt}`,
    job: buildJobLifecycleAnalyticsPayload({ jobId, jobData, lifecycleStatus, deleted, closed }),
    context
  });
}

const Home = () => {
  useUiLanguage();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { dialog: homeDialog, notify: showHomeNotice, confirm: confirmHomeAction, prompt: promptHomeAction, dismiss: dismissHomeDialog } = useHomeFeedback();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();

  // i18n hooks
  const { t, i18n } = useTranslation('home');
  const { isRTL } = useLanguage();

  // ✅ NEW: Check for trial or subscription access
  const { accessType, trial, productTypes, hasAccess, loading: accessLoading } = useAccessControl();
  const hasAtsApplications = hasHostedJobApplications({ hasAccess, productTypes });



  // Tutorial state management
  const [tutorialActive, setTutorialActive] = useState(false);
  const [realJobPosts, setRealJobPosts] = useState([]); // Backup of real data
  const [realNotifs, setRealNotifs] = useState([]); // Backup of real notifs
  const [realAnalytics, setRealAnalytics] = useState(null); // Backup of real analytics
  const [driverInstance, setDriverInstance] = useState(null); // Store driver instance for cleanup
  const tutorialCompletedRef = useRef(false);
  const shouldReloadWithPromptRef = useRef(false);
  const atsPromptHandledRef = useRef(false);
  const prevAtsStatusRef = useRef(null);
  const approvalNotifKeysRef = useRef(new Set());
  const taskNotifKeysRef = useRef(new Set());
  const taskNotifSignatureRef = useRef(new Map());
  const notificationsClearedAtRef = useRef(0);
  const submitterEmailCacheRef = useRef(new Map());
  const jobModalInitialSnapshotRef = useRef(null);

  // State management for all modals and forms
  const [jobPosts, setJobPosts] = useState([]);
  const [pendingJobApprovalsByJobId, setPendingJobApprovalsByJobId] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [jobPostingPhase, setJobPostingPhase] = useState(null);
  const jobPostingBusy = useRef(false);
  const [showLIPopup, setShowLIPopup] = useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [showApplicationFormBuilder, setShowApplicationFormBuilder] = useState(false);
  const [sourceManpowerRequestId, setSourceManpowerRequestId] = useState(null);
  const [pendingJobFocusId, setPendingJobFocusId] = useState(null);
  const [modalPage, setModalPage] = useState(1);
  const [agencyJobPostingEnabled, setAgencyJobPostingEnabled] = useState(false);
  const [editingMode, setEditingMode] = useState(false);
  const [currentJobKey, setCurrentJobKey] = useState('');
  const [currentAppType, setCurrentAppType] = useState('link');
  const [jobSubmitAttempted, setJobSubmitAttempted] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobShareState, setJobShareState] = useState({ jobId: '', text: '', disabled: false });
  const [notifs, setNotifs] = useState([]);
  const [showNotifsModal, setShowNotifsModal] = useState(false);
  // Mini analytics state
  const [analyticsMetric, setAnalyticsMetric] = useState('views'); // 'views' | 'applies'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return m;
  });
  const [jobFilter, setJobFilter] = useState('');
  const [miniAnalytics, setMiniAnalytics] = useState({
    jobs: [], // job titles
    viewsCounts: {}, // by jobTitle for selectedMonth
    applyCounts: {}, // by jobTitle for selectedMonth
    series: [], // 6-month time-series totals across all jobs
    seriesByJob: {}, // per-job 6-month series
  });
  // Job-details analytics
  const [showJobAnalytics, setShowJobAnalytics] = useState(false);
  const [jobAnalyticsMetric, setJobAnalyticsMetric] = useState('views');
  const [jobAnalyticsSeries, setJobAnalyticsSeries] = useState([]);
  const [jobAnalyticsLoading, setJobAnalyticsLoading] = useState(false);

  // Authentication state
  const [companyUID, setCompanyUID] = useState('');
  const [loading, setLoading] = useState(true);

  // Approval settings hook - companyUID is set after auth
  const {
    requiresJobApproval,
    isJobApprover,
    isOfferApprover
  } = useApprovalSettings(companyUID);
  const teamAccess = useTeamAccessProfile(companyUID);
  const { teamMembers } = useTeamMembers(companyUID);
  const canCreateOrEditJobs = teamAccess.isCompanyOwner || teamAccess.userPermissions.canPostJobs;
  const canAccessAnalytics = teamAccess.isCompanyOwner || teamAccess.userPermissions.canAccessAnalytics;
  const visibleJobPosts = useMemo(
    () => teamAccess.filterByDepartmentAccess(jobPosts, (job) => job.department),
    [jobPosts, teamAccess]
  );
  const visibleMiniAnalyticsJobs = useMemo(
    () => miniAnalytics.jobs.filter((title) => visibleJobPosts.some((job) => job.jobOpening === title)),
    [miniAnalytics.jobs, visibleJobPosts]
  );
  const currentUserRecruiterOption = useMemo(() => ({
    id: currentUser?.uid || '',
    name: currentUser?.displayName || currentUser?.email || t('formLabels.currentUserRecruiter', 'Current user'),
    email: currentUser?.email || ''
  }), [currentUser?.displayName, currentUser?.email, currentUser?.uid, t]);
  const recruiterOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const addOption = ({ id, name, email }) => {
      const normalizedId = String(id || '').trim();
      const normalizedEmail = String(email || '').trim();
      const optionId = normalizedId || normalizedEmail;
      if (!optionId || seen.has(optionId)) return;
      seen.add(optionId);
      options.push({
        id: optionId,
        name: String(name || normalizedEmail || optionId).trim(),
        email: normalizedEmail
      });
    };

    (teamMembers || []).forEach((member) => {
      addOption({
        id: member.uid || member.email,
        name: member.name || [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email,
        email: member.email
      });
    });

    addOption(currentUserRecruiterOption);
    return options;
  }, [currentUserRecruiterOption, teamMembers]);
  const getRecruiterOptionById = useCallback((recruiterId = '') => {
    const normalizedId = String(recruiterId || '').trim();
    return recruiterOptions.find((option) => option.id === normalizedId) || null;
  }, [recruiterOptions]);

  const getApprovalSubmitter = useCallback((approval = {}) => {
    const submittedBy = approval?.submittedBy;
    if (submittedBy && typeof submittedBy === 'object') {
      return {
        uid: String(submittedBy.uid || '').trim(),
        email: String(submittedBy.email || approval.submittedByEmail || '').trim().toLowerCase()
      };
    }

    if (typeof submittedBy === 'string') {
      const submittedByValue = submittedBy.trim();
      return {
        uid: submittedByValue.includes('@') ? '' : submittedByValue,
        email: submittedByValue.includes('@')
          ? submittedByValue.toLowerCase()
          : String(approval.submittedByEmail || '').trim().toLowerCase()
      };
    }

    return {
      uid: '',
      email: String(approval.submittedByEmail || '').trim().toLowerCase()
    };
  }, []);

  const isApprovalSubmittedByCurrentUser = useCallback((approval = {}) => {
    const submitter = getApprovalSubmitter(approval);
    const currentUid = String(currentUser?.uid || '').trim();
    const currentEmail = String(currentUser?.email || '').trim().toLowerCase();
    return Boolean(
      (submitter.uid && currentUid && submitter.uid === currentUid) ||
      (submitter.email && currentEmail && submitter.email === currentEmail)
    );
  }, [currentUser?.email, currentUser?.uid, getApprovalSubmitter]);

  const canReviewJobApproval = useCallback((approval = {}) => {
    if (!approval || approval.status !== 'pending' || approval.type !== 'job_posting') return false;
    if (!isJobApprover) return false;
    if (isApprovalSubmittedByCurrentUser(approval)) return false;

    const approvalDepartment = approval.metadata?.department || approval.metadata?.jobDepartment || approval.snapshot?.department || '';
    return teamAccess.canAccessDepartment(approvalDepartment);
  }, [isApprovalSubmittedByCurrentUser, isJobApprover, teamAccess]);

  const canCancelJobApproval = useCallback((approval = {}) => (
    Boolean(approval?.id || approval?.entityId) &&
    (teamAccess.isCompanyOwner || isApprovalSubmittedByCurrentUser(approval))
  ), [isApprovalSubmittedByCurrentUser, teamAccess.isCompanyOwner]);

  const canEditPendingJobApproval = useCallback((approval = {}) => (
    canCancelJobApproval(approval) || canReviewJobApproval(approval)
  ), [canCancelJobApproval, canReviewJobApproval]);

  const getKnownPendingJobApproval = useCallback((job = {}) => {
    if (!job?.id || job.approvalStatus !== 'pending_approval') return null;

    return pendingJobApprovalsByJobId[job.id] || {
      id: job.approvalId || '',
      type: 'job_posting',
      status: 'pending',
      entityId: job.id,
      entityName: job.jobOpening || job.title || 'Job Post',
      submittedBy: { uid: job.approvalSubmittedByUid || '' },
      submittedAt: job.approvalSubmittedAt || 0,
      metadata: {
        department: job.department || '',
        jobTitle: job.jobOpening || job.title || ''
      },
      snapshot: {
        department: job.department || '',
        title: job.jobOpening || job.title || ''
      }
    };
  }, [pendingJobApprovalsByJobId]);

  const canEditJobRecord = useCallback((job = {}) => {
    if (job?.approvalStatus === 'pending_approval') {
      return canEditPendingJobApproval(getKnownPendingJobApproval(job) || {});
    }

    return canCreateOrEditJobs;
  }, [canCreateOrEditJobs, canEditPendingJobApproval, getKnownPendingJobApproval]);

  const buildPendingJobApprovalEditUpdates = useCallback(({ approval, jobData, editedAt }) => {
    if (!approval?.id || !jobData) return {};

    const basePath = `companies/${companyUID}/pending_approvals/${approval.id}`;
    const editor = {
      uid: currentUser?.uid || '',
      name: currentUser?.displayName || currentUser?.email || '',
      email: currentUser?.email || ''
    };

    return {
      [`${basePath}/entityName`]: jobData.jobOpening || jobData.title || 'Job Post',
      [`${basePath}/snapshot/title`]: jobData.jobOpening || jobData.title || 'Untitled',
      [`${basePath}/snapshot/department`]: jobData.department || '',
      [`${basePath}/snapshot/seniority`]: jobData.seniority || '',
      [`${basePath}/metadata/jobTitle`]: jobData.jobOpening || jobData.title || '',
      [`${basePath}/metadata/department`]: jobData.department || '',
      [`${basePath}/metadata/location`]: jobData.companyLocation || jobData.location || '',
      [`${basePath}/metadata/schedule`]: jobData.schedule || '',
      [`${basePath}/metadata/ownerRecruiterId`]: jobData.ownerRecruiterId || '',
      [`${basePath}/metadata/ownerRecruiterName`]: jobData.ownerRecruiterName || '',
      [`${basePath}/metadata/ownerRecruiterEmail`]: jobData.ownerRecruiterEmail || '',
      [`${basePath}/metadata/tagGroups`]: jobData.tagGroupLabels || [],
      [`${basePath}/metadata/tags`]: jobData.tags || [],
      [`${basePath}/metadata/lastEditedAt`]: editedAt,
      [`${basePath}/metadata/lastEditedBy`]: editor
    };
  }, [companyUID, currentUser?.displayName, currentUser?.email, currentUser?.uid]);

  // Form state
  const [formData, setFormData] = useState(getDefaultJobFormData);
  const [defaultApplicationMethod, setDefaultApplicationMethod] = useState('ats');
  const manualJobFields = useRef(new Set());
  const usesHostedApplications = hasAtsApplications && (editingMode ? formData.applicationMethod : defaultApplicationMethod) !== 'external';
  useEffect(() => {
    if (!accessLoading && !usesHostedApplications && modalPage === 3) setModalPage(2);
  }, [accessLoading, usesHostedApplications, modalPage]);
  const [jobTagGroups, setJobTagGroups] = useState([]);
  const [jobUngroupedTags, setJobUngroupedTags] = useState([]);
  const [tagGroupInput, setTagGroupInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedTagGroupKey, setSelectedTagGroupKey] = useState(UNGROUPED_JOB_TAG_KEY);

  // User data
  const [companyData, setCompanyData] = useState({
    companyName: '',
    city: '',
    country: '',
    description: ''
  });
  const [showEOR, setShowEOR] = useState(false);
  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const [liInputValue, setLiInputValue] = useState('');
  const [userLiId, setUserLiId] = useState(null);
  const [linkedInCompanyPageChecked, setLinkedInCompanyPageChecked] = useState(false);
  const [showLinkedInPrompt, setShowLinkedInPrompt] = useState(false);
  const hasLinkedInCompanyPage = Boolean(normalizeLinkedInCompanyId(userLiId));

  // Subscription state
  const [isFilterSubstageEnabled, setIsFilterSubstageEnabled] = useState(false);
  const [publicJobSettingsLoading, setPublicJobSettingsLoading] = useState(true);
  const [publicJobSettingsSaving, setPublicJobSettingsSaving] = useState(false);
  const [publicJobSettingsSaveState, setPublicJobSettingsSaveState] = useState('idle');
  const [publicJobSettingsSaveMessage, setPublicJobSettingsSaveMessage] = useState('');
  const [applicationFormScope, setApplicationFormScope] = useState('general');
  const [applicationFormDepartment, setApplicationFormDepartment] = useState('');
  const [applicationFormViewMode, setApplicationFormViewMode] = useState('edit');
  const [newApplicationFieldLabel, setNewApplicationFieldLabel] = useState('');
  const [newApplicationFieldType, setNewApplicationFieldType] = useState('text');
  const [newApplicationFieldRequired, setNewApplicationFieldRequired] = useState(false);
  const [draggedApplicationFieldId, setDraggedApplicationFieldId] = useState('');
  const [applicationFieldDropIndicator, setApplicationFieldDropIndicator] = useState({ fieldId: '', position: 'before' });
  const [generalPublicJobApplicationSettings, setGeneralPublicJobApplicationSettings] = useState(getDefaultPublicJobApplicationSettings);
  const [departmentPublicJobApplicationSettings, setDepartmentPublicJobApplicationSettings] = useState({});
  const [savedGeneralPublicJobApplicationSignature, setSavedGeneralPublicJobApplicationSignature] = useState(getPublicJobSettingsSignature(getDefaultPublicJobApplicationSettings()));
  const [savedDepartmentPublicJobApplicationSignatures, setSavedDepartmentPublicJobApplicationSignatures] = useState({});
  const [jobApplicationDraftSettings, setJobApplicationDraftSettings] = useState(null);
  const [quickApplicationFieldLabel, setQuickApplicationFieldLabel] = useState('');
  const [quickApplicationFieldType, setQuickApplicationFieldType] = useState('text');
  const [quickApplicationFieldRequired, setQuickApplicationFieldRequired] = useState(false);

  // Job categories
  const jobCategories = [
    "Accounting // محاسبة // Comptabilité",
    "Administration & Office // الإدارة & المكتب // Administration & Bureau",
    "Advertising & Marketing // التسويق الإعلاني // Publicité & Marketing",
    "Agriculture & Food // الزراعة والغذاء // Agriculture & Alimentation",
    "Architecture & Construction // العمارة والبناء // Architecture & Bâtiment",
    "Arts // الفنون // Arts",
    "Business & Finance // الأعمال التجارية والمالية // Affaires & Finance",
    "Communication // اخصائي اتصالات // Communication",
    "Community Service & Volunteering // خدمة المجتمع والعمل التطوعي // Service communautaire et bénévolat",
    "Customer Service // خدمة الزبائن // Service Clients",
    "Data Analysis // تحليل البيانات // Analyse des données",
    "Economics // اقتصاد // Économie",
    "Education & Teaching // التربية والتعليم // Education & Enseignement",
    "Engineering // هندسة // Ingénierie",
    "Fashion // الموضة // Mode",
    "Government & Public Sector // القطاع الحكومي والعام // Gouvernement & Secteur Public",
    "Graphic Design // تصميم جرافيك // Design graphique",
    "Healthcare & Medicine // الطب والرعاية الصحية // Santé et médecine",
    "Hospitality // الضيافة // Hospitalité",
    "Human Resources // الموارد البشرية // Ressources Humaines",
    "Humanitarian Aid // المساعدات الإنسانية // Aide humanitaire",
    "Information Security // أمان المعلومات // Sécurité de l'information",
    "Legal // قانوني // Juridique",
    "Logistics // اللوجستيات // Logistique",
    "Manufacturing // صناعة // Fabrication",
    "Market Research // بحث السوق // Recherche de marché",
    "Natural Resources // الموارد الطبيعية // Ressources Naturelles",
    "Operations Management // إدارة العمليات // Gestion des opérations",
    "Product Management // ادارة المنتج // Gestion des produits",
    "Program and Project Management // إدارة البرامج والمشاريع // Gestion de Programmes et Projets",
    "Public Relations // العلاقات العامة // Relations publiques",
    "Quality Control // مراقبة الجودة // Contrôle de Qualité",
    "Real Estate // العقارات // Immobilier",
    "Research // بحث // Rechercher",
    "Sales // مبيعات // Ventes",
    "Science // علم // Sciences",
    "Social Media Management // إدارة وسائل الاعلام الاجتماعية // Gestion des médias sociaux",
    "Software & Information Technology // البرمجيات وتكنولوجيا المعلومات // Logiciels & Technologies de L'Information",
    "Supply Chain & Distribution // سلسلة التوريد والتوزيع // Chaîne d'Approvisionnement et Distribution",
    "Web Development // تطوير الويب // Développement Web",
    "Writing & Publishing // الكتابة والنشر // Écriture et Publication"
  ];

  // Common company departments (dropdown)
  const departments = [
    'Administration',
    'Customer Support',
    'Engineering',
    'Product Management',
    'Design / UX',
    'Data & Analytics',
    'Marketing',
    'Sales',
    'Finance & Accounting',
    'Human Resources',
    'Operations',
    'IT / Systems',
    'Security / InfoSec',
    'Legal',
    'Compliance',
    'Procurement',
    'Supply Chain',
    'Logistics',
    'Manufacturing',
    'Quality Assurance',
    'Research & Development',
    'Business Development',
    'Partnerships',
    'Strategy',
    'Content',
    'Communications / PR',
    'Community',
    'Facilities',
    'Real Estate',
    'Construction',
    'Healthcare',
    'Education'
  ];

  const archiveApplicationTargets = useCallback(async () => {
    if (!companyUID) return;
    await archiveCompanyApplicationTargets(database, companyUID);
  }, [companyUID, database]);

  const restoreApplicationTargets = useCallback(async () => {
    if (!companyUID) return;
    await restoreCompanyApplicationTargets(database, companyUID);
  }, [companyUID, database]);

  const clearApplicationArchive = useCallback(async () => {
    if (!companyUID) return;
    await clearCompanyApplicationTargetArchive(database, companyUID);
  }, [companyUID, database]);

  // Rich text editor ref
  const descriptionRef = useRef(null);
  const fullscreenDescriptionRef = useRef(null);
  const [jobDescription, setJobDescription] = useState('');
  const [showFullscreenEditor, setShowFullscreenEditor] = useState(false);
  const [regularQuillReady, setRegularQuillReady] = useState(false);
  const [fullscreenQuillReady, setFullscreenQuillReady] = useState(false);

  // New state for AI popup
  const [aiGenerating, setAiGenerating] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
  const ATS_PROMPT_MESSAGE = "Ready to see the ATS (Applicant Tracking System)?\n\nTrack applications with AI-powered filtering, automated workflows, and smart candidate management.\n\nTakes 2 minutes.";

  // Load external scripts and initialize
  useEffect(() => {
    // Load external dependencies
    const loadExternalDependencies = () => {
      // FontAwesome
      const fontAwesome = document.createElement('script');
      fontAwesome.src = 'https://kit.fontawesome.com/f6e3baed53.js';
      fontAwesome.crossOrigin = 'anonymous';
      document.head.appendChild(fontAwesome);

      // Use the bundled editor so the toolbar is ready whenever the modal opens.
      window.Quill = window.Quill || ReactQuill.Quill;

      // Driver.js CSS
      const driverCSS = document.createElement('link');
      driverCSS.rel = 'stylesheet';
      driverCSS.href = 'https://cdn.jsdelivr.net/npm/driver.js@1.0.1/dist/driver.css';
      document.head.appendChild(driverCSS);

      // Driver.js
      const driverJS = document.createElement('script');
      driverJS.src = 'https://cdn.jsdelivr.net/npm/driver.js@1.0.1/dist/driver.js.iife.js';
      document.head.appendChild(driverJS);

      // Montserrat fonts
      const montserrat400 = document.createElement('link');
      montserrat400.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap';
      montserrat400.rel = 'stylesheet';
      document.head.appendChild(montserrat400);

      const montserrat900 = document.createElement('link');
      montserrat900.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@900&display=swap';
      montserrat900.rel = 'stylesheet';
      document.head.appendChild(montserrat900);
    };

    loadExternalDependencies();

    // Seed an extra history state so browser back can be intercepted in-app.
    window.history.pushState(null, null, window.location.href);

    // Initialize tutorial button functionality
    setTimeout(() => {
      const tutorialBtn = document.querySelector('.tutorial-button');
      const tutorialText = tutorialBtn?.querySelector('.button-text');

      if (tutorialText) {
        tutorialText.style.display = 'inline';
        tutorialText.style.opacity = '1';

        setTimeout(() => {
          setTimeout(() => {
            tutorialText.style.opacity = '0';
            tutorialText.style.display = 'none';
          }, 300);
        }, 3000);
      }
    }, 1000);

  }, []);

  // Open Post a Job modal if requested via navigation state
  useEffect(() => {
    if (location?.state?.openPostJob) {
      const sourceRequest = location.state.sourceManpowerRequest || null;
      const defaultFormData = getDefaultJobFormData(currentUserRecruiterOption.id || '');
      const nextFormData = {
        ...defaultFormData,
        jobOpening: sourceRequest?.position?.title || '',
        department: sourceRequest?.submitter?.department || '',
        mode1: sourceRequest?.position?.type === 'part-time' ? 'Part-Time' : defaultFormData.mode1,
        jobCity: sourceRequest?.submitter?.location || '',
        salary: sourceRequest?.position?.salaryRange || ''
      };
      const nextApplicationSettings = buildPublicApplicationSettingsForJob(nextFormData.department || '');
      const nextDescription = sourceRequest?.justification || '';
      setEditingMode(false);
      setCurrentJobKey('');
      setFormData(nextFormData);
      manualJobFields.current.clear();
      if (nextFormData.department) manualJobFields.current.add('department');
      setJobTagGroups([]);
      setJobUngroupedTags([]);
      setTagGroupInput('');
      setTagInput('');
      setSelectedTagGroupKey(UNGROUPED_JOB_TAG_KEY);
      setCurrentAppType('link');
      setSourceManpowerRequestId(sourceRequest?.id || null);
      setJobApplicationDraftSettings(nextApplicationSettings);
      resetQuickApplicationFieldComposer();
      setJobDescription(nextDescription);
      window.tempQuillContent = '';
      setModalPage(1);
      setJobSubmitAttempted(false);
      markJobModalSnapshot({
        editingMode: false,
        currentJobKey: '',
        formData: sourceRequest ? defaultFormData : nextFormData,
        currentAppType: 'link',
        jobDescription: sourceRequest ? '' : nextDescription,
        jobDescriptionHTML: '',
        jobTagGroups: [],
        jobUngroupedTags: [],
        tagGroupInput: '',
        tagInput: '',
        selectedTagGroupKey: UNGROUPED_JOB_TAG_KEY,
        quickApplicationFieldLabel: '',
        quickApplicationFieldType: 'text',
        quickApplicationFieldRequired: false,
        jobApplicationDraftSettings: sourceRequest
          ? buildPublicApplicationSettingsForJob(defaultFormData.department || '')
          : nextApplicationSettings,
        sourceManpowerRequestId: null
      });
      setShowModal(true);
      // Clean the state so it doesn't persist on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location, currentUserRecruiterOption.id]);

  useEffect(() => {
    if (location?.state?.openApplicationFormBuilder) {
      handleOpenApplicationFormBuilder();
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Authentication and company setup when component mounts or user changes
  useEffect(() => {
    if (currentUser) {
      authenticateAndSetupCompany();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    approvalNotifKeysRef.current = new Set();
    taskNotifKeysRef.current = new Set();
    taskNotifSignatureRef.current = new Map();
    notificationsClearedAtRef.current = 0;
  }, [companyUID, currentUser?.uid]);

  // Load company data and subscriptions when companyUID is set
  useEffect(() => {
    if (companyUID) {
      prevAtsStatusRef.current = null;
      loadUserData();
      loadJobPosts();

      // Fetch subscription & company feature status from unified subscription node
      const subRef = ref(database, `companies/${companyUID}/subscription`);
      onValue(subRef, (snapshot) => {
        const subData = snapshot.val();
        const status = subData && subData.productType !== 'tse' && (subData.status === 'active' || subData.status === 'trialing' || subData.status === 'unlimited')
          ? subData.status
          : null;

        const previousStatus = prevAtsStatusRef.current;
        if (previousStatus === status) {
          return;
        }
        prevAtsStatusRef.current = status;

        if (status === 'trialing') {
          archiveApplicationTargets().catch((error) => {
            console.error('Failed to archive application target when ATS trial started:', error);
          });
        } else if (status === 'active') {
          clearApplicationArchive().catch((error) => {
            console.error('Failed to clear archived application target when ATS subscription is active:', error);
          });
        } else if (previousStatus === 'trialing') {
          restoreApplicationTargets().catch((error) => {
            console.error('Failed to restore application target after ATS trial ended:', error);
          });
        }
      });

      const filterEnabledRef = ref(database, `companies/${companyUID}/filterSubstageEnabled`);
      onValue(filterEnabledRef, (snapshot) => {
        setIsFilterSubstageEnabled(!!snapshot.val());
      });


    }
  }, [companyUID]);

  useEffect(() => {
    if (!companyUID) {
      setAgencyJobPostingEnabled(false);
      return undefined;
    }
    return onValue(ref(database, `companies/${companyUID}/settings/agencyJobPostingEnabled`), (snapshot) => {
      setAgencyJobPostingEnabled(snapshot.val() === true || companyUID === '1YA2sUjvgnUo9yrh7To9NxXvl1T2');
    });
  }, [companyUID]);

  useEffect(() => {
    if (!companyUID || !isCompanyOwner) {
      setPublicJobSettingsLoading(false);
      return;
    }

    setPublicJobSettingsLoading(true);
    const settingsRef = ref(database, `companies/${companyUID}/settings/publicJobApplication`);
    const departmentSettingsRef = ref(database, `companies/${companyUID}/settings/publicJobApplicationDepartments`);

    const stopGeneral = onValue(settingsRef, async (snapshot) => {
      if (snapshot.exists()) {
        const normalizedSettings = normalizePublicJobSettings(snapshot.val());
        setGeneralPublicJobApplicationSettings(normalizedSettings);
        setSavedGeneralPublicJobApplicationSignature(getPublicJobSettingsSignature(normalizedSettings));
        setPublicJobSettingsLoading(false);
        return;
      }

      try {
        const legacySnapshot = await get(ref(database, `companies/${companyUID}/publicJobApplication`));
        if (legacySnapshot.exists()) {
          const normalizedSettings = normalizePublicJobSettings(legacySnapshot.val());
          setGeneralPublicJobApplicationSettings(normalizedSettings);
          setSavedGeneralPublicJobApplicationSignature(getPublicJobSettingsSignature(normalizedSettings));
        } else {
          const defaultSettings = getDefaultPublicJobApplicationSettings();
          setGeneralPublicJobApplicationSettings(defaultSettings);
          setSavedGeneralPublicJobApplicationSignature(getPublicJobSettingsSignature(defaultSettings));
        }
      } catch (error) {
        console.error('Error loading public job application settings:', error);
      } finally {
        setPublicJobSettingsLoading(false);
      }
    });

    const stopDepartments = onValue(departmentSettingsRef, (snapshot) => {
      const raw = snapshot.val() || {};
      const normalized = Object.entries(raw).reduce((acc, [key, value]) => {
        acc[key] = normalizePublicJobSettings(value);
        return acc;
      }, {});
      setDepartmentPublicJobApplicationSettings(normalized);
      setSavedDepartmentPublicJobApplicationSignatures(
        Object.entries(normalized).reduce((acc, [key, value]) => {
          acc[key] = getPublicJobSettingsSignature(value);
          return acc;
        }, {})
      );
    });

    return () => {
      stopGeneral();
      stopDepartments();
    };
  }, [companyUID, isCompanyOwner]);

  // User-specific notifications listener (per-user log)
  useEffect(() => {
    if (!companyUID || !currentUser?.uid || tutorialActive) return;
    const notifsRef = ref(database, `companies/${companyUID}/user_notifications/${currentUser.uid}`);
    const unsubscribe = onValue(notifsRef, (snapshot) => {
      const clearedAt = notificationsClearedAtRef.current || 0;
      const data = snapshot.val() || {};
      const list = Object.entries(data)
        .filter(([, value]) => value && typeof value === 'object')
        .map(([key, value]) => ({
          id: value.id || key,
          ...value
        }))
        .filter((item) => (item.timestamp || 0) > clearedAt)
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setNotifs(list);
    });

    return () => unsubscribe();
  }, [companyUID, currentUser?.uid, tutorialActive]);

  // Track when notifications were cleared so we can hide older entries
  useEffect(() => {
    if (!companyUID || !currentUser?.uid || tutorialActive) return;
    const clearedRef = ref(database, `companies/${companyUID}/user_notification_clears/${currentUser.uid}`);
    const unsubscribe = onValue(clearedRef, (snapshot) => {
      const clearedAt = snapshot.val() || 0;
      notificationsClearedAtRef.current = typeof clearedAt === 'number' ? clearedAt : 0;
    });

    return () => unsubscribe();
  }, [companyUID, currentUser?.uid, tutorialActive]);

  // Sync approval requests into the current user's notifications log (approvers only)
  useEffect(() => {
    if (!companyUID || !currentUser?.uid || tutorialActive) return;
    const resolveSubmitterEmail = async (uid) => {
      if (!uid) return '';
      if (submitterEmailCacheRef.current.has(uid)) {
        return submitterEmailCacheRef.current.get(uid) || '';
      }

      let email = '';
      try {
        const collabSnap = await get(ref(database, `collaborators/${uid}`));
        if (collabSnap.exists()) {
          email = collabSnap.val()?.email || '';
        }
      } catch (e) { }

      if (!email) {
        try {
          const personSnap = await get(ref(database, `people/${uid}`));
          if (personSnap.exists()) {
            email = personSnap.val()?.email || '';
          }
        } catch (e) { }
      }

      if (!email) {
        try {
          const emailResp = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid })
          });
          const emailData = await emailResp.json();
          email = emailData?.email || '';
        } catch (e) { }
      }

      submitterEmailCacheRef.current.set(uid, email || '');
      return email || '';
    };

    const approvalsRef = ref(database, `companies/${companyUID}/pending_approvals`);
    const unsubscribe = onValue(approvalsRef, async (snapshot) => {
      const updates = {};
      const nextKeys = new Set();
      const prevKeys = approvalNotifKeysRef.current;
      const clearedAt = notificationsClearedAtRef.current || 0;
      const canSeeJob = isJobApprover === true;
      const canSeeOffer = isOfferApprover === true;

      if (snapshot.exists() && (canSeeJob || canSeeOffer)) {
        const approvals = [];
        snapshot.forEach((childSnap) => {
	          const approval = childSnap.val();
	          if (!approval || approval.status !== 'pending') return;
	          if (approval.type === 'job_posting' && !canSeeJob) return;
	          if (approval.type === 'offer_letter' && !canSeeOffer) return;
	          const approvalDepartment = approval.metadata?.department || approval.metadata?.jobDepartment || approval.snapshot?.department || '';
	          if (!teamAccess.canAccessDepartment(approvalDepartment)) return;

	          const key = `approval_${childSnap.key}`;
          nextKeys.add(key);
          approvals.push({ approvalId: childSnap.key, approval, key });
        });

        for (const item of approvals) {
          const approval = item.approval;
          const submittedByRaw = approval.submittedBy;
          const submittedBy = submittedByRaw && typeof submittedByRaw === 'object' ? submittedByRaw : {};
          let submittedByEmail = submittedBy.email || approval.submittedByEmail || '';

          if (!submittedByEmail) {
            if (typeof submittedByRaw === 'string') {
              submittedByEmail = submittedByRaw.includes('@')
                ? submittedByRaw
                : await resolveSubmitterEmail(submittedByRaw);
            } else if (submittedBy.uid) {
              submittedByEmail = await resolveSubmitterEmail(submittedBy.uid);
            }
          }

          const submittedByName = submittedBy.name || submittedByEmail || 'Unknown';
          const entityLabel = approval.entityName || approval.snapshot?.title || approval.metadata?.jobTitle || 'Approval Request';
          const title = approval.type === 'job_posting' ? 'Job approval request' : 'Offer approval request';
          const message = submittedByName
            ? `${entityLabel} • Requested by ${submittedByName}`
            : `${entityLabel} awaiting approval`;
          const approvalTimestamp = approval.submittedAt || Date.now();

          if (!prevKeys.has(item.key) && approvalTimestamp > clearedAt) {
            updates[`companies/${companyUID}/user_notifications/${currentUser.uid}/${item.key}`] = {
              id: item.key,
              type: 'approval_request',
              ...systemMessage('approval_request', { entity: entityLabel, name: submittedByName, approvalType: approval.type }),
              approvalType: approval.type,
              title,
              message,
              text: message,
              timestamp: approvalTimestamp,
              approvalId: item.approvalId,
              entityId: approval.entityId || '',
              entityName: entityLabel
            };
          }

          if (submittedByEmail && !approval.submittedByEmail) {
            updates[`companies/${companyUID}/pending_approvals/${item.approvalId}/submittedByEmail`] = submittedByEmail;
          }
        }
      }

      approvalNotifKeysRef.current.forEach((existingKey) => {
        if (!nextKeys.has(existingKey)) {
          updates[`companies/${companyUID}/user_notifications/${currentUser.uid}/${existingKey}`] = null;
        }
      });

      if (Object.keys(updates).length > 0) {
        update(ref(database), updates).catch((error) => {
          console.error('Error syncing approval notifications:', error);
        });
      }

      approvalNotifKeysRef.current = nextKeys;
    });

    return () => unsubscribe();
	  }, [companyUID, currentUser?.uid, tutorialActive, isJobApprover, isOfferApprover, teamAccess]);

  useEffect(() => {
    if (!companyUID || tutorialActive) {
      setPendingJobApprovalsByJobId({});
      return undefined;
    }

    const approvalsRef = ref(database, `companies/${companyUID}/pending_approvals`);
    const unsubscribe = onValue(approvalsRef, (snapshot) => {
      const nextApprovals = {};

      if (snapshot.exists()) {
        snapshot.forEach((childSnap) => {
          const approval = childSnap.val();
          if (!approval || approval.type !== 'job_posting' || approval.status !== 'pending' || !approval.entityId) {
            return;
          }

          const hydratedApproval = { ...approval, id: approval.id || childSnap.key };
          const existing = nextApprovals[approval.entityId];
          if (!existing || (hydratedApproval.submittedAt || 0) > (existing.submittedAt || 0)) {
            nextApprovals[approval.entityId] = hydratedApproval;
          }
        });
      }

      setPendingJobApprovalsByJobId(nextApprovals);
    });

    return () => unsubscribe();
  }, [companyUID, tutorialActive]);

  // Sync today's onboarding tasks into the current user's notifications log
  useEffect(() => {
    if (!companyUID || !currentUser?.uid || !currentUser?.email || tutorialActive) return;
    const hiresRef = ref(database, `companies/${companyUID}/onboarding`);
    const unsubscribe = onValue(hiresRef, (snapshot) => {
      const updates = {};
      const nextKeys = new Set();
      const nextSignatures = new Map();
      const prevKeys = taskNotifKeysRef.current;
      const prevSignatures = taskNotifSignatureRef.current;
      const clearedAt = notificationsClearedAtRef.current || 0;
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const todayTimestamp = new Date(`${todayStr}T00:00:00`).getTime();

      if (snapshot.exists()) {
        const hires = snapshot.val() || {};
        Object.entries(hires).forEach(([hireId, hireData]) => {
          const hireName = hireData?.name || hireData?.fullName || 'New Hire';
          const tasksByCategory = hireData?.tasks || {};

          Object.entries(tasksByCategory).forEach(([category, categoryData]) => {
            if (!categoryData || typeof categoryData !== 'object') return;
            const tasksMap = categoryData.workflow?.tasks || categoryData;

            Object.entries(tasksMap || {}).forEach(([taskId, task]) => {
              if (!task || typeof task !== 'object') return;
              if (taskId === 'workflow') return;
              if (!task.dueDate || task.status === 'completed') return;
              if (task.dueDate !== todayStr) return;

              const assignees = Array.isArray(task.assignees)
                ? task.assignees
                : (task.assignee ? [task.assignee] : []);
              if (!assignees.includes(currentUser.email)) return;

              const key = `task_${hireId}_${category}_${taskId}`;
              const signature = `${task.title || ''}|${hireName}|${task.dueDate || ''}`;
              nextKeys.add(key);
              nextSignatures.set(key, signature);
              if ((todayTimestamp > clearedAt) && (!prevKeys.has(key) || prevSignatures.get(key) !== signature)) {
                updates[`companies/${companyUID}/user_notifications/${currentUser.uid}/${key}`] = {
                  id: key,
                  type: 'onboarding_task',
                  ...systemMessage('onboarding_task', { name: hireName, taskTitle: task.title || '' }),
                  title: task.title || 'Onboarding task due today',
                  message: `Onboarding task due today • ${hireName}`,
                  text: `${task.title || 'Task'} • ${hireName}`,
                  timestamp: todayTimestamp,
                  hireId,
                  hireName,
                  category,
                  taskId,
                  dueDate: task.dueDate
                };
              }
            });
          });
        });
      }

      taskNotifKeysRef.current.forEach((existingKey) => {
        if (!nextKeys.has(existingKey)) {
          updates[`companies/${companyUID}/user_notifications/${currentUser.uid}/${existingKey}`] = null;
        }
      });

      if (Object.keys(updates).length > 0) {
        update(ref(database), updates).catch((error) => {
          console.error('Error syncing onboarding task notifications:', error);
        });
      }

      taskNotifKeysRef.current = nextKeys;
      taskNotifSignatureRef.current = nextSignatures;
    });

    return () => unsubscribe();
  }, [companyUID, currentUser?.uid, currentUser?.email, tutorialActive]);

  // Load the saved page for the on-demand setup banner. Never auto-open setup.
  useEffect(() => {
    if (!companyUID) return;

    setLinkedInCompanyPageChecked(false);

    // Check LinkedIn ID in company data. Older and newer integrations have used different field names.
    const checkLinkedInId = async () => {
      try {
        const companyRef = ref(database, `companies/${companyUID}`);
        const companySnapshot = await get(companyRef);
        const liId = companySnapshot.exists() ? getLinkedInCompanyId(companySnapshot.val()) : '';
        setUserLiId(liId || null);
      } catch (error) {
        console.error('Error checking LinkedIn ID:', error);
        setShowLinkedInPrompt(false);
      } finally {
        setLinkedInCompanyPageChecked(true);
      }
    };

    checkLinkedInId();
  }, [companyUID]);

  // Reset analytics view when switching jobs
  useEffect(() => {
    setShowJobAnalytics(false);
    setJobAnalyticsSeries([]);
  }, [selectedJob]);

  useEffect(() => {
    if (!pendingJobFocusId) return;
    const match = jobPosts.find(job => job.id === pendingJobFocusId);
    if (match) {
      setSelectedJob(match);
      setShowJobAnalytics(false);
      setPendingJobFocusId(null);
    }
  }, [pendingJobFocusId, jobPosts]);

  // Keep selectedJob in sync with real-time jobPosts updates
  useEffect(() => {
    if (!selectedJob) return;
    const updated = jobPosts.find(j => j.id === selectedJob.id);
    if (updated) setSelectedJob(updated);
  }, [jobPosts]);

  useEffect(() => {
    if (selectedJob && !teamAccess.canAccessDepartment(selectedJob.department)) {
      setSelectedJob(null);
    }
  }, [selectedJob, teamAccess]);

  useEffect(() => {
    if (jobFilter && !visibleMiniAnalyticsJobs.includes(jobFilter)) {
      setJobFilter('');
    }
  }, [jobFilter, visibleMiniAnalyticsJobs]);

  // Build mini analytics when inputs change
  useEffect(() => {
    const fetchMini = async () => {
      if (!companyUID) return;
      try {
        const monthKey = selectedMonth; // YYYY-MM
        // Build the last six months in chronological order.
        const months = Array.from({ length: 6 }).map((_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - (5 - i));
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          const label = d.toLocaleString(getUiLocale(), { month: 'short' });
          return { key, label };
        });
        // Load posts to map titles to ids and published status
        const postsRef = ref(database, `companies/${companyUID}/posts`);
        const postsSnap = await get(postsRef);
        const jobTitleToId = {};
        const jobTitleToPublished = {};
        const titles = [];
        if (postsSnap.exists()) {
          const postsData = postsSnap.val();
          Object.entries(postsData).forEach(([jobId, jobData]) => {
            if (jobData.jobOpening) {
              jobTitleToId[jobData.jobOpening] = jobId;
              jobTitleToPublished[jobData.jobOpening] = !!jobData.published;
              titles.push(jobData.jobOpening);
            }
          });
        }

        // Prepare per-job containers for series
        const perJobAppliesByMonth = {};
        const perJobViewsByMonth = {};
        titles.forEach((t) => {
          perJobAppliesByMonth[t] = months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {});
          perJobViewsByMonth[t] = months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {});
        });

        // Apply clicks by month - read from appropriate location based on published status
        const applyCounts = {};
        const applyTotalsByMonth = months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {});
        for (const [title, jobId] of Object.entries(jobTitleToId)) {
          const isPublished = jobTitleToPublished[title];
          const analyticsBasePath = isPublished
            ? `job_posts/${jobId}`
            : `companies/${companyUID}/posts/${jobId}`;

          let count = 0;
          try {
            const acRef = ref(database, `${analyticsBasePath}/applyClicksByMonth/${monthKey}`);
            const acSnap = await get(acRef);
            if (acSnap.exists()) {
              count = acSnap.val() || 0;
            }
          } catch (e) { }
          applyCounts[title] = count;
          // accumulate apply click totals across months for all jobs
          for (const m of months) {
            try {
              const mRef = ref(database, `${analyticsBasePath}/applyClicksByMonth/${m.key}`);
              const mSnap = await get(mRef);
              if (mSnap.exists()) {
                applyTotalsByMonth[m.key] = (applyTotalsByMonth[m.key] || 0) + (mSnap.val() || 0);
                perJobAppliesByMonth[title][m.key] = (mSnap.val() || 0);
              }
            } catch (e) { }
          }
        }

        // Views by month - read from appropriate location based on published status
        const viewsCounts = {};
        const viewTotalsByMonth = months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {});
        for (const [title, jobId] of Object.entries(jobTitleToId)) {
          const isPublished = jobTitleToPublished[title];
          const analyticsBasePath = isPublished
            ? `job_posts/${jobId}`
            : `companies/${companyUID}/posts/${jobId}`;

          let count = 0;
          try {
            const vmRef = ref(database, `${analyticsBasePath}/viewsByMonth/${monthKey}`);
            const vmSnap = await get(vmRef);
            if (vmSnap.exists()) {
              count = vmSnap.val() || 0;
            }
          } catch (e) {
            count = 0;
          }
          viewsCounts[title] = count;
          // accumulate timeseries per-month totals across all jobs
          for (const m of months) {
            try {
              const mRef = ref(database, `${analyticsBasePath}/viewsByMonth/${m.key}`);
              const mSnap = await get(mRef);
              if (mSnap.exists()) {
                viewTotalsByMonth[m.key] = (viewTotalsByMonth[m.key] || 0) + (mSnap.val() || 0);
                perJobViewsByMonth[title][m.key] = (mSnap.val() || 0);
              }
            } catch (e) { }
          }
        }

        // Ensure we include titles even if no data yet
        titles.forEach(t => {
          if (!(t in applyCounts)) applyCounts[t] = 0;
          if (!(t in viewsCounts)) viewsCounts[t] = 0;
        });

        const series = months.map(m => ({
          key: m.key,
          label: m.label,
          applies: applyTotalsByMonth[m.key] || 0,
          views: viewTotalsByMonth[m.key] || 0,
        }));

        // Build per-job series arrays
        const seriesByJob = {};
        titles.forEach((t) => {
          seriesByJob[t] = months.map((m) => ({
            key: m.key,
            label: m.label,
            applies: perJobAppliesByMonth[t][m.key] || 0,
            views: perJobViewsByMonth[t][m.key] || 0,
          }));
        });

        setMiniAnalytics({ jobs: Array.from(new Set(titles)), viewsCounts, applyCounts, series, seriesByJob });
      } catch (e) {
        console.error('Mini analytics error:', e);
        setMiniAnalytics({ jobs: [], viewsCounts: {}, applyCounts: {}, series: [] });
      }
    };
    fetchMini();
  }, [companyUID, selectedMonth, jobPosts.length]);

  // Authenticate user and determine company UID (matches vanilla site logic)
  const authenticateAndSetupCompany = async () => {
    try {
      setLoading(true);

      // First check if user is a company owner
      const companySnapshot = await get(ref(database, `companies/${currentUser.uid}`));

      if (companySnapshot.exists()) {
        // User is a company owner
        setCompanyUID(currentUser.uid);
        setIsCompanyOwner(true);
      } else {
        // Check if user is a collaborator
        const collabSnapshot = await get(ref(database, `collaborators/${currentUser.uid}`));

        if (collabSnapshot.exists()) {
          // User is a collaborator
          const collaboratorData = collabSnapshot.val();
          setCompanyUID(collaboratorData.companyId);
          setIsCompanyOwner(false);
        } else {
          // User is neither company owner nor collaborator
          console.log('User is not authorized. Signing out.');
          await logout();
          return;
        }
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      setIsCompanyOwner(false);
      setLoading(false);
    }
  };

  // Load company data from Firebase
  const loadUserData = async () => {
    try {
      const companyRef = ref(database, `companies/${companyUID}`);
      const snapshot = await get(companyRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        setCompanyData({
          companyName: data.companyName || '',
          city: data.city || '',
          country: data.country || '',
          description: data.description || ''
        });

        setUserLiId(getLinkedInCompanyId(data) || null);

        // Show EOR section if company is outside Lebanon (replicates old vanilla behaviour)
        if (data.country && data.country !== 'Lebanon') {
          setShowEOR(true);
        } else {
          setShowEOR(false);
        }
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  // Load job posts from Firebase once (fallback/manual refresh)
  const loadJobPosts = async () => {
    try {
      setLoading(true);
      const postsRef = ref(database, `companies/${companyUID}/posts`);
      const snapshot = await get(postsRef);
      if (snapshot.exists()) {
        const postsData = snapshot.val();
        const postsArray = Object.keys(postsData).map(key => ({
          id: key,
          ...postsData[key]
        }));
        // Sort by posted time, newest first
        postsArray.sort((a, b) => (b.postedTime || 0) - (a.postedTime || 0));
        setJobPosts(postsArray);
      } else {
        setJobPosts([]);
      }
    } catch (error) {
      console.error('Error loading job posts:', error);
      setJobPosts([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------
     Real-time job listener – replicates old site
     --------------------------------------------- */
  useEffect(() => {
    if (!companyUID) return;

    const postsRef = ref(database, `companies/${companyUID}/posts`);
    const unsubscribe = onValue(postsRef, (snapshot) => {
      if (snapshot.exists()) {
        const postsData = snapshot.val();
        const postsArray = Object.keys(postsData).map(key => ({ id: key, ...postsData[key] }));
        postsArray.sort((a, b) => (b.postedTime || 0) - (a.postedTime || 0));
        setJobPosts(postsArray);
      } else {
        setJobPosts([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Real-time job listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyUID]);

  // Re-initialize Quill when modal opens and we're on page 2
  useEffect(() => {
    if (showModal && modalPage === 2 && window.Quill && descriptionRef.current && !regularQuillReady) {
      // Clean up existing Quill instance
      if (window.quill) {
        window.quill = null;
      }

      initializeQuillEditor(descriptionRef.current);
      setRegularQuillReady(true);
    }

    // Reset when modal closes or page changes
    if (!showModal || modalPage !== 2) {
      setRegularQuillReady(false);
      if (window.quill) {
        window.quill = null;
      }
    }
  }, [showModal, modalPage, regularQuillReady]);

  // Initialize fullscreen Quill editor
  useEffect(() => {
    if (showFullscreenEditor && window.Quill && fullscreenDescriptionRef.current && !fullscreenQuillReady && !window.fullscreenQuill) {
      initializeFullscreenQuillEditor(fullscreenDescriptionRef.current);
      setFullscreenQuillReady(true);
    }

    // Cleanup when fullscreen editor closes
    if (!showFullscreenEditor && fullscreenQuillReady) {
      if (window.fullscreenQuill) {
        window.fullscreenQuill = null;
      }
      setFullscreenQuillReady(false);
    }
  }, [showFullscreenEditor, fullscreenQuillReady]);

  // Utility function to initialize Quill editor
  const initializeQuillEditor = (container) => {
    if (!container || !window.Quill || window.quill) return;

    try {
      container.innerHTML = '';

      window.quill = new window.Quill(container, {
        theme: 'snow',
        placeholder: t('formLabels.jobDescriptionPlaceholder', 'Enter your job description, or list the required skills and qualifications and click "Generate" to create one with AI.'),
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['link'],
            ['clean']
          ]
        }
      });

      window.quill.on('text-change', () => {
        setJobDescription(window.quill.getText());
        window.tempQuillContent = window.quill.root.innerHTML;
      });

      if (window.tempQuillContent) {
        window.quill.clipboard.dangerouslyPasteHTML(window.tempQuillContent);
      } else if (jobDescription) {
        window.quill.setText(jobDescription);
      }
    } catch (error) {
      console.error('Error initializing Quill:', error);
    }
  };

  // Utility function to initialize fullscreen Quill editor
  const initializeFullscreenQuillEditor = (container) => {
    if (!container || !window.Quill || window.fullscreenQuill) return;

    try {
      container.innerHTML = '';

      window.fullscreenQuill = new window.Quill(container, {
        theme: 'snow',
        placeholder: t('formLabels.jobDescriptionPlaceholder', 'Enter your job description, or list the required skills and qualifications and click "Generate" to create one with AI.'),
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': [] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['blockquote', 'code-block'],
            ['clean']
          ]
        }
      });

      if (window.tempQuillContent) {
        window.fullscreenQuill.clipboard.dangerouslyPasteHTML(window.tempQuillContent);
      } else if (jobDescription) {
        window.fullscreenQuill.setText(jobDescription);
      }

      window.fullscreenQuill.on('text-change', () => {
        setJobDescription(window.fullscreenQuill.getText());
        window.tempQuillContent = window.fullscreenQuill.root.innerHTML;
      });
    } catch (error) {
      console.error('Error initializing fullscreen Quill:', error);
    }
  };

  // Utility functions
  const getEnglishFromMultilang = (string) => {
    if (!string) return '';
    return string.split(" // ")[0];
  };

  const renderStatusTag = (published, approvalStatus, pendingUpdateStatus) => {
    if (approvalStatus === 'pending_approval') {
      return (
        <span className="status-tag" style={{ backgroundColor: 'rgba(255, 193, 7, 0.15)', color: 'var(--app-tone-ffc107, #ffc107)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
          <i className="fa-solid fa-clock" style={{ marginRight: '6px' }}></i>{uiText("Pending Approval")}</span>
      );
    }
    if (published && pendingUpdateStatus === 'pending_approval') {
      return (
        <span className="status-tag" style={{ backgroundColor: 'rgba(255, 193, 7, 0.15)', color: 'var(--app-tone-ffc107, #ffc107)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
          <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '6px' }}></i>{uiText("Update Pending")}</span>
      );
    }
    if (published && pendingUpdateStatus === 'rejected') {
      return (
        <span className="status-tag" style={{ backgroundColor: 'rgba(220, 53, 69, 0.15)', color: 'var(--app-tone-dc3545, #dc3545)', border: '1px solid rgba(220, 53, 69, 0.3)' }}>
          <i className="fa-solid fa-xmark" style={{ marginRight: '6px' }}></i>{uiText("Update Rejected")}</span>
      );
    }
    if (approvalStatus === 'rejected') {
      return (
        <span className="status-tag" style={{ backgroundColor: 'rgba(220, 53, 69, 0.15)', color: 'var(--app-tone-dc3545, #dc3545)', border: '1px solid rgba(220, 53, 69, 0.3)' }}>
          <i className="fa-solid fa-xmark" style={{ marginRight: '6px' }}></i>{uiText("Rejected")}</span>
      );
    }
    return published ? (
      <span className="status-tag status-published">{uiText("Published")}</span>
    ) : (
      <span className="status-tag status-unpublished">{uiText("Unpublished")}</span>
    );
  };

  const formatNotificationDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    if (Number.isNaN(date.getTime())) return '';
    const dateText = date.toLocaleDateString(getUiLocale());
    const timeText = date.toLocaleTimeString(getUiLocale(), { hour: '2-digit', minute: '2-digit' });
    return `${dateText} • ${timeText}`;
  };

  const getNotificationAction = (notif) => {
    if (!notif) return null;
    const manpowerRequestId = notif.manpowerRequestId
      || notif.requestId
      || ((typeof notif.type === 'string' && notif.type.includes('manpower')) ? (notif.entityId || '') : '');

    if (
      notif.type === 'manpower_request'
      || notif.type === 'manpower_request_update'
      || notif.type === 'manpower_request_approval'
      || (manpowerRequestId && typeof notif.type === 'string' && notif.type.includes('manpower'))
    ) {
      return { type: 'manpower', requestId: manpowerRequestId };
    }

    if (
      (notif.type === 'approval_request' || notif.type === 'approval_decision')
      && notif.approvalType === 'job_posting'
    ) {
      return { type: 'job_approval', jobId: notif.entityId || '' };
    }

    if (notif.type === 'approval_request' || notif.type === 'approval_decision') {
      return { type: 'route', path: notif.approvalType === 'offer_letter' ? '/applicants' : '/home' };
    }

    if (notif.type === 'onboarding_task' || notif.type === 'onboarding_task_reminder') {
      return { type: 'route', path: '/employee-onboarding' };
    }

    if (['new_application', 'assessment_completed', 'unread_chat', 'unmoved_candidate'].includes(notif.type)) {
      return { type: 'route', path: '/applicants' };
    }

    if (notif.type === 'applicant_note_mention' && notif.applicantId) {
      const query = new URLSearchParams({
        applicantId: notif.applicantId,
        connectionKey: notif.connectionKey || '',
        jobId: notif.jobId || '',
        jobTitle: notif.jobTitle || '',
        tab: 'team'
      });
      return { type: 'route', path: `/applicants?${query.toString()}` };
    }

    return null;
  };

  const navigateToNotification = (notif) => {
    const action = getNotificationAction(notif);
    if (!action) return;

    if (action.type === 'manpower') {
      if (!canCreateOrEditJobs) {
        return;
      }
      setShowNotifsModal(false);
      navigate('/manpower-requests', {
        state: {
          manpowerFocusId: action.requestId || null
        }
      });
      return;
    }

    if (action.type === 'job_approval') {
      setShowNotifsModal(false);
      if (action.jobId) {
        const targetJob = jobPosts.find(job => job.id === action.jobId);
        if (targetJob) {
          setSelectedJob(targetJob);
          setShowJobAnalytics(false);
          return;
        }
        setPendingJobFocusId(action.jobId);
      }
      navigate('/home');
      return;
    }

    if (action.type === 'route') {
      setShowNotifsModal(false);
      navigate(action.path);
    }
  };

  const clearNotifications = async () => {
    if (!companyUID || !currentUser?.uid) return;
    try {
      const clearedAt = Date.now();
      await Promise.all([
        set(ref(database, `companies/${companyUID}/user_notification_clears/${currentUser.uid}`), clearedAt),
        remove(ref(database, `companies/${companyUID}/user_notifications/${currentUser.uid}`))
      ]);
      notificationsClearedAtRef.current = clearedAt;
      setNotifs([]);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      showHomeNotice('Could not clear notifications. Please try again.');
    }
  };

  // Build 6-month analytics for a specific job
  const handleSeeAnalytics = async (job) => {
    try {
      setShowJobAnalytics(true);
      setJobAnalyticsLoading(true);
      const months = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - (5 - i));
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleString(getUiLocale(), { month: 'short' });
        return { key, label };
      });

      // Determine where to read analytics from based on published status
      const isPublished = !!job.published;
      const analyticsBasePath = isPublished
        ? `job_posts/${job.id}`
        : `companies/${companyUID}/posts/${job.id}`;

      // Apply clicks: read from appropriate location
      const appliesByMonth = {};
      for (const m of months) {
        let v = 0;
        try {
          const acRef = ref(database, `${analyticsBasePath}/applyClicksByMonth/${m.key}`);
          const acSnap = await get(acRef);
          if (acSnap.exists()) v = acSnap.val() || 0;
        } catch (e) { }
        appliesByMonth[m.key] = v;
      }

      // Views: read from appropriate location
      const viewsByMonth = {};
      for (const m of months) {
        let v = 0;
        try {
          const vmRef = ref(database, `${analyticsBasePath}/viewsByMonth/${m.key}`);
          const vmSnap = await get(vmRef);
          if (vmSnap.exists()) v = vmSnap.val() || 0;
          else v = Math.floor((appliesByMonth[m.key] || 0) * 6);
        } catch (e) {
          v = Math.floor((appliesByMonth[m.key] || 0) * 6);
        }
        viewsByMonth[m.key] = v;
      }

      let series = months.map(m => ({
        key: m.key,
        label: m.label,
        applies: appliesByMonth[m.key] || 0,
        views: viewsByMonth[m.key] || 0,
      }));

      setJobAnalyticsSeries(series);
    } catch (e) {
      console.error('Job analytics error:', e);
      setJobAnalyticsSeries([]);
    } finally {
      setJobAnalyticsLoading(false);
    }
  };

  const renderJobAnalyticsChart = () => {
    if (jobAnalyticsLoading) return <p style={{ color: 'var(--app-text, #FFFFFF)' }}>{uiText("Loading analytics…")}</p>;
    const data = jobAnalyticsSeries;
    if (!data || data.length === 0) return <p style={{ color: 'var(--app-text, #FFFFFF)' }}>{uiText("No analytics available.")}</p>;

    return <JobAnalyticsChart data={data} metric={jobAnalyticsMetric} isRTL={isRTL} locale={getUiLocale()} width={520} height={180} svgHeight={200} label={t(jobAnalyticsMetric === 'views' ? 'analytics.views' : 'analytics.applies')} />;
  };

  const expandJobPost = (jobKey, isButton = false) => {
    const element = document.getElementById(jobKey);
    if (!element) return;

    if (element.classList.contains('expanded') && !isButton) {
      return;
    }

    const hiddenDiv = element.getElementsByClassName('more-info')[0];
    const buttonsArea = element.getElementsByClassName('buttons-area')[0];

    if (hiddenDiv) hiddenDiv.classList.toggle('show');
    if (buttonsArea) buttonsArea.classList.toggle('show');
    element.classList.toggle('expanded');
  };

  useEffect(() => {
    if (!companyUID) {
      setDefaultApplicationMethod('ats');
      return undefined;
    }
    return onValue(ref(database, `companies/${companyUID}/settings/defaultApplicationMethod`), snapshot => {
      setDefaultApplicationMethod(snapshot.val() === 'external' ? 'external' : 'ats');
    });
  }, [companyUID]);

  // Form handlers
  const handleFormChange = (field, value) => {
    manualJobFields.current.add(field);
    const inferredDepartment = field === 'jobCategory'
      ? departments.find(department => department.toLowerCase() === String(value).split('//')[0].trim().toLowerCase()) || ''
      : null;
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(!editingMode && field === 'jobOpening' && !manualJobFields.current.has('seniority')
        ? { seniority: inferJobSeniority(value) } : {}),
      ...(!editingMode && inferredDepartment !== null && !manualJobFields.current.has('department')
        ? { department: inferredDepartment } : {})
    }));

    if (field === 'department' || (!editingMode && inferredDepartment !== null && !manualJobFields.current.has('department'))) {
      setJobApplicationDraftSettings(buildPublicApplicationSettingsForJob(field === 'department' ? value : inferredDepartment));
    }
  };

  const ensureTagGroup = (rawLabel) => {
    const label = normalizeTagLabel(rawLabel);
    const key = buildAnalyticsKey(label);
    if (!label || !key) return '';

    const existingGroup = jobTagGroups.find((group) => group.key === key);
    if (existingGroup) return existingGroup.key;

    setJobTagGroups((prev) => {
      if (prev.some((group) => group.key === key)) return prev;
      return [...prev, { key, label, tags: [] }];
    });

    return key;
  };

  const removeTagGroup = (groupKey) => {
    if (!groupKey) return;

    setJobTagGroups((prev) => prev.filter((group) => group.key !== groupKey));

    setSelectedTagGroupKey((prev) => (prev === groupKey ? UNGROUPED_JOB_TAG_KEY : prev));
  };

  const addJobTag = () => {
    const label = normalizeTagLabel(tagInput);
    const key = buildAnalyticsKey(label);
    if (!label || !key) return;

    const createdGroupKey = ensureTagGroup(tagGroupInput);
    const targetGroupKey = createdGroupKey || selectedTagGroupKey;

    if (targetGroupKey && targetGroupKey !== UNGROUPED_JOB_TAG_KEY) {
      setJobTagGroups((prev) => prev.map((group) => {
        if (group.key !== targetGroupKey) return group;
        if ((group.tags || []).some((tag) => tag.key === key)) return group;
        return {
          ...group,
          tags: [...(group.tags || []), { key, label }]
        };
      }));
    } else {
      setJobUngroupedTags((prev) => {
        if (prev.some((tag) => tag.key === key)) return prev;
        return [...prev, { key, label }];
      });
    }

    if (createdGroupKey) {
      setSelectedTagGroupKey(createdGroupKey);
    }
    setTagGroupInput('');
    setTagInput('');
  };

  const removeJobTag = (tagKey, groupKey = UNGROUPED_JOB_TAG_KEY) => {
    if (!tagKey) return;

    if (groupKey && groupKey !== UNGROUPED_JOB_TAG_KEY) {
      setJobTagGroups((prev) => prev.map((group) => (
        group.key === groupKey
          ? { ...group, tags: (group.tags || []).filter((tag) => tag.key !== tagKey) }
          : group
      )));
      return;
    }

    setJobUngroupedTags((prev) => prev.filter((tag) => tag.key !== tagKey));
  };

  const hasDepartmentApplicationOverride = Boolean(
    applicationFormScope === 'department'
    && applicationFormDepartment
    && departmentPublicJobApplicationSettings[applicationFormDepartment]
  );

  const activeApplicationSettings = (() => {
    if (applicationFormScope === 'department' && applicationFormDepartment) {
      return departmentPublicJobApplicationSettings[applicationFormDepartment] || generalPublicJobApplicationSettings;
    }
    return generalPublicJobApplicationSettings;
  })();
  const activeApplicationSettingsSignature = getPublicJobSettingsSignature(activeApplicationSettings);
  const savedActiveApplicationSettingsSignature = applicationFormScope === 'department' && applicationFormDepartment
    ? (savedDepartmentPublicJobApplicationSignatures[applicationFormDepartment] || savedGeneralPublicJobApplicationSignature)
    : savedGeneralPublicJobApplicationSignature;
  const hasUnsavedApplicationFormChanges = activeApplicationSettingsSignature !== savedActiveApplicationSettingsSignature;

  const buildPublicApplicationSettingsForJob = (department = '') => {
    const departmentKey = sanitizeDepartmentSettingsKey(department);
    const settings = departmentKey && departmentPublicJobApplicationSettings[departmentKey]
      ? departmentPublicJobApplicationSettings[departmentKey]
      : generalPublicJobApplicationSettings;

    return buildPublicJobSettingsPayload(settings);
  };

  const getJobApplicationSettingsForSubmit = () => (
    jobApplicationDraftSettings
      ? buildPublicJobSettingsPayload(jobApplicationDraftSettings)
      : buildPublicApplicationSettingsForJob(formData.department || '')
  );

  const getCurrentJobDescriptionHtml = () => {
    if (window.fullscreenQuill?.root) {
      return window.fullscreenQuill.root.innerHTML || '';
    }

    if (window.quill?.root) {
      return window.quill.root.innerHTML || '';
    }

    return window.tempQuillContent || '';
  };

  const buildJobModalSnapshot = (overrides = {}) => {
    const snapshotFormData = overrides.formData || formData;
    const snapshotApplicationSettings = Object.prototype.hasOwnProperty.call(overrides, 'jobApplicationDraftSettings')
      ? overrides.jobApplicationDraftSettings
      : jobApplicationDraftSettings;
    const applicationSettings = snapshotApplicationSettings
      || buildPublicApplicationSettingsForJob(snapshotFormData.department || '');

    return JSON.stringify({
      editingMode: overrides.editingMode ?? editingMode,
      currentJobKey: overrides.currentJobKey ?? currentJobKey,
      formData: snapshotFormData,
      currentAppType: overrides.currentAppType ?? currentAppType,
      jobDescription: normalizeJobModalDescriptionText(overrides.jobDescription ?? jobDescription),
      jobDescriptionHTML: normalizeJobModalDescriptionHtml(overrides.jobDescriptionHTML ?? getCurrentJobDescriptionHtml()),
      jobTagGroups: overrides.jobTagGroups || jobTagGroups,
      jobUngroupedTags: overrides.jobUngroupedTags || jobUngroupedTags,
      tagGroupInput: overrides.tagGroupInput ?? tagGroupInput,
      tagInput: overrides.tagInput ?? tagInput,
      selectedTagGroupKey: overrides.selectedTagGroupKey ?? selectedTagGroupKey,
      quickApplicationFieldLabel: overrides.quickApplicationFieldLabel ?? quickApplicationFieldLabel,
      quickApplicationFieldType: overrides.quickApplicationFieldType ?? quickApplicationFieldType,
      quickApplicationFieldRequired: overrides.quickApplicationFieldRequired ?? quickApplicationFieldRequired,
      applicationSettingsSignature: getPublicJobSettingsSignature(applicationSettings),
      sourceManpowerRequestId: overrides.sourceManpowerRequestId ?? sourceManpowerRequestId ?? ''
    });
  };

  const markJobModalSnapshot = (overrides = {}) => {
    jobModalInitialSnapshotRef.current = buildJobModalSnapshot(overrides);
  };

  const hasJobModalPendingChanges = () => (
    Boolean(jobModalInitialSnapshotRef.current)
    && buildJobModalSnapshot() !== jobModalInitialSnapshotRef.current
  );

  const isApplicationPresetOption = (value) => String(value || '').startsWith('preset:');

  const getApplicationPresetKeyFromOption = (value) => (
    isApplicationPresetOption(value) ? String(value).replace('preset:', '') : ''
  );

  const getApplicationFieldAddOptions = () => ([
    { value: 'text', label: t('manageJobs.fieldTypeShortText', 'Short text') },
    { value: 'textarea', label: t('manageJobs.fieldTypeLongText', 'Long text') },
    { value: 'url', label: t('manageJobs.fieldTypeLink', 'Link') },
    { value: 'file', label: t('manageJobs.fieldTypeFile', 'File upload') },
    { value: 'video', label: t('manageJobs.fieldTypeVideo', 'Video submission') },
    { value: 'boolean', label: t('manageJobs.fieldTypeYesNo', 'Yes / No') },
    { value: 'preset:coverLetter', label: t('manageJobs.coverLetterPreset', 'Cover Letter') },
    { value: 'preset:portfolioLink', label: t('manageJobs.portfolioPreset', 'Portfolio Link') },
    { value: 'preset:linkedinUrl', label: t('manageJobs.linkedinPreset', 'LinkedIn URL') }
  ]);

  const getApplicationPresetDuplicateMessage = (presetKey) => (
    presetKey === 'coverLetter'
      ? t('manageJobs.coverLetterAlreadyAdded', 'Cover Letter has already been added.')
      : presetKey === 'linkedinUrl'
        ? t('manageJobs.linkedinAlreadyAdded', 'LinkedIn URL has already been added.')
        : t('manageJobs.portfolioAlreadyAdded', 'Portfolio Link has already been added.')
  );

  const buildApplicationFieldFromAddSelection = ({ selectedType, label, required }) => {
    const presetKey = getApplicationPresetKeyFromOption(selectedType);
    if (presetKey) {
      const preset = PUBLIC_APPLICATION_PRESET_FIELDS[presetKey];
      return preset ? { ...preset } : null;
    }

    const normalizedLabel = normalizeTagLabel(label);
    if (!normalizedLabel) return null;

    return {
      id: buildPublicApplicationFieldId(normalizedLabel),
      label: normalizedLabel,
      type: selectedType,
      required
    };
  };

  const resetQuickApplicationFieldComposer = () => {
    setQuickApplicationFieldLabel('');
    setQuickApplicationFieldType('text');
    setQuickApplicationFieldRequired(false);
  };

  const handleQuickApplicationFieldTypeChange = (value) => {
    setQuickApplicationFieldType(value);
    if (isApplicationPresetOption(value)) {
      setQuickApplicationFieldLabel('');
      setQuickApplicationFieldRequired(false);
    }
  };

  const handleNewApplicationFieldTypeChange = (value) => {
    setNewApplicationFieldType(value);
    if (isApplicationPresetOption(value)) {
      setNewApplicationFieldLabel('');
      setNewApplicationFieldRequired(false);
    }
  };

  const handleAddQuickApplicationField = () => {
    const presetKey = getApplicationPresetKeyFromOption(quickApplicationFieldType);
    const label = normalizeTagLabel(quickApplicationFieldLabel);
    if (!presetKey && !label) {
      showHomeNotice(t('manageJobs.fieldLabelRequired', 'Enter a field label first.'));
      return;
    }

    const currentSettings = jobApplicationDraftSettings || buildPublicApplicationSettingsForJob(formData.department || '');
    const currentFields = currentSettings.fields || [];
    if (presetKey && currentFields.some((field) => field.systemKey === presetKey)) {
      showHomeNotice(getApplicationPresetDuplicateMessage(presetKey));
      return;
    }

    if (currentFields.length >= PUBLIC_APPLICATION_MAX_FIELDS) {
      showHomeNotice(t('manageJobs.maxFieldsReached', 'You can add up to 20 extra fields.'));
      return;
    }

    const nextField = buildApplicationFieldFromAddSelection({
      selectedType: quickApplicationFieldType,
      label,
      required: quickApplicationFieldRequired
    });
    if (!nextField) return;

    setJobApplicationDraftSettings(buildPublicJobSettingsPayload({
      ...currentSettings,
      fields: [...currentFields, nextField]
    }));
    resetQuickApplicationFieldComposer();
  };

  const handleRemoveQuickApplicationField = (fieldId) => {
    const currentSettings = jobApplicationDraftSettings || buildPublicApplicationSettingsForJob(formData.department || '');
    setJobApplicationDraftSettings(buildPublicJobSettingsPayload({
      ...currentSettings,
      fields: (currentSettings.fields || []).filter((field) => field.id !== fieldId)
    }));
  };

  useEffect(() => {
    if (publicJobSettingsSaveState !== 'idle' && hasUnsavedApplicationFormChanges) {
      setPublicJobSettingsSaveState('idle');
      setPublicJobSettingsSaveMessage('');
    }
  }, [publicJobSettingsSaveState, hasUnsavedApplicationFormChanges]);

  useEffect(() => {
    if (publicJobSettingsSaveState !== 'saved' || !publicJobSettingsSaveMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setPublicJobSettingsSaveState('idle');
      setPublicJobSettingsSaveMessage('');
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [publicJobSettingsSaveState, publicJobSettingsSaveMessage]);

  const updateActiveApplicationSettings = (updater) => {
    if (applicationFormScope === 'department' && applicationFormDepartment) {
      setDepartmentPublicJobApplicationSettings((prev) => {
        const current = prev[applicationFormDepartment] || generalPublicJobApplicationSettings || getDefaultPublicJobApplicationSettings();
        const next = typeof updater === 'function' ? updater(current) : updater;
        return {
          ...prev,
          [applicationFormDepartment]: next
        };
      });
      return;
    }

    setGeneralPublicJobApplicationSettings((prev) => (
      typeof updater === 'function' ? updater(prev) : updater
    ));
  };

  const resetForm = () => {
    setFormData(getDefaultJobFormData(currentUserRecruiterOption.id || ''));
    setJobTagGroups([]);
    setJobUngroupedTags([]);
    setTagGroupInput('');
    setTagInput('');
    setSelectedTagGroupKey(UNGROUPED_JOB_TAG_KEY);
    setJobDescription('');
    setCurrentAppType('link');
    setJobApplicationDraftSettings(null);
    resetQuickApplicationFieldComposer();
    window.tempQuillContent = '';
    if (window.quill) {
      window.quill.setText('');
    }
    if (window.fullscreenQuill) {
      window.fullscreenQuill.setText('');
    }
    window.tempQuillContent = '';
    setModalPage(1);
    setJobSubmitAttempted(false);
  };

  const resetApplicationFieldComposer = () => {
    setNewApplicationFieldLabel('');
    setNewApplicationFieldType('text');
    setNewApplicationFieldRequired(false);
  };

  const closeApplicationFormBuilder = () => {
    setShowApplicationFormBuilder(false);
    setApplicationFormScope('general');
    setApplicationFormDepartment('');
    setApplicationFormViewMode('edit');
    setPublicJobSettingsSaveState('idle');
    setPublicJobSettingsSaveMessage('');
    resetApplicationFieldComposer();
  };

  const confirmApplicationFormExit = async () => {
    if (!hasUnsavedApplicationFormChanges) return true;

    return await confirmHomeAction(
      t('manageJobs.unsavedLeavePrompt', 'You have unsaved changes. Leave this form without saving?')
    );
  };

  const handleCloseApplicationFormBuilder = async () => {
    if (!(await confirmApplicationFormExit())) return;
    closeApplicationFormBuilder();
  };

  const savePublicJobApplicationSettings = async (scope, settings, departmentKey = '') => {
    if (!companyUID) return;

    const payload = buildPublicJobSettingsPayload(settings);
    setPublicJobSettingsSaving(true);
    try {
      const updates = {};
      if (scope === 'department' && departmentKey) {
        updates[`companies/${companyUID}/settings/publicJobApplicationDepartments/${departmentKey}`] = payload;
        updates[`companies/${companyUID}/publicJobApplicationDepartments/${departmentKey}`] = payload;
      } else {
        updates[`companies/${companyUID}/settings/publicJobApplication`] = payload;
        updates[`companies/${companyUID}/publicJobApplication`] = payload;
      }

      const postsSnapshot = await get(ref(database, `companies/${companyUID}/posts`));
      const posts = postsSnapshot.val() || {};
      Object.entries(posts).forEach(([jobId, job]) => {
        const jobDepartmentKey = sanitizeDepartmentSettingsKey(job?.department || '');
        const jobHasTargetDepartment = scope === 'department'
          ? jobDepartmentKey === departmentKey
          : !jobDepartmentKey || !departmentPublicJobApplicationSettings[jobDepartmentKey];

        if (!jobHasTargetDepartment) return;

        updates[`companies/${companyUID}/posts/${jobId}/publicJobApplicationSettings`] = payload;
        updates[`companies/${companyUID}/posts/${jobId}/applicationFormConfig`] = payload;
        updates[`companies/${companyUID}/posts/${jobId}/publicJobApplication`] = payload;
      });

      await update(ref(database), updates);
      setJobPosts((prev) => prev.map((job) => {
        const jobDepartmentKey = sanitizeDepartmentSettingsKey(job?.department || '');
        const jobHasTargetDepartment = scope === 'department'
          ? jobDepartmentKey === departmentKey
          : !jobDepartmentKey || !departmentPublicJobApplicationSettings[jobDepartmentKey];
        return jobHasTargetDepartment
          ? {
              ...job,
              publicJobApplicationSettings: payload,
              applicationFormConfig: payload,
              publicJobApplication: payload
            }
          : job;
      }));
      if (selectedJob) {
        const selectedDepartmentKey = sanitizeDepartmentSettingsKey(selectedJob?.department || '');
        const selectedHasTargetDepartment = scope === 'department'
          ? selectedDepartmentKey === departmentKey
          : !selectedDepartmentKey || !departmentPublicJobApplicationSettings[selectedDepartmentKey];
        if (selectedHasTargetDepartment) {
          setSelectedJob((prev) => prev
            ? {
                ...prev,
                publicJobApplicationSettings: payload,
                applicationFormConfig: payload,
                publicJobApplication: payload
              }
            : prev);
        }
      }
    } catch (error) {
      console.error('Error saving public job application settings:', error);
      throw error;
    } finally {
      setPublicJobSettingsSaving(false);
    }
  };

  const handleOpenApplicationFormBuilder = (options = {}) => {
    const departmentKey = options.scope === 'department'
      ? sanitizeDepartmentSettingsKey(options.departmentKey || '')
      : '';

    setApplicationFormScope(departmentKey ? 'department' : 'general');
    setApplicationFormDepartment(departmentKey);
    setApplicationFormViewMode('edit');
    setPublicJobSettingsSaveState('idle');
    setPublicJobSettingsSaveMessage('');
    resetApplicationFieldComposer();
    setShowApplicationFormBuilder(true);
  };

  useEffect(() => {
    const handlePopState = async () => {
      window.history.pushState(null, null, window.location.href);

      if (!showApplicationFormBuilder) return;
      if (!(await confirmApplicationFormExit())) return;

      closeApplicationFormBuilder();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showApplicationFormBuilder, hasUnsavedApplicationFormChanges, t]);

  const handleSaveActiveApplicationSettings = async () => {
    if (applicationFormScope === 'department' && !applicationFormDepartment) {
      showHomeNotice(t('manageJobs.selectDepartmentPrompt', 'Choose a department to edit its application form override.'));
      return;
    }

    if (!hasUnsavedApplicationFormChanges) {
      setPublicJobSettingsSaveState('saved');
      setPublicJobSettingsSaveMessage(t('manageJobs.applicationFormAllChangesSaved', 'All changes saved.'));
      return;
    }

    try {
      await savePublicJobApplicationSettings(applicationFormScope, activeApplicationSettings, applicationFormDepartment);
      if (applicationFormScope === 'department' && applicationFormDepartment) {
        setSavedDepartmentPublicJobApplicationSignatures((prev) => ({
          ...prev,
          [applicationFormDepartment]: activeApplicationSettingsSignature
        }));
      } else {
        setSavedGeneralPublicJobApplicationSignature(activeApplicationSettingsSignature);
      }
      setPublicJobSettingsSaveState('saved');
      setPublicJobSettingsSaveMessage(t('manageJobs.applicationFormSaved', 'Application form saved.'));
    } catch (error) {
      showHomeNotice(t('manageJobs.applicationFormSaveFailed', 'Failed to save application form. Please try again.'));
    }
  };

  const handleAddApplicationField = () => {
    if (applicationFormScope === 'department' && !applicationFormDepartment) {
      showHomeNotice(t('manageJobs.selectDepartmentPrompt', 'Choose a department to edit its application form override.'));
      return;
    }

    const presetKey = getApplicationPresetKeyFromOption(newApplicationFieldType);
    if (presetKey) {
      handleAddApplicationPresetField(presetKey);
      resetApplicationFieldComposer();
      return;
    }

    const label = normalizeTagLabel(newApplicationFieldLabel);
    if (!label) {
      showHomeNotice(t('manageJobs.fieldLabelRequired', 'Enter a field label first.'));
      return;
    }

    const currentFields = activeApplicationSettings?.fields || [];
    if (currentFields.length >= PUBLIC_APPLICATION_MAX_FIELDS) {
      showHomeNotice(t('manageJobs.maxFieldsReached', 'You can add up to 20 extra fields.'));
      return;
    }

    updateActiveApplicationSettings((prev) => ({
      ...prev,
      fields: [
        ...(prev.fields || []),
        {
          ...buildApplicationFieldFromAddSelection({
            selectedType: newApplicationFieldType,
            label,
            required: newApplicationFieldRequired
          }),
          options: [],
          systemKey: ''
        }
      ]
    }));
    resetApplicationFieldComposer();
  };

  const handleAddApplicationPresetField = (presetKey) => {
    if (applicationFormScope === 'department' && !applicationFormDepartment) {
      showHomeNotice(t('manageJobs.selectDepartmentPrompt', 'Choose a department to edit its application form override.'));
      return;
    }

    const preset = PUBLIC_APPLICATION_PRESET_FIELDS[presetKey];
    if (!preset) return;

    updateActiveApplicationSettings((prev) => {
      if ((prev.fields || []).some((field) => field.systemKey === preset.systemKey)) {
        showHomeNotice(getApplicationPresetDuplicateMessage(preset.systemKey));
        return prev;
      }

      return {
        ...prev,
        coverLetterEnabled: preset.systemKey === 'coverLetter' ? true : prev.coverLetterEnabled === true,
        fields: [...(prev.fields || []), { ...preset }]
      };
    });
  };

  const handleUpdateApplicationField = (fieldId, patch) => {
    updateActiveApplicationSettings((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((field) => {
        if (field.id !== fieldId) return field;
        const requestedType = String(patch.type || field.type || 'text');
        const nextType = requestedType === 'select' ? 'text' : requestedType;

        return {
          ...field,
          ...patch,
          type: nextType,
          options: []
        };
      })
    }));
  };

  const handleStartApplicationFieldDrag = (fieldId) => {
    setDraggedApplicationFieldId(fieldId);
    setApplicationFieldDropIndicator({ fieldId: '', position: 'before' });
  };

  const handleDragOverApplicationField = (event, targetFieldId) => {
    event.preventDefault();
    if (!targetFieldId || draggedApplicationFieldId === targetFieldId) {
      setApplicationFieldDropIndicator({ fieldId: '', position: 'before' });
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const offset = event.clientY - rect.top;
    const position = offset < rect.height / 2 ? 'before' : 'after';
    setApplicationFieldDropIndicator({ fieldId: targetFieldId, position });
  };

  const handleDropApplicationField = (targetFieldId) => {
    if (!draggedApplicationFieldId || !targetFieldId || draggedApplicationFieldId === targetFieldId) {
      setDraggedApplicationFieldId('');
      setApplicationFieldDropIndicator({ fieldId: '', position: 'before' });
      return;
    }

    updateActiveApplicationSettings((prev) => {
      const fields = [...(prev.fields || [])];
      const draggedIndex = fields.findIndex((field) => field.id === draggedApplicationFieldId);
      const targetIndex = fields.findIndex((field) => field.id === targetFieldId);
      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const [draggedField] = fields.splice(draggedIndex, 1);
      let insertIndex = targetIndex + (applicationFieldDropIndicator.position === 'after' ? 1 : 0);
      if (draggedIndex < insertIndex) {
        insertIndex -= 1;
      }
      fields.splice(insertIndex, 0, draggedField);

      return {
        ...prev,
        fields
      };
    });

    setDraggedApplicationFieldId('');
    setApplicationFieldDropIndicator({ fieldId: '', position: 'before' });
  };

  const handleRemoveApplicationField = (fieldId) => {
    updateActiveApplicationSettings((prev) => ({
      ...prev,
      coverLetterEnabled: (prev.fields || []).some((field) => field.id === fieldId && field.systemKey === 'coverLetter')
        ? false
        : prev.coverLetterEnabled === true,
      fields: (prev.fields || []).filter((field) => field.id !== fieldId)
    }));
  };

  const autoResizeApplicationQuestion = (event) => {
    const element = event.currentTarget;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  useEffect(() => {
    if (!showApplicationFormBuilder || applicationFormViewMode !== 'edit') return undefined;

    const resizeAll = () => {
      document
        .querySelectorAll('.application-builder-row-edit textarea, .application-builder-add-inline textarea')
        .forEach((element) => {
          element.style.height = 'auto';
          element.style.height = `${element.scrollHeight}px`;
        });
    };

    const frameId = window.requestAnimationFrame(resizeAll);
    window.addEventListener('resize', resizeAll);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resizeAll);
    };
  }, [showApplicationFormBuilder, applicationFormViewMode, activeApplicationSettings.fields, newApplicationFieldLabel]);

  const handleResetDepartmentApplicationSettings = async () => {
    if (!applicationFormDepartment || !companyUID) return;

    const previousSettings = departmentPublicJobApplicationSettings;
    setDepartmentPublicJobApplicationSettings((prev) => {
      const next = { ...prev };
      delete next[applicationFormDepartment];
      return next;
    });

    setPublicJobSettingsSaving(true);
    try {
      await Promise.all([
        remove(ref(database, `companies/${companyUID}/settings/publicJobApplicationDepartments/${applicationFormDepartment}`)),
        remove(ref(database, `companies/${companyUID}/publicJobApplicationDepartments/${applicationFormDepartment}`))
      ]);
    } catch (error) {
      setDepartmentPublicJobApplicationSettings(previousSettings);
      showHomeNotice(t('manageJobs.departmentResetFailed', 'Failed to reset the department form. Please try again.'));
    } finally {
      setPublicJobSettingsSaving(false);
    }
  };

  const closeModal = async (options = {}) => {
    if (jobPostingBusy.current && options?.skipConfirmation !== true) return false;
    if (options?.skipConfirmation !== true && hasJobModalPendingChanges()) {
      const confirmed = await confirmHomeAction(
        t('formLabels.unsavedJobClosePrompt', 'Are you sure you want to close? Changes will not be saved.')
      );

      if (!confirmed) return false;
    }

    resetForm();
    setShowModal(false);
    setShowFullscreenEditor(false);
    setRegularQuillReady(false);
    setFullscreenQuillReady(false);

    // Clean up Quill instances
    if (window.quill) {
      window.quill = null;
    }
    if (window.fullscreenQuill) {
      window.fullscreenQuill = null;
    }

    // Clear manpower request source if modal closed without creating job
    setSourceManpowerRequestId(null);
    setEditingMode(false);
    setCurrentJobKey('');
    jobModalInitialSnapshotRef.current = null;
    return true;
  };

  const handleModalNext = () => {
    setModalPage((prev) => Math.min(prev + 1, usesHostedApplications ? 3 : 2));
  };

  const handleModalPrev = () => {
    setModalPage((prev) => Math.max(prev - 1, 1));
  };

  const updateAppLinkInput = (type) => {
    if (type === currentAppType) return;
    setCurrentAppType(type);
    setFormData((previous) => ({ ...previous, appWindow: '' }));
  };

  const openCreateJobModal = () => {
    if (!canCreateOrEditJobs) {
      showHomeNotice('You do not have permission to create job posts.');
      return;
    }

    const nextFormData = getDefaultJobFormData(currentUserRecruiterOption.id || '');
    const nextApplicationSettings = buildPublicApplicationSettingsForJob('');
    setEditingMode(false);
    setCurrentJobKey('');
    setFormData(nextFormData);
    manualJobFields.current.clear();
    setJobTagGroups([]);
    setJobUngroupedTags([]);
    setTagGroupInput('');
    setTagInput('');
    setSelectedTagGroupKey(UNGROUPED_JOB_TAG_KEY);
    setJobDescription('');
    setCurrentAppType('link');
    setSourceManpowerRequestId(null);
    setJobApplicationDraftSettings(nextApplicationSettings);
    resetQuickApplicationFieldComposer();
    window.tempQuillContent = '';
    setModalPage(1);
    setJobSubmitAttempted(false);
    markJobModalSnapshot({
      editingMode: false,
      currentJobKey: '',
      formData: nextFormData,
      currentAppType: 'link',
      jobDescription: '',
      jobDescriptionHTML: '',
      jobTagGroups: [],
      jobUngroupedTags: [],
      tagGroupInput: '',
      tagInput: '',
      selectedTagGroupKey: UNGROUPED_JOB_TAG_KEY,
      quickApplicationFieldLabel: '',
      quickApplicationFieldType: 'text',
      quickApplicationFieldRequired: false,
      jobApplicationDraftSettings: nextApplicationSettings,
      sourceManpowerRequestId: null
    });
    setShowModal(true);
  };

  const isApplicationTargetRequired = () => !usesHostedApplications;
  const getFormattedApplicationTarget = () => currentAppType === 'whatsapp'
    ? buildWhatsAppApplicationTarget(formData.appWindow)
    : normalizeJobApplicationTarget(formData.appWindow);

  const getMissingJobFields = () => {
    const missingFields = new Set();
    const requiredTextFields = [
      ['jobOpening', formData.jobOpening],
      ['salary', formData.salary],
      ['ownerRecruiterId', formData.ownerRecruiterId],
      ['negotiability', formData.negotiability],
      ['jobCategory', formData.jobCategory],
      ['mode1', formData.mode1],
      ['mode2', formData.mode2]
    ];

    requiredTextFields.forEach(([fieldName, value]) => {
      if (!normalizeTagLabel(value)) {
        missingFields.add(fieldName);
      }
    });

    if (!jobDescription.trim()) {
      missingFields.add('jobDescription');
    }

    if (isApplicationTargetRequired() && !getFormattedApplicationTarget()) {
      missingFields.add('appWindow');
    }

    return missingFields;
  };

  const getFirstMissingJobModalPage = (missingFields) => {
    const jobDetailsFields = ['jobOpening', 'salary', 'ownerRecruiterId', 'negotiability', 'jobCategory', 'mode1', 'mode2', 'appWindow'];

    if (jobDetailsFields.some((fieldName) => missingFields.has(fieldName))) {
      return 1;
    }

    if (missingFields.has('jobDescription')) {
      return 2;
    }

    return modalPage;
  };

  const isJobFieldMissing = (fieldName) => jobSubmitAttempted && getMissingJobFields().has(fieldName);

  const isJobModalStepMissing = (page) => {
    if (!jobSubmitAttempted) return false;

    const missingFields = getMissingJobFields();

    if (page === 1) {
      return ['jobOpening', 'salary', 'ownerRecruiterId', 'negotiability', 'jobCategory', 'mode1', 'mode2', 'appWindow']
        .some((fieldName) => missingFields.has(fieldName));
    }

    if (page === 2) {
      return missingFields.has('jobDescription');
    }

    return false;
  };

  const renderRequiredLabel = (label) => (
    <>
      {uiText(label)}
      <span className="job-modal-required-star" aria-hidden="true">*</span>
    </>
  );

  const areFieldsFilled = () => {
    if (agencyJobPostingEnabled && formData.linkedinCompanyId?.trim() && !parseLinkedInCompanyId(formData.linkedinCompanyId)) {
      setModalPage(1);
      showHomeNotice(t('formLabels.invalidLinkedInCompanyId', 'Enter a numeric LinkedIn company ID or a numeric company admin URL.'));
      return false;
    }
    const missingFields = getMissingJobFields();

    if (missingFields.size > 0) {
      setModalPage(getFirstMissingJobModalPage(missingFields));
      showHomeNotice(
        missingFields.size === 1 && missingFields.has('appWindow')
          ? currentAppType === 'whatsapp'
            ? t('formLabels.whatsAppNumberRequired', 'Enter a WhatsApp number with country code, for example +961 70 123 456.')
            : t('formLabels.applicationTargetRequired', 'Enter a valid application link or email address to receive applicants.')
          : t('formLabels.requiredFieldsMissing', 'Fill in the required fields marked in red.')
      );
      return false;
    }

    return true;
  };

  const handleJobSubmit = () => {
    if (jobPostingBusy.current) return;
    const existingJob = editingMode
      ? (jobPosts.find(job => job.id === currentJobKey) || selectedJob || {})
      : null;

    if (editingMode ? !canEditJobRecord(existingJob) : !canCreateOrEditJobs) {
      showHomeNotice('You do not have permission to create or edit job posts.');
      return;
    }

    if (accessLoading) return;

    setJobSubmitAttempted(true);

    if (!areFieldsFilled()) {
      return;
    }

    if (editingMode) {
      // Update existing job
      updateJob();
	    } else {
	      // Create new job
	      postJob();
	    }
  };

  const postJob = async () => {
    if (jobPostingBusy.current) return;
    if (!canCreateOrEditJobs) {
      showHomeNotice('You do not have permission to create job posts.');
      return;
    }

    jobPostingBusy.current = true;
    setJobPostingPhase('saving');
    let saved = false;
    try {
      const formattedAppTarget = getFormattedApplicationTarget();
      const nowIso = new Date().toISOString();

      // Use job-specific location if provided, otherwise fall back to company headquarters
      const jobCity = formData.jobCity || companyData.city || '';
      const jobCountry = formData.jobCountry || companyData.country || '';
      const locationDisplay = jobCity && jobCountry ? `${jobCity}, ${jobCountry}` : (jobCity || jobCountry || 'Location');
      const tagTaxonomy = buildJobTagTaxonomy({
        tagGroups: jobTagGroups,
        ungroupedTags: jobUngroupedTags
      });
      const newJobRef = push(ref(database, `companies/${companyUID}/posts`));
      const jobKey = newJobRef.key;
      const ownerRecruiter = getRecruiterOptionById(formData.ownerRecruiterId) || currentUserRecruiterOption;
      const ownerRecruiterName = ownerRecruiter?.name || currentUser?.displayName || currentUser?.email || '';
      const ownerRecruiterEmail = ownerRecruiter?.email || currentUser?.email || '';
      const hiringPlan = buildHiringPlan(formData.targetHireDate);
      const publicApplicationSettings = usesHostedApplications ? getJobApplicationSettingsForSubmit() : null;

      const jobData = {
        companyName: (agencyJobPostingEnabled && formData.postingCompanyName.trim()) || companyData.companyName || 'Company',
        linkedinCompanyId: agencyJobPostingEnabled ? (parseLinkedInCompanyId(formData.linkedinCompanyId) || null) : null,
        companyLocation: locationDisplay,
        location: locationDisplay,
        jobCity: formData.jobCity || '',
        jobCountry: formData.jobCountry || '',
        jobOpening: formData.jobOpening,
        salary: formData.salary,
        negotiability: formData.negotiability,
        jobFunction: formData.jobCategory,
        workspace: formData.mode2,
        schedule: formData.mode1,
        jobDescription: jobDescription,
        jobDescriptionHTML: getCurrentJobDescriptionHtml(),
        appLink: null,
        [APP_LINK_ARCHIVE_FIELD]: null,
        applicationMethod: usesHostedApplications ? 'ats' : 'external',
        applicationDestinationType: usesHostedApplications ? null : currentAppType,
        postedTime: Date.now(),
        createdAt: nowIso,
        updatedAt: nowIso,
        filterEnabled: usesHostedApplications && (isFilterSubstageEnabled || formData.enableFiltering),
        department: formData.department || '',
        seniority: formData.seniority || '',
        experienceYears: formData.experienceYears || '',
        targetHireDate: hiringPlan.targetHireDate,
        hiringPlan,
        publicJobApplicationSettings: publicApplicationSettings,
        applicationFormConfig: publicApplicationSettings,
        publicJobApplication: publicApplicationSettings,
        ...buildRoleAnalyticsFields({
          roleId: jobKey,
          jobTitle: formData.jobOpening,
          department: formData.department || '',
          location: locationDisplay,
          ownerRecruiterId: ownerRecruiter?.id || currentUser?.uid || '',
          ownerRecruiterName,
          ownerRecruiterEmail,
          tags: [
            ...(tagTaxonomy.tags || []),
            ...(tagTaxonomy.tagGroupLabels || [])
          ],
          sourceManpowerRequestId: sourceManpowerRequestId || ''
        }),
        ...tagTaxonomy
      };

      if (usesHostedApplications) {
        jobData[APP_LINK_ARCHIVE_FIELD] = formattedAppTarget || null;
      } else {
        jobData.appLink = formattedAppTarget || null;
      }

      const updates = {};

      // APPROVAL WORKFLOW: Check if approvals are required
      if (requiresJobApproval) {
        const approvalId = `apr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const submittedAt = Date.now();
        // Save as draft pending approval - NOT published
        updates[`companies/${companyUID}/posts/${jobKey}`] = {
          ...jobData,
          published: false,
          approvalId,
          approvalStatus: 'pending_approval',
          approvalSubmittedAt: submittedAt,
          approvalSubmittedByUid: currentUser.uid,
          lifecycleStatus: 'pending_approval',
          employerId: companyUID
        };
        // Do NOT write to job_posts (public)

        // Create approval request
        const approvalData = {
          id: approvalId,
          type: 'job_posting',
          status: 'pending',
          submittedBy: {
            uid: currentUser.uid,
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email
          },
          submittedAt,
          entityId: jobKey,
          entityName: jobData.jobOpening || jobData.title || 'New Job Post',
          snapshot: {
            title: jobData.jobOpening || jobData.title || 'Untitled',
            department: jobData.department || '',
            seniority: jobData.seniority || ''
          },
          metadata: {
            jobTitle: jobData.jobOpening || jobData.title || '',
            department: jobData.department || '',
            location: jobData.companyLocation || '',
            schedule: jobData.schedule || '',
            ownerRecruiterId: jobData.ownerRecruiterId || '',
            ownerRecruiterName: jobData.ownerRecruiterName || '',
            ownerRecruiterEmail: jobData.ownerRecruiterEmail || '',
            tagGroups: jobData.tagGroupLabels || [],
            tags: jobData.tags || []
          }
        };
        updates[`companies/${companyUID}/pending_approvals/${approvalId}`] = approvalData;

        await update(ref(database), updates);
        saved = true;
        await emitJobLifecycleAnalyticsEvent({
          companyUID,
          eventType: 'job_created',
          jobId: jobKey,
          jobData: { ...jobData, lifecycleStatus: 'pending_approval' },
          lifecycleStatus: 'pending_approval',
          occurredAt: nowIso,
          context: {
            originAction: 'job_create',
            approvalStatus: 'pending_approval'
          }
        });
        closeModal({ skipConfirmation: true });
        showHomeNotice('Job post submitted for approval. An approver will review it shortly.');
      } else {
        // Normal flow - publish immediately
        const jobPostData = {
          ...jobData,
          published: true,
          lifecycleStatus: 'published',
          firstPublishedAt: nowIso,
          employerId: companyUID
        };

        // If this job was created from a manpower request, link it for hire tracking
        if (sourceManpowerRequestId) {
          jobPostData.sourceManpowerRequestId = sourceManpowerRequestId;
        }

        updates[`companies/${companyUID}/posts/${jobKey}`] = jobPostData;

        // If this job was created from a manpower request, mark it as fulfilled
        if (sourceManpowerRequestId) {
          updates[`companies/${companyUID}/manpower_requests/${sourceManpowerRequestId}/status`] = 'fulfilled';
          updates[`companies/${companyUID}/manpower_requests/${sourceManpowerRequestId}/linkedJobPostId`] = jobKey;
          updates[`companies/${companyUID}/manpower_requests/${sourceManpowerRequestId}/fulfilledAt`] = Date.now();
        }

        await update(ref(database), updates);
        saved = true;
        setJobPostingPhase('publishing');
        await waitForJobPublication(database, companyUID, jobKey);
        await Promise.allSettled([
          emitJobLifecycleAnalyticsEvent({
            companyUID,
            eventType: 'job_created',
            jobId: jobKey,
            jobData: jobPostData,
            lifecycleStatus: 'published',
            occurredAt: nowIso,
            context: { originAction: 'job_create' }
          }),
          emitJobLifecycleAnalyticsEvent({
            companyUID,
            eventType: 'job_published',
            jobId: jobKey,
            jobData: jobPostData,
            lifecycleStatus: 'published',
            occurredAt: nowIso,
            context: { originAction: 'job_create_publish' }
          })
        ]);

        // Clear the source request ID after successful creation
        if (sourceManpowerRequestId) {
          setSourceManpowerRequestId(null);
        }

        closeModal({ skipConfirmation: true });
        showHomeNotice('Job posted successfully!', { tone: 'success', title: t('feedback.publishedTitle', 'Your job is live') });
      }
    } catch (error) {
      console.error('Error posting job:', error);
      if (saved) {
        closeModal({ skipConfirmation: true });
        showHomeNotice(t('formLabels.publicationPending', 'Your job is saved, but publication is not confirmed yet. Please do not post it again. Check the job shortly.'));
        return;
      }
      showHomeNotice(error?.code === 'PERMISSION_DENIED'
        ? 'Your account could not save this job. Please check your company access and try again.'
        : 'Failed to post job. Please try again.');
    } finally {
      jobPostingBusy.current = false;
      setJobPostingPhase(null);
    }
  };

  const buildRecruiterAssignmentPatch = (recruiter = {}) => ({
    ownerRecruiterId: recruiter.id || '',
    ownerRecruiterName: recruiter.name || '',
    ownerRecruiterEmail: recruiter.email || ''
  });

  const collectRecruiterAssignmentPropagationUpdates = async ({
    jobId,
    previousRecruiterId,
    nextRecruiter
  }) => {
    if (!companyUID || !jobId || !nextRecruiter?.id) return {};

    const nextPatch = buildRecruiterAssignmentPatch(nextRecruiter);
    const updates = {};
    const shouldMoveOwner = (ownerId = '') => {
      const normalizedOwnerId = String(ownerId || '').trim();
      const normalizedPreviousId = String(previousRecruiterId || '').trim();
      return !normalizedOwnerId || normalizedOwnerId === normalizedPreviousId;
    };

    const applicantIndexSnap = await get(ref(database, `companies/${companyUID}/applicant_index/by_job/${jobId}`));
    if (applicantIndexSnap.exists()) {
      const applicantIndex = applicantIndexSnap.val() || {};
      const connectionReads = Object.entries(applicantIndex).map(async ([connectionKey, row]) => {
        if (shouldMoveOwner(row?.ownerRecruiterId)) {
          updates[`companies/${companyUID}/applicant_index/by_job/${jobId}/${connectionKey}/ownerRecruiterId`] = nextPatch.ownerRecruiterId;
          updates[`companies/${companyUID}/applicant_index/by_job/${jobId}/${connectionKey}/ownerRecruiterName`] = nextPatch.ownerRecruiterName;
          updates[`companies/${companyUID}/applicant_index/by_job/${jobId}/${connectionKey}/ownerRecruiterEmail`] = nextPatch.ownerRecruiterEmail;
        }

        const connectionSnap = await get(ref(database, `connections/${connectionKey}`));
        if (connectionSnap.exists()) {
          const connection = connectionSnap.val() || {};
          if (shouldMoveOwner(connection.ownerRecruiterId)) {
            updates[`connections/${connectionKey}/ownerRecruiterId`] = nextPatch.ownerRecruiterId;
            updates[`connections/${connectionKey}/ownerRecruiterName`] = nextPatch.ownerRecruiterName;
            updates[`connections/${connectionKey}/ownerRecruiterEmail`] = nextPatch.ownerRecruiterEmail;
          }
        }
      });
      await Promise.all(connectionReads);
    }

    const pendingApprovalsSnap = await get(ref(database, `companies/${companyUID}/pending_approvals`));
    if (pendingApprovalsSnap.exists()) {
      Object.entries(pendingApprovalsSnap.val() || {}).forEach(([approvalId, approval]) => {
        if (approval?.type !== 'job_posting' || approval?.entityId !== jobId) return;
        updates[`companies/${companyUID}/pending_approvals/${approvalId}/metadata/ownerRecruiterId`] = nextPatch.ownerRecruiterId;
        updates[`companies/${companyUID}/pending_approvals/${approvalId}/metadata/ownerRecruiterName`] = nextPatch.ownerRecruiterName;
        updates[`companies/${companyUID}/pending_approvals/${approvalId}/metadata/ownerRecruiterEmail`] = nextPatch.ownerRecruiterEmail;
        if (approval.snapshot && typeof approval.snapshot === 'object') {
          updates[`companies/${companyUID}/pending_approvals/${approvalId}/snapshot/ownerRecruiterId`] = nextPatch.ownerRecruiterId;
          updates[`companies/${companyUID}/pending_approvals/${approvalId}/snapshot/ownerRecruiterName`] = nextPatch.ownerRecruiterName;
          updates[`companies/${companyUID}/pending_approvals/${approvalId}/snapshot/ownerRecruiterEmail`] = nextPatch.ownerRecruiterEmail;
        }
      });
    }

    return updates;
  };

  const submitPublishedJobUpdateForApproval = async ({
    jobId,
    existingJob,
    proposedJobData,
    previousRecruiterId,
    updatedAtIso
  }) => {
    const submittedAt = Date.now();
    const approvalId = `apr_${submittedAt}_${Math.random().toString(36).substr(2, 9)}`;
    const previousApprovalId = existingJob?.pendingUpdateApprovalId || '';
    const firstPublishedAt = existingJob?.firstPublishedAt || updatedAtIso;
    const proposedPublishedJob = {
      ...proposedJobData,
      published: true,
      approvalStatus: null,
      lifecycleStatus: 'published',
      firstPublishedAt,
      closedAt: null,
      employerId: companyUID
    };

    const approvalData = {
      id: approvalId,
      type: 'job_posting',
      status: 'pending',
      submittedBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || currentUser.email,
        email: currentUser.email
      },
      submittedAt,
      entityId: jobId,
      entityName: proposedJobData.jobOpening || proposedJobData.title || existingJob?.jobOpening || 'Job Post Update',
      snapshot: {
        title: proposedJobData.jobOpening || proposedJobData.title || existingJob?.jobOpening || 'Untitled',
        currentTitle: existingJob?.jobOpening || existingJob?.title || '',
        department: proposedJobData.department || '',
        action: 'job_update'
      },
      metadata: {
        action: 'job_update',
        jobId,
        jobTitle: proposedJobData.jobOpening || proposedJobData.title || '',
        department: proposedJobData.department || '',
        location: proposedJobData.companyLocation || proposedJobData.location || '',
        schedule: proposedJobData.schedule || '',
        previousOwnerRecruiterId: previousRecruiterId || '',
        ownerRecruiterId: proposedJobData.ownerRecruiterId || '',
        ownerRecruiterName: proposedJobData.ownerRecruiterName || '',
        ownerRecruiterEmail: proposedJobData.ownerRecruiterEmail || '',
        proposedJobData: proposedPublishedJob
      }
    };

    const updates = {
      [`companies/${companyUID}/pending_approvals/${approvalId}`]: approvalData,
      [`companies/${companyUID}/posts/${jobId}/pendingUpdateApprovalId`]: approvalId,
      [`companies/${companyUID}/posts/${jobId}/pendingUpdateSubmittedAt`]: submittedAt,
      [`companies/${companyUID}/posts/${jobId}/pendingUpdateStatus`]: 'pending_approval',
      [`companies/${companyUID}/posts/${jobId}/pendingUpdatePreview`]: {
        jobTitle: approvalData.metadata.jobTitle,
        department: approvalData.metadata.department,
        location: approvalData.metadata.location
      }
    };

    if (previousApprovalId && previousApprovalId !== approvalId && existingJob?.pendingUpdateStatus === 'pending_approval') {
      updates[`companies/${companyUID}/pending_approvals/${previousApprovalId}/status`] = 'cancelled';
      updates[`companies/${companyUID}/pending_approvals/${previousApprovalId}/reviewedAt`] = submittedAt;
      updates[`companies/${companyUID}/pending_approvals/${previousApprovalId}/metadata/cancelReason`] = 'superseded_by_new_job_update';
    }

    await update(ref(database), updates);

    closeModal({ skipConfirmation: true });
    setJobPosts((prev) => prev.map((job) => (
      job.id === jobId
        ? {
          ...job,
          pendingUpdateApprovalId: approvalId,
          pendingUpdateSubmittedAt: submittedAt,
          pendingUpdateStatus: 'pending_approval',
          pendingUpdatePreview: updates[`companies/${companyUID}/posts/${jobId}/pendingUpdatePreview`]
        }
        : job
    )));
    setSelectedJob((prev) => prev && prev.id === jobId ? {
      ...prev,
      pendingUpdateApprovalId: approvalId,
      pendingUpdateSubmittedAt: submittedAt,
      pendingUpdateStatus: 'pending_approval',
      pendingUpdatePreview: updates[`companies/${companyUID}/posts/${jobId}/pendingUpdatePreview`]
    } : prev);

    showHomeNotice('Job update submitted for approval. The live job post remains unchanged until an approver accepts the update.');
  };

  const resolvePendingJobApproval = async (job, { action = 'job_posting', approvalId = '' } = {}) => {
    if (!job?.id || !companyUID) return null;
    const expectedAction = action === 'job_update' ? 'job_update' : '';
    const matchesApproval = (approval) => {
      if (!approval || approval.type !== 'job_posting' || approval.entityId !== job.id || approval.status !== 'pending') {
        return false;
      }

      const approvalAction = approval.metadata?.action || '';
      return expectedAction ? approvalAction === expectedAction : approvalAction !== 'job_update';
    };

    const directApprovalId = approvalId || (expectedAction === 'job_update' ? job.pendingUpdateApprovalId : job.approvalId);
    if (directApprovalId) {
      const approvalSnap = await get(ref(database, `companies/${companyUID}/pending_approvals/${directApprovalId}`));
      if (approvalSnap.exists()) {
        const approval = approvalSnap.val() || {};
        const hydratedApproval = { ...approval, id: approval.id || directApprovalId };
        if (matchesApproval(hydratedApproval)) {
          return hydratedApproval;
        }
      }
    }

    const cachedApproval = pendingJobApprovalsByJobId[job.id];
    if (matchesApproval(cachedApproval)) {
      return cachedApproval;
    }

    const approvalsSnap = await get(ref(database, `companies/${companyUID}/pending_approvals`));
    if (!approvalsSnap.exists()) return null;

    const approvals = approvalsSnap.val() || {};
    return Object.entries(approvals)
      .map(([approvalId, approval]) => ({ ...approval, id: approval?.id || approvalId }))
      .filter(matchesApproval)
      .sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0))[0] || null;
  };

  const resolvePendingJobUpdateApproval = async (job) => (
    resolvePendingJobApproval(job, { action: 'job_update', approvalId: job?.pendingUpdateApprovalId || '' })
  );

  const notifyJobApprovalRequester = ({
    updates,
    approval,
    status,
    now,
    approvedTitle = 'Job approval approved',
    rejectedTitle = 'Job approval rejected',
    entityFallback = 'Job Post'
  }) => {
    const requesterUid = getApprovalSubmitter(approval).uid;
    if (!requesterUid) return;

    const approverName = currentUser?.displayName || currentUser?.email || 'Approver';
    const entityLabel = approval.entityName || approval.snapshot?.title || approval.metadata?.jobTitle || entityFallback;
    const notifRef = push(ref(database, `companies/${companyUID}/user_notifications/${requesterUid}`));

    updates[`companies/${companyUID}/user_notifications/${requesterUid}/${notifRef.key}`] = {
      id: notifRef.key,
      type: 'approval_decision',
      ...systemMessage('approval_decision', { entity: entityLabel, name: approverName, status, title: status === 'approved' ? approvedTitle : rejectedTitle, approvalType: 'job_posting' }),
      approvalType: 'job_posting',
      status,
      title: status === 'approved' ? approvedTitle : rejectedTitle,
      message: `${entityLabel} was ${status} by ${approverName}.`,
      text: `${entityLabel} was ${status} by ${approverName}.`,
      timestamp: now,
      approvalId: approval.id,
      entityId: approval.entityId || '',
      entityName: entityLabel,
      approver: {
        uid: currentUser?.uid,
        name: approverName,
        email: currentUser?.email
      }
    };
  };

  const notifyJobUpdateRequester = ({ updates, approval, status, now }) => {
    notifyJobApprovalRequester({
      updates,
      approval,
      status,
      now,
      approvedTitle: 'Job update approved',
      rejectedTitle: 'Job update rejected',
      entityFallback: 'Job Post Update'
    });
  };

  const approvePublishedJobUpdate = async (job) => {
    try {
      const approval = await resolvePendingJobUpdateApproval(job);
      const proposedJobData = approval?.metadata?.proposedJobData || approval?.proposedJobData || null;
      if (!approval || !proposedJobData) {
        showHomeNotice('Could not find the pending job update approval.');
        return;
      }
      if (!canReviewJobApproval(approval)) {
        showHomeNotice('You do not have permission to approve this job update.');
        return;
      }

      const now = Date.now();
      const approvedAtIso = new Date(now).toISOString();
      const jobSnap = await get(ref(database, `companies/${companyUID}/posts/${job.id}`));
      const existingJobData = jobSnap.exists() ? { ...jobSnap.val() } : { ...job };
      const firstPublishedAt = existingJobData.firstPublishedAt || proposedJobData.firstPublishedAt || approvedAtIso;
      const approvedJobData = {
        ...existingJobData,
        ...proposedJobData,
        published: true,
        approvalStatus: null,
        pendingUpdateApprovalId: null,
        pendingUpdateSubmittedAt: null,
        pendingUpdateStatus: null,
        pendingUpdateRejectedAt: null,
        pendingUpdatePreview: null,
        lifecycleStatus: 'published',
        firstPublishedAt,
        closedAt: null,
        employerId: companyUID,
        updatedAt: proposedJobData.updatedAt || approvedAtIso
      };

      const updates = {
        [`companies/${companyUID}/pending_approvals/${approval.id}/status`]: 'approved',
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedBy`]: {
          uid: currentUser?.uid,
          name: currentUser?.displayName || currentUser?.email,
          email: currentUser?.email
        },
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedAt`]: now,
        [`companies/${companyUID}/posts/${job.id}`]: approvedJobData
      };

      const previousRecruiterId = approval.metadata?.previousOwnerRecruiterId || existingJobData.ownerRecruiterId || '';
      if (String(previousRecruiterId || '') !== String(approvedJobData.ownerRecruiterId || '')) {
        Object.assign(updates, await collectRecruiterAssignmentPropagationUpdates({
          jobId: job.id,
          previousRecruiterId,
          nextRecruiter: {
            id: approvedJobData.ownerRecruiterId || '',
            name: approvedJobData.ownerRecruiterName || '',
            email: approvedJobData.ownerRecruiterEmail || ''
          }
        }));
      }

      notifyJobUpdateRequester({ updates, approval, status: 'approved', now });
      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: job.id,
        jobData: approvedJobData,
        lifecycleStatus: 'published',
        occurredAt: approvedAtIso,
        context: {
          originAction: 'approve_job_update',
          approvalId: approval.id
        }
      });

      setJobPosts((prev) => prev.map((row) => (row.id === job.id ? { ...approvedJobData, id: job.id } : row)));
      setSelectedJob({ ...approvedJobData, id: job.id });
      showHomeNotice('Job update approved and published.');
    } catch (error) {
      console.error('approvePublishedJobUpdate error:', error);
      showHomeNotice('Failed to approve job update. Please try again.');
    }
  };

  const rejectPublishedJobUpdate = async (job) => {
    const rejectionReason = await promptHomeAction('Reason for rejecting this job update?');
    if (rejectionReason === null) return;

    try {
      const approval = await resolvePendingJobUpdateApproval(job);
      if (!approval) {
        showHomeNotice('Could not find the pending job update approval.');
        return;
      }
      if (!canReviewJobApproval(approval)) {
        showHomeNotice('You do not have permission to reject this job update.');
        return;
      }

      const now = Date.now();
      const rejectedAtIso = new Date(now).toISOString();
      const updates = {
        [`companies/${companyUID}/pending_approvals/${approval.id}/status`]: 'rejected',
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedBy`]: {
          uid: currentUser?.uid,
          name: currentUser?.displayName || currentUser?.email,
          email: currentUser?.email
        },
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedAt`]: now,
        [`companies/${companyUID}/posts/${job.id}/pendingUpdateApprovalId`]: null,
        [`companies/${companyUID}/posts/${job.id}/pendingUpdateStatus`]: 'rejected',
        [`companies/${companyUID}/posts/${job.id}/pendingUpdateRejectedAt`]: now,
        [`companies/${companyUID}/posts/${job.id}/pendingUpdatePreview`]: null,
        [`companies/${companyUID}/posts/${job.id}/updatedAt`]: rejectedAtIso
      };

      const trimmedReason = rejectionReason.trim();
      if (trimmedReason) {
        updates[`companies/${companyUID}/pending_approvals/${approval.id}/rejectionReason`] = trimmedReason;
      }

      notifyJobUpdateRequester({ updates, approval, status: 'rejected', now });
      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: job.id,
        jobData: {
          ...job,
          pendingUpdateApprovalId: null,
          pendingUpdateStatus: 'rejected',
          pendingUpdatePreview: null
        },
        lifecycleStatus: 'published',
        occurredAt: rejectedAtIso,
        context: {
          originAction: 'reject_job_update',
          approvalId: approval.id
        }
      });

      const nextJobState = {
        ...job,
        pendingUpdateApprovalId: null,
        pendingUpdateStatus: 'rejected',
        pendingUpdateRejectedAt: now,
        pendingUpdatePreview: null,
        updatedAt: rejectedAtIso
      };
      setJobPosts((prev) => prev.map((row) => (row.id === job.id ? nextJobState : row)));
      setSelectedJob(nextJobState);
      showHomeNotice('Job update rejected. The live job post was not changed.');
    } catch (error) {
      console.error('rejectPublishedJobUpdate error:', error);
      showHomeNotice('Failed to reject job update. Please try again.');
    }
  };

  const updateJob = async () => {
    try {
      const existingJob = jobPosts.find(job => job.id === currentJobKey) || selectedJob || {};
      if (!canEditJobRecord(existingJob)) {
        showHomeNotice('You do not have permission to edit job posts.');
        return;
      }

      const formattedAppTarget = getFormattedApplicationTarget();
      let pendingApproval = null;
      if (existingJob?.approvalStatus === 'pending_approval') {
        pendingApproval = await resolvePendingJobApproval(existingJob, { action: 'job_posting' });
        if (!pendingApproval || !canEditPendingJobApproval(pendingApproval)) {
          showHomeNotice('Only the requester, workspace owner, or an eligible job approver can edit a pending approval request.');
          return;
        }
      }
      const existingArchived = existingJob ? existingJob[APP_LINK_ARCHIVE_FIELD] : null;
      const existingAppLink = existingJob ? existingJob.appLink : null;
      const updatedAtIso = new Date().toISOString();

      // Use job-specific location if provided, otherwise fall back to company headquarters
      const jobCity = formData.jobCity || companyData.city || '';
      const jobCountry = formData.jobCountry || companyData.country || '';
      const locationDisplay = jobCity && jobCountry ? `${jobCity}, ${jobCountry}` : (jobCity || jobCountry || 'Location');
      const tagTaxonomy = buildJobTagTaxonomy({
        tagGroups: jobTagGroups,
        ungroupedTags: jobUngroupedTags
      });
      const previousRecruiterId = existingJob?.ownerRecruiterId || '';
      const ownerRecruiter = getRecruiterOptionById(formData.ownerRecruiterId || previousRecruiterId) || {
        id: formData.ownerRecruiterId || previousRecruiterId || currentUser?.uid || '',
        name: existingJob?.ownerRecruiterName || currentUser?.displayName || currentUser?.email || '',
        email: existingJob?.ownerRecruiterEmail || currentUser?.email || ''
      };
      const ownerRecruiterName = ownerRecruiter?.name || existingJob?.ownerRecruiterName || currentUser?.displayName || currentUser?.email || '';
      const ownerRecruiterEmail = ownerRecruiter?.email || existingJob?.ownerRecruiterEmail || currentUser?.email || '';
      const hiringPlan = buildHiringPlan(formData.targetHireDate);
      const publicApplicationSettings = usesHostedApplications ? getJobApplicationSettingsForSubmit() : null;

      const jobData = {
        linkedinCompanyId: agencyJobPostingEnabled ? (parseLinkedInCompanyId(formData.linkedinCompanyId) || null) : (existingJob.linkedinCompanyId || null),
        companyName: agencyJobPostingEnabled
          ? (formData.postingCompanyName.trim() || companyData.companyName || 'Company')
          : (existingJob.companyName || companyData.companyName || 'Company'),
        companyLocation: locationDisplay,
        location: locationDisplay,
        jobCity: formData.jobCity || '',
        jobCountry: formData.jobCountry || '',
        jobOpening: formData.jobOpening,
        salary: formData.salary,
        negotiability: formData.negotiability,
        jobFunction: formData.jobCategory,
        workspace: formData.mode2,
        schedule: formData.mode1,
        jobDescription: jobDescription,
        jobDescriptionHTML: getCurrentJobDescriptionHtml(),
        appLink: null,
        [APP_LINK_ARCHIVE_FIELD]: null,
        applicationMethod: usesHostedApplications ? 'ats' : 'external',
        applicationDestinationType: usesHostedApplications ? null : currentAppType,
        createdAt: existingJob?.createdAt || existingJob?.postedTime || updatedAtIso,
        updatedAt: updatedAtIso,
        filterEnabled: usesHostedApplications && (isFilterSubstageEnabled || formData.enableFiltering),
        department: formData.department || '',
        seniority: formData.seniority || '',
        experienceYears: formData.experienceYears || '',
        targetHireDate: hiringPlan.targetHireDate,
        hiringPlan,
        publicJobApplicationSettings: publicApplicationSettings,
        applicationFormConfig: publicApplicationSettings,
        publicJobApplication: publicApplicationSettings,
        ...buildRoleAnalyticsFields({
          roleId: currentJobKey,
          jobTitle: formData.jobOpening,
          department: formData.department || '',
          location: locationDisplay,
          ownerRecruiterId: ownerRecruiter?.id || currentUser?.uid || '',
          ownerRecruiterName,
          ownerRecruiterEmail,
          tags: [
            ...(tagTaxonomy.tags || []),
            ...(tagTaxonomy.tagGroupLabels || [])
          ],
          sourceManpowerRequestId: existingJob?.sourceManpowerRequestId || sourceManpowerRequestId || ''
        }),
        ...tagTaxonomy
      };

      if (usesHostedApplications) {
        const archiveValue = formattedAppTarget || existingArchived || existingAppLink || null;
        jobData[APP_LINK_ARCHIVE_FIELD] = archiveValue || null;
      } else {
        jobData.appLink = formattedAppTarget;
      }

      const isPublished = !!(selectedJob?.published ?? existingJob?.published);
      const currentApprovalStatus = existingJob?.approvalStatus || selectedJob?.approvalStatus || null;
      const recruiterChanged = previousRecruiterId !== jobData.ownerRecruiterId;

      if (isPublished && requiresJobApproval) {
        await submitPublishedJobUpdateForApproval({
          jobId: currentJobKey,
          existingJob,
          proposedJobData: jobData,
          previousRecruiterId,
          updatedAtIso
        });
        return;
      }

      const updates = {};

      updates[`companies/${companyUID}/posts/${currentJobKey}`] = {
        ...jobData,
        published: isPublished,
        approvalId: pendingApproval?.id || existingJob?.approvalId || null,
        approvalStatus: currentApprovalStatus,
        approvalSubmittedAt: existingJob?.approvalSubmittedAt || pendingApproval?.submittedAt || null,
        approvalSubmittedByUid: existingJob?.approvalSubmittedByUid || getApprovalSubmitter(pendingApproval || {}).uid || null,
        approvalRejectedAt: existingJob?.approvalRejectedAt || null,
        approvalRejectionReason: existingJob?.approvalRejectionReason || null,
        pendingUpdateApprovalId: existingJob?.pendingUpdateApprovalId || null,
        pendingUpdateSubmittedAt: existingJob?.pendingUpdateSubmittedAt || null,
        pendingUpdateStatus: existingJob?.pendingUpdateStatus || null,
        pendingUpdateRejectedAt: existingJob?.pendingUpdateRejectedAt || null,
        pendingUpdatePreview: existingJob?.pendingUpdatePreview || null,
        lifecycleStatus: isPublished ? 'published' : (currentApprovalStatus === 'pending_approval' ? 'pending_approval' : 'closed'),
        firstPublishedAt: existingJob?.firstPublishedAt || (isPublished ? updatedAtIso : null),
        closedAt: !isPublished && currentApprovalStatus !== 'pending_approval' ? (existingJob?.closedAt || updatedAtIso) : null,
        employerId: companyUID
      };

      if (pendingApproval) {
        Object.assign(updates, buildPendingJobApprovalEditUpdates({
          approval: pendingApproval,
          jobData,
          editedAt: Date.now()
        }));
      }

      if (recruiterChanged) {
        Object.assign(updates, await collectRecruiterAssignmentPropagationUpdates({
          jobId: currentJobKey,
          previousRecruiterId,
          nextRecruiter: ownerRecruiter
        }));
      }

      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: currentJobKey,
        jobData: {
          ...jobData,
          lifecycleStatus: isPublished ? 'published' : (currentApprovalStatus === 'pending_approval' ? 'pending_approval' : 'closed')
        },
        lifecycleStatus: isPublished ? 'published' : (currentApprovalStatus === 'pending_approval' ? 'pending_approval' : 'closed'),
        occurredAt: updatedAtIso,
        context: {
          originAction: 'job_update',
          ...(recruiterChanged ? {
            previousOwnerRecruiterId: previousRecruiterId || '',
            ownerRecruiterId: jobData.ownerRecruiterId || '',
            ownerRecruiterName: jobData.ownerRecruiterName || '',
            ownerRecruiterEmail: jobData.ownerRecruiterEmail || ''
          } : {})
        }
      });

      closeModal({ skipConfirmation: true });
      setJobPosts((prev) => prev.map((job) => (
        job.id === currentJobKey
          ? {
            ...job,
            ...jobData,
            published: isPublished,
            approvalId: pendingApproval?.id || existingJob?.approvalId || null,
            approvalStatus: currentApprovalStatus,
            approvalSubmittedAt: existingJob?.approvalSubmittedAt || pendingApproval?.submittedAt || null,
            approvalSubmittedByUid: existingJob?.approvalSubmittedByUid || getApprovalSubmitter(pendingApproval || {}).uid || null,
            approvalRejectedAt: existingJob?.approvalRejectedAt || null,
            approvalRejectionReason: existingJob?.approvalRejectionReason || null,
            pendingUpdateApprovalId: existingJob?.pendingUpdateApprovalId || null,
            pendingUpdateSubmittedAt: existingJob?.pendingUpdateSubmittedAt || null,
            pendingUpdateStatus: existingJob?.pendingUpdateStatus || null,
            pendingUpdateRejectedAt: existingJob?.pendingUpdateRejectedAt || null,
            pendingUpdatePreview: existingJob?.pendingUpdatePreview || null,
            lifecycleStatus: isPublished ? 'published' : (currentApprovalStatus === 'pending_approval' ? 'pending_approval' : 'closed'),
            updatedAt: updatedAtIso
          }
          : job
      )));
      // Update selected job so details reflect immediately
      setSelectedJob({
        id: currentJobKey,
        ...jobData,
        published: isPublished,
        approvalId: pendingApproval?.id || existingJob?.approvalId || null,
        approvalStatus: currentApprovalStatus,
        approvalSubmittedAt: existingJob?.approvalSubmittedAt || pendingApproval?.submittedAt || null,
        approvalSubmittedByUid: existingJob?.approvalSubmittedByUid || getApprovalSubmitter(pendingApproval || {}).uid || null,
        approvalRejectedAt: existingJob?.approvalRejectedAt || null,
        approvalRejectionReason: existingJob?.approvalRejectionReason || null,
        pendingUpdateApprovalId: existingJob?.pendingUpdateApprovalId || null,
        pendingUpdateSubmittedAt: existingJob?.pendingUpdateSubmittedAt || null,
        pendingUpdateStatus: existingJob?.pendingUpdateStatus || null,
        pendingUpdateRejectedAt: existingJob?.pendingUpdateRejectedAt || null,
        pendingUpdatePreview: existingJob?.pendingUpdatePreview || null
      });
      showHomeNotice('Job updated successfully!', { tone: 'success', title: t('feedback.savedTitle', 'Changes saved') });
    } catch (error) {
      console.error('Error updating job:', error);
      showHomeNotice('Failed to update job. Please try again.');
    }
  };

  const handleEditJob = (job) => {
    if (!canEditJobRecord(job)) {
      showHomeNotice('You do not have permission to edit job posts.');
      return;
    }
    if (job?.approvalStatus === 'pending_approval') {
      const pendingApproval = getKnownPendingJobApproval(job);
      if (!canEditPendingJobApproval(pendingApproval || {})) {
        showHomeNotice('Only the requester, workspace owner, or an eligible job approver can edit a pending approval request.');
        return;
      }
    }

    const resolvedAppTarget = job.appLink || job[APP_LINK_ARCHIVE_FIELD] || '';
    const whatsAppNumber = getWhatsAppNumberFromTarget(resolvedAppTarget);
    const isEmailTarget = typeof resolvedAppTarget === 'string' && /^mailto:/i.test(resolvedAppTarget);
    const nextAppType = whatsAppNumber ? 'whatsapp' : isEmailTarget ? 'email' : 'link';
    const normalizedAppWindow = whatsAppNumber || (isEmailTarget
      ? resolvedAppTarget.replace(/^mailto:/i, '')
      : resolvedAppTarget || '');
    const nextApplicationMethod = hasAtsApplications && job.applicationMethod !== 'external' && !job.appLink
      ? 'ats'
      : 'external';
    const nextFormData = {
      jobOpening: job.jobOpening,
      salary: job.salary,
      negotiability: job.negotiability,
      jobCategory: job.jobFunction,
      mode1: job.schedule,
      mode2: job.workspace,
      appWindow: normalizedAppWindow,
      applicationMethod: nextApplicationMethod,
      enableFiltering: nextApplicationMethod === 'ats' && Boolean(job.filterEnabled),
      department: job.department || '',
      seniority: job.seniority || '',
      experienceYears: job.experienceYears || '',
      jobCity: job.jobCity || '',
      jobCountry: job.jobCountry || '',
      postingCompanyName: job.companyName && job.companyName !== companyData.companyName ? job.companyName : '',
      linkedinCompanyId: job.linkedinCompanyId || '',
      targetHireDate: normalizeDateInputValue(job.hiringPlan?.targetHireDate || job.targetHireDate || ''),
      ownerRecruiterId: job.ownerRecruiterId || currentUserRecruiterOption.id || ''
    };
    const nextApplicationSettings = buildPublicJobSettingsPayload(
      job.publicJobApplicationSettings
      || job.applicationFormConfig
      || job.publicJobApplication
      || buildPublicApplicationSettingsForJob(job.department || '')
    );
    const nextDescription = job.jobDescription || '';
    const hydratedTaxonomy = hydrateJobTagTaxonomy(job);
    const nextDescriptionHtml = job.jobDescriptionHTML
      || (job.jobDescription ? `<p>${job.jobDescription}</p>` : '');

    setEditingMode(true);
    setCurrentJobKey(job.id);
    setJobSubmitAttempted(false);
    setSourceManpowerRequestId(null);
    setFormData(nextFormData);
    manualJobFields.current.clear();
    setJobApplicationDraftSettings(nextApplicationSettings);
    resetQuickApplicationFieldComposer();

    setCurrentAppType(nextAppType);
    setJobDescription(nextDescription);
    setJobTagGroups(hydratedTaxonomy.tagGroups);
    setJobUngroupedTags(hydratedTaxonomy.ungroupedTags);
    setTagGroupInput('');
    setTagInput('');
    setSelectedTagGroupKey(UNGROUPED_JOB_TAG_KEY);

    // Store the job description content for when Quill initializes
    window.tempQuillContent = nextDescriptionHtml;
    markJobModalSnapshot({
      editingMode: true,
      currentJobKey: job.id,
      formData: nextFormData,
      currentAppType: nextAppType,
      jobDescription: nextDescription,
      jobDescriptionHTML: nextDescriptionHtml,
      jobTagGroups: hydratedTaxonomy.tagGroups,
      jobUngroupedTags: hydratedTaxonomy.ungroupedTags,
      tagGroupInput: '',
      tagInput: '',
      selectedTagGroupKey: UNGROUPED_JOB_TAG_KEY,
      quickApplicationFieldLabel: '',
      quickApplicationFieldType: 'text',
      quickApplicationFieldRequired: false,
      jobApplicationDraftSettings: nextApplicationSettings,
      sourceManpowerRequestId: null
    });

    setShowModal(true);
  };

  // Publish/unpublish toggle
  const togglePublish = async (job) => {
    try {
      if (!job?.id) return;

      const now = Date.now();
      const nowIso = new Date(now).toISOString();
      let approvalToReview = null;

      if (!job.published && job.approvalStatus === 'pending_approval') {
        approvalToReview = await resolvePendingJobApproval(job, { action: 'job_posting' });
        if (!canReviewJobApproval(approvalToReview)) {
          showHomeNotice('Only a job approver who did not submit this request can publish it.');
          return;
        }
      } else if (!canCreateOrEditJobs) {
        showHomeNotice('You do not have permission to publish or unpublish job posts.');
        return;
      }

      if (!job.published && requiresJobApproval && !approvalToReview) {
        await submitJobForApproval(job);
        return;
      }

      const updates = {};
      if (job.published) {
        // Unpublish: archive analytics; the server removes the public copy.

        // Read analytics data from public node before removing
        const viewsByMonthRef = ref(database, `job_posts/${job.id}/viewsByMonth`);
        const applyClicksByMonthRef = ref(database, `job_posts/${job.id}/applyClicksByMonth`);

        const [viewsSnap, appliesSnap] = await Promise.all([
          get(viewsByMonthRef),
          get(applyClicksByMonthRef)
        ]);

        // Save analytics to company node (directly under the job post, not in analytics subfolder)
        if (viewsSnap.exists()) {
          updates[`companies/${companyUID}/posts/${job.id}/viewsByMonth`] = viewsSnap.val();
        }
        if (appliesSnap.exists()) {
          updates[`companies/${companyUID}/posts/${job.id}/applyClicksByMonth`] = appliesSnap.val();
        }

        // Mark the source unpublished; server-side sync withdraws the public copy.
        updates[`companies/${companyUID}/posts/${job.id}/published`] = false;
        updates[`companies/${companyUID}/posts/${job.id}/lifecycleStatus`] = 'closed';
        updates[`companies/${companyUID}/posts/${job.id}/closedAt`] = nowIso;
        updates[`companies/${companyUID}/posts/${job.id}/updatedAt`] = nowIso;
      } else {
        // The server projects the published record and restores its archived counters.
        updates[`companies/${companyUID}/posts/${job.id}/published`] = true;
        updates[`companies/${companyUID}/posts/${job.id}/approvalId`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/approvalStatus`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/approvalSubmittedAt`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/approvalSubmittedByUid`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/approvalRejectedAt`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/approvalRejectionReason`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/lifecycleStatus`] = 'published';
        updates[`companies/${companyUID}/posts/${job.id}/firstPublishedAt`] = job.firstPublishedAt || nowIso;
        updates[`companies/${companyUID}/posts/${job.id}/closedAt`] = null;
        updates[`companies/${companyUID}/posts/${job.id}/updatedAt`] = nowIso;

        // If this was pending approval, mark approval as reviewed + notify requester
        if (approvalToReview) {
          updates[`companies/${companyUID}/pending_approvals/${approvalToReview.id}/status`] = 'approved';
          updates[`companies/${companyUID}/pending_approvals/${approvalToReview.id}/reviewedBy`] = {
            uid: currentUser?.uid,
            name: currentUser?.displayName || currentUser?.email,
            email: currentUser?.email
          };
          updates[`companies/${companyUID}/pending_approvals/${approvalToReview.id}/reviewedAt`] = now;
          notifyJobApprovalRequester({
            updates,
            approval: approvalToReview,
            status: 'approved',
            now
          });
        }
      }
      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: job.published ? 'job_closed' : 'job_published',
        jobId: job.id,
        jobData: {
          ...job,
          companyLocation: job.companyLocation || job.location || '',
          lifecycleStatus: job.published ? 'closed' : 'published'
        },
        lifecycleStatus: job.published ? 'closed' : 'published',
        occurredAt: nowIso,
        context: {
          originAction: 'toggle_publish',
          previousPublished: !!job.published,
          ...(approvalToReview ? { approvalId: approvalToReview.id } : {})
        },
        closed: !!job.published
      });
      setJobPosts((prev) => prev.map((row) => (
        row.id === job.id
          ? {
            ...row,
            published: !job.published,
            approvalId: null,
            approvalStatus: null,
            approvalSubmittedAt: null,
            approvalSubmittedByUid: null,
            approvalRejectedAt: null,
            approvalRejectionReason: null,
            lifecycleStatus: job.published ? 'closed' : 'published',
            firstPublishedAt: !job.published ? (row.firstPublishedAt || nowIso) : row.firstPublishedAt,
            closedAt: job.published ? nowIso : null,
            updatedAt: nowIso
          }
          : row
      )));
      // Update selected job state immediately
      setSelectedJob(prev => prev ? {
        ...prev,
        published: !job.published,
        approvalId: null,
        approvalStatus: null,
        approvalSubmittedAt: null,
        approvalSubmittedByUid: null,
        approvalRejectedAt: null,
        approvalRejectionReason: null,
        lifecycleStatus: job.published ? 'closed' : 'published',
        firstPublishedAt: !job.published ? (prev.firstPublishedAt || nowIso) : prev.firstPublishedAt,
        closedAt: job.published ? nowIso : null,
        updatedAt: nowIso
      } : prev);
    } catch (e) {
      console.error('togglePublish error:', e);
      showHomeNotice('Failed to toggle publish state. Please try again.');
    }
  };

  // Submit job post for approval (when approvals enabled and user lacks canPublishJobs)
  const submitJobForApproval = async (job) => {
    try {
      const submittedAtIso = new Date().toISOString();
      const submittedAt = Date.now();
      const approvalId = `apr_${submittedAt}_${Math.random().toString(36).substr(2, 9)}`;
      const approvalData = {
        id: approvalId,
        type: 'job_posting',
        entityId: job.id,
        status: 'pending',
        submittedBy: {
          uid: currentUser.uid,
          name: currentUser.displayName || currentUser.email,
          email: currentUser.email
        },
        submittedAt,
        reviewedBy: null,
        reviewedAt: null,
        rejectionReason: null,
        snapshot: {
          title: job.jobOpening || job.title || 'Untitled',
          department: job.department || '',
          seniority: job.seniority || ''
        }
      };

      const updates = {
        [`companies/${companyUID}/pending_approvals/${approvalId}`]: approvalData,
        [`companies/${companyUID}/posts/${job.id}/approvalId`]: approvalId,
        [`companies/${companyUID}/posts/${job.id}/approvalStatus`]: 'pending_approval',
        [`companies/${companyUID}/posts/${job.id}/approvalSubmittedAt`]: submittedAt,
        [`companies/${companyUID}/posts/${job.id}/approvalSubmittedByUid`]: currentUser.uid,
        [`companies/${companyUID}/posts/${job.id}/approvalRejectedAt`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalRejectionReason`]: null,
        [`companies/${companyUID}/posts/${job.id}/lifecycleStatus`]: 'pending_approval',
        [`companies/${companyUID}/posts/${job.id}/updatedAt`]: submittedAtIso
      };

      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: job.id,
        jobData: {
          ...job,
          lifecycleStatus: 'pending_approval'
        },
        lifecycleStatus: 'pending_approval',
        occurredAt: submittedAtIso,
        context: {
          originAction: 'submit_job_for_approval',
          approvalId
        }
      });

      // Update local state
      setSelectedJob(prev => prev ? {
        ...prev,
        approvalId,
        approvalStatus: 'pending_approval',
        approvalSubmittedAt: submittedAt,
        approvalSubmittedByUid: currentUser.uid,
        approvalRejectedAt: null,
        approvalRejectionReason: null
      } : prev);
      setJobPosts(prev => prev.map(p =>
        p.id === job.id ? {
          ...p,
          approvalId,
          approvalStatus: 'pending_approval',
          approvalSubmittedAt: submittedAt,
          approvalSubmittedByUid: currentUser.uid,
          approvalRejectedAt: null,
          approvalRejectionReason: null
        } : p
      ));

      showHomeNotice('Job post submitted for approval. An approver will review it shortly.');
    } catch (e) {
      console.error('submitJobForApproval error:', e);
      showHomeNotice('Failed to submit for approval. Please try again.');
    }
  };

  const rejectJobApproval = async (job) => {
    const rejectionReason = await promptHomeAction('Reason for rejecting this job post?');
    if (rejectionReason === null) return;

    try {
      const approval = await resolvePendingJobApproval(job, { action: 'job_posting' });
      if (!approval) {
        showHomeNotice('Could not find the pending job approval.');
        return;
      }
      if (!canReviewJobApproval(approval)) {
        showHomeNotice('You do not have permission to reject this job approval.');
        return;
      }

      const now = Date.now();
      const rejectedAtIso = new Date(now).toISOString();
      const trimmedReason = rejectionReason.trim();
      const reviewer = {
        uid: currentUser?.uid,
        name: currentUser?.displayName || currentUser?.email,
        email: currentUser?.email
      };
      const updates = {
        [`companies/${companyUID}/pending_approvals/${approval.id}/status`]: 'rejected',
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedBy`]: reviewer,
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedAt`]: now,
        [`companies/${companyUID}/posts/${job.id}/approvalId`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalStatus`]: 'rejected',
        [`companies/${companyUID}/posts/${job.id}/approvalRejectedAt`]: now,
        [`companies/${companyUID}/posts/${job.id}/approvalRejectionReason`]: trimmedReason || null,
        [`companies/${companyUID}/posts/${job.id}/lifecycleStatus`]: 'draft',
        [`companies/${companyUID}/posts/${job.id}/updatedAt`]: rejectedAtIso
      };

      if (trimmedReason) {
        updates[`companies/${companyUID}/pending_approvals/${approval.id}/rejectionReason`] = trimmedReason;
      }

      notifyJobApprovalRequester({
        updates,
        approval,
        status: 'rejected',
        now
      });
      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: job.id,
        jobData: {
          ...job,
          approvalStatus: 'rejected',
          lifecycleStatus: 'draft'
        },
        lifecycleStatus: 'draft',
        occurredAt: rejectedAtIso,
        context: {
          originAction: 'reject_job_approval',
          approvalId: approval.id
        }
      });

      const nextJobState = {
        ...job,
        approvalId: null,
        approvalStatus: 'rejected',
        approvalRejectedAt: now,
        approvalRejectionReason: trimmedReason || null,
        lifecycleStatus: 'draft',
        updatedAt: rejectedAtIso
      };
      setSelectedJob(nextJobState);
      setJobPosts((prev) => prev.map((row) => (row.id === job.id ? nextJobState : row)));
      showHomeNotice('Job approval rejected.');
    } catch (error) {
      console.error('rejectJobApproval error:', error);
      showHomeNotice('Failed to reject job approval. Please try again.');
    }
  };

  // Cancel pending approval request
  const cancelApprovalRequest = async (job) => {
    if (!(await confirmHomeAction('Are you sure you want to cancel this approval request?'))) return;

    try {
      const approval = await resolvePendingJobApproval(job, { action: 'job_posting' });
      if (!approval) {
        showHomeNotice('Could not find the pending job approval.');
        return;
      }
      if (!canCancelJobApproval(approval)) {
        showHomeNotice('Only the requester or workspace owner can cancel this approval request.');
        return;
      }

      const cancelledAt = Date.now();
      const cancelledAtIso = new Date(cancelledAt).toISOString();
      const updates = {
        [`companies/${companyUID}/pending_approvals/${approval.id}/status`]: 'cancelled',
        [`companies/${companyUID}/pending_approvals/${approval.id}/reviewedAt`]: cancelledAt,
        [`companies/${companyUID}/pending_approvals/${approval.id}/metadata/cancelReason`]: 'cancelled_by_requester',
        [`companies/${companyUID}/posts/${job.id}/approvalId`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalStatus`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalSubmittedAt`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalSubmittedByUid`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalRejectedAt`]: null,
        [`companies/${companyUID}/posts/${job.id}/approvalRejectionReason`]: null,
        [`companies/${companyUID}/posts/${job.id}/lifecycleStatus`]: 'draft',
        [`companies/${companyUID}/posts/${job.id}/closedAt`]: null,
        [`companies/${companyUID}/posts/${job.id}/updatedAt`]: cancelledAtIso
      };
      await update(ref(database), updates);
      await emitJobLifecycleAnalyticsEvent({
        companyUID,
        eventType: 'job_updated',
        jobId: job.id,
        jobData: {
          ...job,
          lifecycleStatus: 'draft'
        },
        lifecycleStatus: 'draft',
        occurredAt: cancelledAtIso,
        context: {
          originAction: 'cancel_job_approval_request',
          approvalId: approval.id
        }
      });

      const nextJobState = {
        ...job,
        approvalId: null,
        approvalStatus: null,
        approvalSubmittedAt: null,
        approvalSubmittedByUid: null,
        approvalRejectedAt: null,
        approvalRejectionReason: null,
        lifecycleStatus: 'draft',
        closedAt: null,
        updatedAt: cancelledAtIso
      };
      setSelectedJob(nextJobState);
      setJobPosts(prev => prev.map(p => (p.id === job.id ? nextJobState : p)));
    } catch (e) {
      console.error('cancelApprovalRequest error:', e);
      showHomeNotice('Failed to cancel request. Please try again.');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!canCreateOrEditJobs) {
      showHomeNotice('You do not have permission to delete job posts.');
      return;
    }

    if (await confirmHomeAction("Would you like to delete this job post?", { title: t('feedback.deleteTitle', 'Delete job?'), confirmLabel: t('feedback.delete', 'Delete job') })) {
      try {
        const jobSnapshot = jobPosts.find(job => job.id === jobId) || selectedJob || {};
        if (jobSnapshot?.approvalStatus === 'pending_approval') {
          const pendingApproval = await resolvePendingJobApproval(jobSnapshot, { action: 'job_posting' });
          if (pendingApproval && !canCancelJobApproval(pendingApproval)) {
            showHomeNotice('Only the requester or workspace owner can delete a pending approval request.');
            return;
          }
        }
        const deletedAt = Date.now();
        const deletedAtIso = new Date().toISOString();
        const updates = {};
        updates[`companies/${companyUID}/posts/${jobId}`] = null;

        const approvalsSnap = await get(ref(database, `companies/${companyUID}/pending_approvals`));
        if (approvalsSnap.exists()) {
          Object.entries(approvalsSnap.val() || {}).forEach(([approvalId, approval]) => {
            if (approval?.type !== 'job_posting' || approval?.entityId !== jobId || approval?.status !== 'pending') return;
            updates[`companies/${companyUID}/pending_approvals/${approvalId}/status`] = 'cancelled';
            updates[`companies/${companyUID}/pending_approvals/${approvalId}/reviewedAt`] = deletedAt;
            updates[`companies/${companyUID}/pending_approvals/${approvalId}/metadata/cancelReason`] = 'job_deleted';
          });
        }

        await update(ref(database), updates);
        await emitJobLifecycleAnalyticsEvent({
          companyUID,
          eventType: 'job_deleted',
          jobId,
          jobData: {
            ...jobSnapshot,
            companyLocation: jobSnapshot.companyLocation || jobSnapshot.location || '',
            deletedAt: deletedAtIso
          },
          lifecycleStatus: 'deleted',
          occurredAt: deletedAtIso,
          context: {
            originAction: 'job_delete',
            deletedAt: deletedAtIso
          },
          deleted: true
        });

        setSelectedJob(null); // Close the details panel
        setJobPosts((prev) => prev.filter((job) => job.id !== jobId));
        showHomeNotice('Job deleted successfully!', { tone: 'success', title: t('feedback.deletedTitle', 'Job deleted') });
      } catch (error) {
        console.error('Error deleting job:', error);
        showHomeNotice('Failed to delete job. Please try again.');
      }
    }
  };

  /* ---------------------------------------------
     Share job link – replicates old site logic
     --------------------------------------------- */
  const handleShareJob = async (job) => {
    if (!job?.published) {
      showHomeNotice('Only published job posts can be shared.');
      return;
    }

    const setButtonState = (text, disabled = false) => {
      setJobShareState({ jobId: job.id, text, disabled });
    };
    setButtonState(t('actions.copyingLink', 'Copying…'), true);

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        // Fallback for older browsers
        const tempInput = document.createElement("input");
        document.body.appendChild(tempInput);
        tempInput.value = text;
        tempInput.select();
        let copied = false;
        try { copied = document.execCommand("copy"); }
        finally { document.body.removeChild(tempInput); }
        if (!copied) throw new Error('Clipboard unavailable');
      }
    };

    try {
      console.log('Checking for existing external link for job:', job.id);

      // Check if externalLink already exists in Firebase
      const jobRef = ref(database, `companies/${companyUID}/posts/${job.id}/externalLink`);
      const snapshot = await get(jobRef);

      if (snapshot.exists()) {
        console.log('External link already exists, copying to clipboard');
        // Link already exists, just copy it
        const externalLink = snapshot.val();
        await copyToClipboard(externalLink);
        setButtonState(t('actions.linkCopied', 'Copied!'), true);
        setTimeout(() => setButtonState(t('actions.copyJobLink', 'Copy link to job')), 3000);
        return;
      }

      console.log('External link does not exist, generating new one...');

      // Show generating state
      setButtonState(t('actions.copyingLink', 'Copying…'), true);

      console.log('Calling cloud function with userUID:', companyUID, 'jobKey:', job.id);

      // Call cloud function to generate encrypted slug
      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/ext-job-link-gen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userUID: companyUID, jobKey: job.id })
      });

      console.log('Cloud function response status:', response.status);

      const data = await response.json();
      console.log('Cloud function response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Generation failed');
      }

      const externalLink = `https://jobseeqr.co/j/?i=${data.encryptedSlug}`;
      console.log('Generated external link:', externalLink);

      // Save to Firebase
      await set(ref(database, `companies/${companyUID}/posts/${job.id}/externalLink`), externalLink);
      console.log('Saved external link to Firebase');

      // Update local state so subsequent clicks are instant
      setJobPosts(prev => prev.map(j => j.id === job.id ? { ...j, externalLink } : j));

      // Copy to clipboard
      await copyToClipboard(externalLink);
      setButtonState(t('actions.linkCopied', 'Copied!'), true);
      setTimeout(() => setButtonState(t('actions.copyJobLink', 'Copy link to job')), 3000);
    } catch (error) {
      console.error('Error generating or copying share link:', error);
      setButtonState(t('actions.copyJobLink', 'Copy link to job'), false);
      showHomeNotice(t('actions.copyJobLinkFailed', 'Could not copy the job link. Please try again.'));
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // ==================== TUTORIAL IMPLEMENTATION ====================

  // Generate demo data for tutorial
  const generateDemoData = () => {
    const tr = (en, ar) => (isRTL ? ar : en);
    const now = Date.now();
    const threeDaysAgo = now - (3 * 24 * 60 * 60 * 1000);
    const fiveDaysAgo = now - (5 * 24 * 60 * 60 * 1000);
    const seniorTitle = tr('Senior Software Engineer', 'مهندس برمجيات أول');
    const marketingTitle = tr('Marketing Manager', 'مدير تسويق');

    // Create 2 demo jobs (1 published, 1 unpublished)
    const demoJobs = [
      {
        id: '_DEMO_JOB_001',
        jobOpening: seniorTitle,
        salary: '$80,000 - $120,000 USD',
        negotiability: 'Negotiable',
        jobCategory: 'Software & Information Technology // البرمجيات وتكنولوجيا المعلومات // Logiciels & Technologies de L\'Information',
        jobFunction: 'Software & Information Technology // البرمجيات وتكنولوجيا المعلومات // Logiciels & Technologies de L\'Information',
        mode1: 'Full-Time',
        mode2: 'Remote',
        schedule: 'Full-Time',
        workspace: 'Remote',
        department: 'Engineering',
        seniority: 'Senior',
        experienceYears: '5',
        postedTime: threeDaysAgo,
        published: true,
        jobDescription: tr(
          'We are looking for a talented Senior Software Engineer to join our growing team. You will work on cutting-edge projects using React, Node.js, and cloud technologies.',
          'نبحث عن مهندس برمجيات أول موهوب للانضمام إلى فريقنا المتنامي. ستعمل على مشاريع متقدمة باستخدام React وNode.js وتقنيات السحابة.'
        ),
        jobDescriptionHTML: tr(
          '<p>We are looking for a talented Senior Software Engineer to join our growing team. You will work on cutting-edge projects using React, Node.js, and cloud technologies.</p><p><strong>Requirements:</strong></p><ul><li>5+ years of experience in software development</li><li>Proficiency in React and Node.js</li><li>Experience with cloud platforms (AWS/Azure)</li><li>Strong problem-solving skills</li></ul>',
          '<p>نبحث عن مهندس برمجيات أول موهوب للانضمام إلى فريقنا المتنامي. ستعمل على مشاريع متقدمة باستخدام React وNode.js وتقنيات السحابة.</p><p><strong>المتطلبات:</strong></p><ul><li>خبرة 5+ سنوات في تطوير البرمجيات</li><li>إجادة React وNode.js</li><li>خبرة في السحابة (AWS/Azure)</li><li>مهارات قوية في حل المشكلات</li></ul>'
        ),
        viewsByMonth: {
          '2025-10': 247,
          '2025-09': 189,
          '2025-08': 156,
          '2025-07': 203,
          '2025-06': 178,
          '2025-05': 142
        },
        applyClicksByMonth: {
          '2025-10': 42,
          '2025-09': 31,
          '2025-08': 28,
          '2025-07': 35,
          '2025-06': 29,
          '2025-05': 24
        }
      },
      {
        id: '_DEMO_JOB_002',
        jobOpening: marketingTitle,
        salary: '$60,000 - $90,000 USD',
        negotiability: 'Negotiable',
        jobCategory: 'Advertising & Marketing // التسويق الإعلاني // Publicité & Marketing',
        jobFunction: 'Advertising & Marketing // التسويق الإعلاني // Publicité & Marketing',
        mode1: 'Full-Time',
        mode2: 'Hybrid',
        schedule: 'Full-Time',
        workspace: 'Hybrid',
        department: 'Marketing',
        seniority: 'Mid-Level',
        experienceYears: '3',
        postedTime: fiveDaysAgo,
        published: false,
        jobDescription: tr(
          'Looking for a creative Marketing Manager to lead our marketing initiatives and drive brand growth.',
          'نبحث عن مدير تسويق مبدع لقيادة مبادرات التسويق ودفع نمو العلامة التجارية.'
        ),
        jobDescriptionHTML: tr(
          '<p>Looking for a creative Marketing Manager to lead our marketing initiatives and drive brand growth.</p><p><strong>Requirements:</strong></p><ul><li>3+ years of marketing experience</li><li>Strong analytical and creative skills</li><li>Experience with digital marketing</li></ul>',
          '<p>نبحث عن مدير تسويق مبدع لقيادة مبادرات التسويق ودفع نمو العلامة التجارية.</p><p><strong>المتطلبات:</strong></p><ul><li>خبرة 3+ سنوات في التسويق</li><li>مهارات تحليلية وإبداعية قوية</li><li>خبرة في التسويق الرقمي</li></ul>'
        ),
        viewsByMonth: {
          '2025-10': 0,
          '2025-09': 0
        },
        applyClicksByMonth: {
          '2025-10': 0,
          '2025-09': 0
        }
      }
    ];

    // Create demo notifications
    const demoNotifications = [
      {
        id: '_DEMO_NOTIF_001',
        type: 'new_application',
        title: tr('New Application Received', 'تم استلام طلب جديد'),
        description: tr('Sarah Johnson applied for Senior Software Engineer', 'تقدمت سارة الخطيب لوظيفة مهندس برمجيات أول'),
        candidateName: tr('Sarah Johnson', 'سارة الخطيب'),
        jobTitle: seniorTitle,
        timestamp: now - (2 * 60 * 60 * 1000), // 2 hours ago
        unread: true
      },
      {
        id: '_DEMO_NOTIF_002',
        type: 'assessment_completed',
        title: tr('Assessment Completed', 'اكتمل التقييم'),
        description: tr('Michael Chen completed the Technical Assessment', 'أنهى مايكل وان التقييم التقني'),
        candidateName: tr('Michael Chen', 'مايكل وان'),
        jobTitle: seniorTitle,
        timestamp: now - (5 * 60 * 60 * 1000), // 5 hours ago
        unread: true
      },
      {
        id: '_DEMO_NOTIF_003',
        type: 'unread_chat',
        title: tr('New Message', 'رسالة جديدة'),
        description: tr('You have a new message from Emma Wilson', 'لديك رسالة جديدة من ليلى حجازي'),
        candidateName: tr('Emma Wilson', 'ليلى حجازي'),
        jobTitle: seniorTitle,
        timestamp: now - (1 * 24 * 60 * 60 * 1000), // 1 day ago
        unread: false
      }
    ];

    // Create demo analytics data
    const currentMonth = '2025-10';
    const months = ['2025-05', '2025-06', '2025-07', '2025-08', '2025-09', '2025-10'];

    const demoAnalytics = {
      jobs: [seniorTitle, marketingTitle],
      viewsCounts: {
        [seniorTitle]: 247,
        [marketingTitle]: 0
      },
      applyCounts: {
        [seniorTitle]: 42,
        [marketingTitle]: 0
      },
      series: months.map(month => {
        const seniorViews = demoJobs[0].viewsByMonth[month] || 0;
        const marketingViews = demoJobs[1].viewsByMonth[month] || 0;
        return seniorViews + marketingViews;
      }),
      seriesByJob: {
        [seniorTitle]: months.map(month => demoJobs[0].viewsByMonth[month] || 0),
        [marketingTitle]: months.map(month => demoJobs[1].viewsByMonth[month] || 0)
      }
    };

    return { demoJobs, demoNotifications, demoAnalytics };
  };

  // Cleanup demo data and restore real data
  const cleanupDemoData = () => {
    setTutorialActive(false);
    setJobPosts(realJobPosts);
    setNotifs(realNotifs);
    if (realAnalytics) {
      setMiniAnalytics(realAnalytics);
    }
    setSelectedJob(null);
    closeModal({ skipConfirmation: true });
    setModalPage(1);
    console.log('✅ Demo data cleaned up, real data restored');
  };

  // Helper function to wait for an element to exist in the DOM
  const waitForElement = (selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkElement = () => {
        const element = document.querySelector(selector);
        if (element) {
          console.log(`✅ Found element: ${selector}`);
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          console.error(`❌ Element ${selector} not found within ${timeout}ms`);
          reject(new Error(`Element ${selector} not found within ${timeout}ms`));
        } else {
          setTimeout(checkElement, 50);
        }
      };
      checkElement();
    });
  };

  const startHomeTutorial = async () => {
    const tr = (en, ar) => (isRTL ? ar : en);
    setTutorialActive(true);

    // Step 1: Backup real data and load demo data
    setRealJobPosts([...jobPosts]);
    setRealNotifs([...notifs]);
    setRealAnalytics({ ...miniAnalytics });

    const { demoJobs, demoNotifications, demoAnalytics } = generateDemoData();
    setJobPosts(demoJobs);
    setNotifs(demoNotifications);
    setMiniAnalytics(demoAnalytics);
    setSelectedJob(null);

    await new Promise(resolve => setTimeout(resolve, 500)); // Wait for UI to update

    let driverObj; // Declare here so it's accessible in callbacks

    const steps = [
      // Step 1: Welcome
      {
        popover: {
          title: tr('👋 Welcome to Job Management!', '👋 أهلاً بك في إدارة الوظائف'),
          description: tr(
            `This is your <strong>Job Management Hub</strong>. Let's explore how to post jobs, track applications, and analyze performance.`,
            `هذا هو <strong>مركز إدارة الوظائف</strong>. دعنا نستكشف كيفية نشر الوظائف، تتبع الطلبات، وتحليل الأداء.`
          ),
        }
      },

      // Step 2: Post a Job Button
      {
        element: '.post-job-cta',
        popover: {
          title: tr('📝 Post a Job', '📝 نشر وظيفة'),
          description: tr(
            `
            <strong>Click this button</strong> to create a new job posting.<br/><br/>
            Let's open it and see what's inside!
          `,
            `
            <strong>اضغط هذا الزر</strong> لإنشاء إعلان وظيفة جديد.<br/><br/>
            لنفتحه ونرى التفاصيل!
          `
          ),
          side: 'bottom',
          align: 'start',
          onNextClick: (element, step, options) => {
            setShowModal(true);
            setModalPage(1);
            setEditingMode(false);
            // Wait for modal to render, then manually move next
            waitForElement('.modal-content')
              .then(() => {
                driverObj.moveNext();
              })
              .catch(error => {
                console.error('Modal content not found:', error);
                driverObj.moveNext(); // Continue anyway
              });
          }
        }
      },

      // Step 3: Job Posting Popup - Page 1
      {
        element: '.modal-content',
        popover: {
          title: tr('📋 Job Details - Page 1', '📋 تفاصيل الوظيفة - الصفحة 1'),
          description: tr(
            `
            <strong>Fill in your job basics:</strong><br/>
            • Job title & category<br/>
            • Salary range<br/>
            • Work mode (Full-Time/Part-Time)<br/>
            • Location type (Remote/Hybrid/In-Person)<br/><br/>
            Let's move to page 2 →
          `,
            `
            <strong>أكمل بيانات الوظيفة الأساسية:</strong><br/>
            • المسمى والفئة الوظيفية<br/>
            • نطاق الراتب<br/>
            • نمط العمل (دوام كامل/جزئي)<br/>
            • نوع الموقع (عن بُعد/هجين/مكتبي)<br/><br/>
            لننتقل إلى الصفحة 2 →
          `
          ),
          side: 'right',
          align: 'start',
          onNextClick: (element, step, options) => {
            setModalPage(2);
            // Wait for page 2 content to render
            waitForElement('#description, .quill-editor')
              .then(() => {
                driverObj.moveNext();
              })
              .catch(error => {
                console.error('Page 2 content not found:', error);
                driverObj.moveNext();
              });
	          },
	          onPrevClick: (element, step, options) => {
            closeModal({ skipConfirmation: true });
            setModalPage(1);
            // Wait for modal to close
            setTimeout(() => {
              driverObj.movePrevious();
            }, 300);
          }
        }
      },

      // Step 4: Job Posting Popup - Page 2
      {
        element: '.modal-content',
        popover: {
          title: tr('✨ AI-Powered Job Description', '✨ وصف وظيفة مدعوم بالذكاء الاصطناعي'),
          description: tr(
            `
            <strong>Page 2 features:</strong><br/>
            • <strong>AI Generate:</strong> Let AI write your job description based on the title<br/>
            • <strong>Manual Entry:</strong> Write your own description<br/>
            • <strong>Rich Text Editor:</strong> Format with bold, lists, etc.<br/><br/>
            Once done, click "Post" to publish to JOBSEEQR!
          `,
            `
            <strong>مزايا الصفحة 2:</strong><br/>
            • <strong>توليد بالذكاء الاصطناعي:</strong> دع النظام يكتب الوصف بناءً على المسمى<br/>
            • <strong>إدخال يدوي:</strong> اكتب الوصف بنفسك<br/>
            • <strong>محرر نصوص غني:</strong> تنسيق بالخط العريض والقوائم وغيرها<br/><br/>
            عند الانتهاء اضغط "Post" للنشر على JOBSEEQR!
          `
          ),
	          side: 'right',
	          align: 'start',
	          onNextClick: (element, step, options) => {
            closeModal({ skipConfirmation: true });
            setModalPage(1);
            // Wait for modal to close and job list to be visible
            setTimeout(() => {
              waitForElement('.job-list')
                .then(() => {
                  driverObj.moveNext();
                })
                .catch(error => {
                  console.error('Job list not found:', error);
                  driverObj.moveNext();
                });
            }, 300);
          },
          onPrevClick: (element, step, options) => {
            setModalPage(1);
            // Wait for page 1 to render
            setTimeout(() => {
              driverObj.movePrevious();
            }, 300);
          }
        }
      },

      // Step 5: Jobs List
      {
        element: '.job-list',
        popover: {
          title: tr('📋 Your Job Posts', '📋 إعلانات وظائفك'),
          description: tr(
            `
            All your jobs appear here.<br/><br/>
            <strong>Jobs are:</strong><br/>
            • Posted to the <strong>JOBSEEQR candidate portal & app</strong><br/>
            • <strong>Syndicated to LinkedIn, Indeed</strong>, and other platforms<br/>
            • <strong>Shareable via easy-to-fill forms</strong> for candidates<br/><br/>
            Click any job to see details →
          `,
            `
            كل الوظائف تظهر هنا.<br/><br/>
            <strong>يتم:</strong><br/>
            • نشرها على <strong>بوابة وتطبيق JOBSEEQR</strong><br/>
            • توزيعها على <strong>LinkedIn وIndeed</strong> ومنصات أخرى<br/>
            • مشاركتها عبر نماذج سهلة للمرشحين<br/><br/>
            انقر على أي وظيفة لرؤية التفاصيل →
          `
          ),
          side: 'right',
          align: 'start',
          onNextClick: (element, step, options) => {
            setSelectedJob(demoJobs[0]);
            // Wait for panel card to render with job details
            waitForElement('.panel-card.job-details, .panel-card:has(.job-title)')
              .then(() => {
                driverObj.moveNext();
              })
              .catch(error => {
                console.error('Job details panel not found:', error);
                driverObj.moveNext();
              });
          },
          onPrevClick: (element, step, options) => {
            setShowModal(true);
            setModalPage(2);
            // Wait for modal to render
            waitForElement('.modal-content')
              .then(() => {
                driverObj.movePrevious();
              })
              .catch(error => {
                console.error('Modal not found:', error);
                driverObj.movePrevious();
              });
          }
        }
      },

      // Step 6: Job Details Panel
      {
        element: '.panel-card',
        popover: {
          title: tr('📊 Job Details Panel', '📊 لوحة تفاصيل الوظيفة'),
          description: tr(
            `
            <strong>Everything about this job:</strong><br/><br/>
            • <strong>Status:</strong> Published/Unpublished<br/>
            • <strong>Actions:</strong> Edit, Share, Delete, Toggle Publish<br/>
            • <strong>Description:</strong> Full job details<br/>
            • <strong>Analytics:</strong> Views & applications over time<br/><br/>
            You can manage everything from here!
          `,
            `
            <strong>كل ما يخص الوظيفة:</strong><br/><br/>
            • <strong>الحالة:</strong> منشورة/غير منشورة<br/>
            • <strong>إجراءات:</strong> تعديل، مشاركة، حذف، تبديل النشر<br/>
            • <strong>الوصف:</strong> تفاصيل الوظيفة كاملة<br/>
            • <strong>التحليلات:</strong> المشاهدات والطلبات بمرور الوقت<br/><br/>
            يمكنك إدارة كل شيء من هنا!
          `
          ),
          side: 'left',
          align: 'start',
          onNextClick: (element, step, options) => {
            setSelectedJob(null);
            // Wait for notifications panel to be visible
            setTimeout(() => {
              waitForElement('.panel-card:has(.notif-header)')
                .then(() => {
                  driverObj.moveNext();
                })
                .catch(error => {
                  console.error('Notifications panel not found:', error);
                  driverObj.moveNext();
                });
            }, 200);
          },
          onPrevClick: (element, step, options) => {
            setSelectedJob(null);
            // Wait a moment for UI to update
            setTimeout(() => {
              waitForElement('.job-list')
                .then(() => {
                  driverObj.movePrevious();
                })
                .catch(error => {
                  console.error('Job list not found:', error);
                  driverObj.movePrevious();
                });
            }, 200);
          }
        }
      },

      // Step 7: Notifications
      {
        element: '.panel-card:has(.notif-header)',
        popover: {
          title: tr('🔔 Notifications Center', '🔔 مركز الإشعارات'),
          description: tr(
            `
            Get instant alerts for:<br/>
            • <strong>New applications</strong> received<br/>
            • <strong>Candidate messages</strong><br/>
            • <strong>Assessment completions</strong><br/>
            • <strong>Candidates needing attention</strong><br/><br/>
            Click "See all" to view full notification history
          `,
            `
            احصل على تنبيهات فورية لـ:<br/>
            • <strong>طلبات جديدة</strong><br/>
            • <strong>رسائل المرشحين</strong><br/>
            • <strong>اكتمال التقييمات</strong><br/>
            • <strong>مرشحين يحتاجون المتابعة</strong><br/><br/>
            اضغط "See all" لعرض سجل الإشعارات كاملًا.
          `
          ),
          side: 'left',
          align: 'start',
          onPrevClick: (element, step, options) => {
            setSelectedJob(demoJobs[0]);
            // Wait for job details panel to render
            waitForElement('.panel-card.job-details, .panel-card:has(.close-x)')
              .then(() => {
                driverObj.movePrevious();
              })
              .catch(error => {
                console.error('Job details panel not found:', error);
                driverObj.movePrevious();
              });
          }
        }
      },

      // Step 8: Analytics Dashboard
      {
        element: '.panel-card:has(.analytics-controls)',
        popover: {
          title: tr('📊 Analytics Dashboard', '📊 لوحة التحليلات'),
          description: tr(
            `
            <strong>Track performance across all jobs:</strong><br/><br/>
            • <strong>Job Post Views:</strong> How many people saw your posting<br/>
            • <strong>Apply Clicks:</strong> How many clicked "Apply"<br/>
            • <strong>6-Month Trends:</strong> Spot patterns over time<br/>
            • <strong>Filter by Job:</strong> See individual job performance<br/><br/>
            Data-driven hiring decisions!
          `,
            `
            <strong>تابع أداء جميع الوظائف:</strong><br/><br/>
            • <strong>مشاهدات الإعلان:</strong> كم شخص شاهد الوظيفة<br/>
            • <strong>نقرات التقديم:</strong> كم شخص ضغط "تقديم"<br/>
            • <strong>اتجاهات 6 أشهر:</strong> اكتشف الأنماط عبر الزمن<br/>
            • <strong>تصنيف حسب الوظيفة:</strong> راقب أداء كل وظيفة<br/><br/>
            قرارات توظيف مبنية على البيانات!
          `
          ),
          side: 'left',
          align: 'start'
        }
      },

      // Step 9: Complete
      {
        popover: {
          title: tr('🎉 Job Management Complete!', '🎉 تم إنهاء دليل إدارة الوظائف'),
          description: tr(
            `
            <strong>You now know how to:</strong><br/>
            ✅ Post and publish jobs<br/>
            ✅ Monitor notifications<br/>
            ✅ Track views and applications<br/>
            ✅ Analyze performance trends<br/><br/>
            <strong>Ready to see the ATS?</strong><br/>
            <em>(AI-powered applicant tracking & automation)</em>
          `,
            `
            <strong>أصبحت تعرف كيف:</strong><br/>
            ✅ تنشر الوظائف وتصدرها<br/>
            ✅ تتابع الإشعارات<br/>
            ✅ تراقب المشاهدات والطلبات<br/>
            ✅ تحلل اتجاهات الأداء<br/><br/>
            <strong>مستعد لرؤية نظام تتبع المتقدمين؟</strong><br/>
            <em>(تتبع المتقدمين وأتمتة مدعومة بالذكاء الاصطناعي)</em>
          `
          ),
          onNextClick: () => {
            tutorialCompletedRef.current = true;
            shouldReloadWithPromptRef.current = true;
            driverObj.destroy();
            return false;
          }
        }
      }
    ];

    driverObj = driver({
      showProgress: true,
      steps: steps,
      onDestroyStarted: () => {
        const wasCompleted = tutorialCompletedRef.current;
        cleanupDemoData();
        if (!wasCompleted) {
          shouldReloadWithPromptRef.current = false;
        }
      },
      onDestroyed: async () => {
        cleanupDemoData();

        const wasCompleted = tutorialCompletedRef.current;
        const shouldReloadWithPrompt = shouldReloadWithPromptRef.current;

        tutorialCompletedRef.current = false;
        shouldReloadWithPromptRef.current = false;

        if (wasCompleted && companyUID) {
          try {
            await set(ref(database, `companies/${companyUID}/tutorialProgress/home`), true);
          } catch (error) {
            console.error('Error saving tutorial progress:', error);
          }
        }

        if (shouldReloadWithPrompt) {
          const url = new URL(window.location.href);
          url.searchParams.set('showAtsPrompt', '1');
          window.location.href = url.toString();
        }
      }
    });

    setDriverInstance(driverObj);
    driverObj.drive();
  };

  // Listen for tutorial trigger from ProductProtectedRoute
  useEffect(() => {
    const handleTutorialFromOverlay = (event) => {
      console.log('[Home] Received startTutorialFromOverlay event', event.detail);
      const { onTutorialComplete } = event.detail;

      // Start the tutorial
      startHomeTutorial();

      // Set up listener for when tutorial completes
      if (onTutorialComplete && driverInstance) {
        const originalOnDestroy = driverInstance.config?.onDestroyStarted;
        driverInstance.config = {
          ...driverInstance.config,
          onDestroyStarted: () => {
            if (originalOnDestroy) originalOnDestroy();
            onTutorialComplete();
          }
        };
      }
    };

    window.addEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    return () => {
      window.removeEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    };
  }, [driverInstance]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('showAtsPrompt') === '1' && !atsPromptHandledRef.current) {
      atsPromptHandledRef.current = true;

      params.delete('showAtsPrompt');
      const newSearch = params.toString();
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash || ''}`;
      navigate(newPath, { replace: true });

      setTimeout(async () => {
        const continueToATS = await confirmHomeAction(ATS_PROMPT_MESSAGE);
        if (continueToATS) {
          navigate('/applicants', { state: { startTutorial: true } });
        }
        atsPromptHandledRef.current = false;
      }, 0);
    }
  }, [location, navigate]);


  const openFullscreenEditor = () => {
    // Save current content before opening fullscreen
    if (window.quill) {
      const currentContent = window.quill.root.innerHTML;
      setJobDescription(window.quill.getText());
      // Store HTML content for fullscreen editor
      window.tempQuillContent = currentContent;
    }
    setShowFullscreenEditor(true);
  };

  const closeFullscreenEditor = () => {
    // Save fullscreen content back to job description
    if (window.fullscreenQuill) {
      setJobDescription(window.fullscreenQuill.getText());
      window.tempQuillContent = window.fullscreenQuill.root.innerHTML;
    }

    if (window.quill && window.tempQuillContent) {
      window.quill.clipboard.dangerouslyPasteHTML(window.tempQuillContent);
    }
    setShowFullscreenEditor(false);
  };

  const handleAIGenerate = async (editorType = 'regular') => {
    if (aiGenerating) return;
    setAiGenerating(true);

    const currentEditor = editorType === 'regular' ? window.quill : window.fullscreenQuill;
    if (!currentEditor) {
      setAiGenerating(false);
      return;
    }

    // Get the current content as skills/requirements
    const skillsInput = currentEditor.getText().trim();

    // Build payload for backend
    const payload = {
      job_title: formData.jobOpening || 'Job Opening',
      job_category: formData.jobCategory || '',
      job_type: `${formData.mode1}, ${formData.mode2}`,
      skills: skillsInput,
      language: i18n.language || (isRTL ? 'ar' : 'en')
    };

    try {
      const response = await fetch(`${BACKEND_URL}/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Generation failed');

      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = data.description;

      // Update both editors using Quill's proper methods
      const updateEditor = (editor) => {
        if (editor) {
          // Clear existing content
          editor.setContents([]);

          // Insert the new content at index 0
          editor.clipboard.dangerouslyPasteHTML(0, data.description);
        }
      };

      updateEditor(window.quill);
      updateEditor(window.fullscreenQuill);

      // Store the content for later use
      window.tempQuillContent = data.description;
      setJobDescription(currentEditor.getText());
    } catch (error) {
      console.error('AI generation error:', error);
      showHomeNotice('Failed to generate description. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const getApplicationFieldTypeLabel = (type) => {
    switch (type) {
      case 'textarea':
        return t('manageJobs.fieldTypeLongText', 'Long text');
      case 'url':
        return t('manageJobs.fieldTypeLink', 'Link');
      case 'file':
        return t('manageJobs.fieldTypeFile', 'File upload');
      case 'video':
        return t('manageJobs.fieldTypeVideo', 'Video submission');
      case 'boolean':
        return t('manageJobs.fieldTypeYesNo', 'Yes / No');
      default:
        return t('manageJobs.fieldTypeShortText', 'Short text');
    }
  };

  const getFixedApplicationFieldLabel = (fieldId) => {
    switch (fieldId) {
      case 'first_name':
        return t('manageJobs.fixedFirstName', 'First Name');
      case 'last_name':
        return t('manageJobs.fixedLastName', 'Last Name');
      case 'linkedin_url':
        return t('manageJobs.fixedLinkedIn', 'LinkedIn URL');
      case 'email':
        return t('manageJobs.fixedEmail', 'Email');
      case 'cv_upload':
        return t('manageJobs.fixedCv', 'CV Upload');
      default:
        return fieldId;
    }
  };

  const renderApplicationPreviewControl = (field, options = {}) => {
    const { isFixed = false, compact = false, hideBadges = false, onRemove = null } = options;
    const label = isFixed ? getFixedApplicationFieldLabel(field.id) : field.label;
    const canRemove = typeof onRemove === 'function';

    return (
      <div key={`${isFixed ? 'fixed' : 'extra'}-${field.id}`} className="application-form-preview-field">
        {!compact && (
          <div className="application-form-preview-label-row">
            <span>
              {uiText(label)}
              {field.required && <span className="application-form-required-star">*</span>}
            </span>
            {(!hideBadges || canRemove) && (
              <div className="application-form-preview-badges">
                {!hideBadges && isFixed && (
                  <span className="application-form-preview-badge locked">
                    {t('manageJobs.lockedField', 'Locked')}
                  </span>
                )}
                {!hideBadges && field.required && (
                  <span className="application-form-preview-badge">
                    {t('manageJobs.requiredField', 'Required')}
                  </span>
                )}
                {canRemove && (
                  <button
                    type="button"
                    className="application-form-preview-delete"
                    onClick={onRemove}
                    aria-label={t('actions.delete', 'Delete')}
                    title={t('actions.delete', 'Delete')}
                  >
                    <i className="fa-solid fa-trash-can"></i>
                    <span>{t('actions.delete', 'Delete')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {field.type === 'textarea' ? (
          <textarea
            className="application-form-preview-textarea"
            disabled
            placeholder={t('manageJobs.previewPlaceholderLong', 'Candidate answer')}
          />
        ) : field.type === 'file' || field.type === 'video' ? (
          <div className="application-form-preview-upload">
            <i className={`fa-solid ${field.type === 'video' ? 'fa-video' : 'fa-cloud-arrow-up'}`}></i>
            <span>
              {field.type === 'video'
                ? t('manageJobs.previewVideoUploadPlaceholder', 'Upload video')
                : t('manageJobs.previewUploadPlaceholder', 'Upload file')}
            </span>
          </div>
        ) : field.type === 'boolean' ? (
          <div className="application-form-preview-boolean">
            <button type="button" disabled>{t('manageJobs.yesOption', 'Yes')}</button>
            <button type="button" disabled>{t('manageJobs.noOption', 'No')}</button>
          </div>
        ) : (
          <input
            className="application-form-preview-input"
            disabled
            placeholder={field.type === 'url'
              ? t('manageJobs.previewPlaceholderLink', 'https://example.com')
              : t('manageJobs.previewPlaceholderShort', 'Candidate answer')}
          />
        )}
      </div>
    );
  };

  const renderEditableApplicationField = (field) => {
    const isPresetField = Boolean(field.systemKey);

    return (
      <div
        key={field.id}
        className={`application-builder-row ${draggedApplicationFieldId === field.id ? 'dragging' : ''} ${applicationFieldDropIndicator.fieldId === field.id ? `drop-${applicationFieldDropIndicator.position}` : ''} ${isPresetField ? 'preset-row' : ''}`}
        onDragOver={(e) => handleDragOverApplicationField(e, field.id)}
        onDrop={() => handleDropApplicationField(field.id)}
      >
        <div className="application-builder-row-head">
          <div
            className="application-builder-row-handle movable"
            draggable
            onDragStart={() => handleStartApplicationFieldDrag(field.id)}
            onDragEnd={() => {
              setDraggedApplicationFieldId('');
              setApplicationFieldDropIndicator({ fieldId: '', position: 'before' });
            }}
            title={t('manageJobs.dragToReorder', 'Drag to reorder')}
          >
            <i className="fa-solid fa-grip-lines"></i>
          </div>

          {isPresetField ? (
            <div className="application-builder-row-title application-builder-row-title-preset">
              <strong>{uiText(field.label)}</strong>
              <span>{getApplicationFieldTypeLabel(field.type)}</span>
            </div>
          ) : (
            <div className="application-builder-row-edit">
              <textarea
                rows={1}
                value={field.label}
                onChange={(e) => handleUpdateApplicationField(field.id, { label: e.target.value })}
                onInput={autoResizeApplicationQuestion}
                placeholder={t('manageJobs.fieldQuestionPlaceholder', 'Question')}
              />
              <select
                value={field.type}
                onChange={(e) => handleUpdateApplicationField(field.id, { type: e.target.value })}
              >
                <option value="text">{t('manageJobs.fieldTypeShortText', 'Short text')}</option>
                <option value="textarea">{t('manageJobs.fieldTypeLongText', 'Long text')}</option>
                <option value="url">{t('manageJobs.fieldTypeLink', 'Link')}</option>
                <option value="file">{t('manageJobs.fieldTypeFile', 'File upload')}</option>
                <option value="video">{t('manageJobs.fieldTypeVideo', 'Video submission')}</option>
                <option value="boolean">{t('manageJobs.fieldTypeYesNo', 'Yes / No')}</option>
              </select>
            </div>
          )}

          <div className="application-builder-row-actions">
            <button
              type="button"
              className="application-builder-delete danger"
              onClick={() => handleRemoveApplicationField(field.id)}
              title={t('actions.remove', 'Remove')}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>

        <div className="application-builder-row-footer">
          <label>
            <input
              type="checkbox"
              checked={field.required === true}
              onChange={(e) => handleUpdateApplicationField(field.id, { required: e.target.checked })}
            />
            <span>{t('manageJobs.requiredField', 'Required')}</span>
          </label>
        </div>
      </div>
    );
  };

  const applicationFormBuilderView = (
    <div className="application-builder-shell" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="application-builder-back-row">
        <button type="button" className="application-builder-back" onClick={handleCloseApplicationFormBuilder}>
          <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`}></i>
          <span>{t('manageJobs.backToHome', 'Back to Home')}</span>
        </button>
      </div>

      <div className="application-builder-content">
      <div className="application-builder-header">
        <div className="application-builder-title-wrap">
          <h2>{t('manageJobs.applicationFormEditor', 'Public Application Form')}</h2>
        </div>

        <div className="application-builder-header-actions">
          <div className="application-builder-view-toggle">
            <button
              type="button"
              className={`chip ${applicationFormViewMode === 'edit' ? 'chip-active' : ''}`}
              onClick={() => setApplicationFormViewMode('edit')}
            >
              {t('actions.edit', 'Edit')}
            </button>
            <button
              type="button"
              className={`chip ${applicationFormViewMode === 'preview' ? 'chip-active' : ''}`}
              onClick={() => setApplicationFormViewMode('preview')}
            >
              {t('manageJobs.previewMode', 'Preview')}
            </button>
          </div>

          <div className="application-builder-save-wrap">
            <button
              type="button"
              className="post-job-cta application-builder-save"
              onClick={handleSaveActiveApplicationSettings}
              disabled={
                publicJobSettingsSaving
                || publicJobSettingsLoading
                || !hasUnsavedApplicationFormChanges
                || (applicationFormScope === 'department' && !applicationFormDepartment)
              }
            >
              <i className={`fa-solid ${publicJobSettingsSaving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`}></i>
              <span>{publicJobSettingsSaving ? t('manageJobs.savingForm', 'Saving...') : t('manageJobs.saveForm', 'Save Form')}</span>
            </button>

            {publicJobSettingsSaveState === 'saved' && publicJobSettingsSaveMessage && (
              <div className="application-builder-save-tooltip" role="status" aria-live="polite">
                {publicJobSettingsSaveMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      {publicJobSettingsLoading ? (
        <div className="application-builder-empty-state">
          {t('manageJobs.loadingApplicationForm', 'Loading application form settings...')}
        </div>
      ) : (
        <div className="application-builder-canvas">
          <div className="application-builder-canvas-top">
            <div className="application-builder-scope-row">
              <button
                type="button"
                className={`chip ${applicationFormScope === 'general' ? 'chip-active' : ''}`}
                onClick={() => {
                  setApplicationFormScope('general');
                  setApplicationFormDepartment('');
                  resetApplicationFieldComposer();
                }}
              >
                {t('manageJobs.generalForm', 'General')}
              </button>
              <button
                type="button"
                className={`chip ${applicationFormScope === 'department' ? 'chip-active' : ''}`}
                onClick={() => {
                  setApplicationFormScope('department');
                  resetApplicationFieldComposer();
                }}
              >
                {t('manageJobs.departmentForm', 'Specific Department')}
              </button>
            </div>

            <div className="application-builder-canvas-meta">
              {applicationFormScope === 'department' && (
                <>
                  <select
                    className="application-builder-select"
                    value={applicationFormDepartment}
                    onChange={(e) => {
                      setApplicationFormDepartment(e.target.value);
                      resetApplicationFieldComposer();
                    }}
                  >
                    <option value="">{t('manageJobs.selectDepartmentForm', 'Select department')}</option>
                    {departments.map((department) => {
                      const departmentKey = sanitizeDepartmentSettingsKey(department);
                      return (
                        <option key={departmentKey} value={departmentKey}>
                          {translateDepartment(department, isRTL ? 'ar' : 'en')}
                        </option>
                      );
                    })}
                  </select>

                  {applicationFormDepartment && (
                    <span className={`application-builder-status ${hasDepartmentApplicationOverride ? 'override' : ''}`}>
                      {hasDepartmentApplicationOverride
                        ? t('manageJobs.departmentOverrideState', 'Department override')
                        : t('manageJobs.inheritingGeneralForm', 'Inheriting the general form')}
                    </span>
                  )}

                  {applicationFormDepartment && hasDepartmentApplicationOverride && (
                    <button
                      type="button"
                      className="application-builder-reset"
                      onClick={handleResetDepartmentApplicationSettings}
                      disabled={publicJobSettingsSaving}
                    >
                      {t('manageJobs.resetDepartmentForm', 'Reset Override')}
                    </button>
                  )}
                </>
              )}

              {applicationFormViewMode === 'edit' ? (
                <>
                  <span className="application-builder-count">
                    {t('manageJobs.extraFieldsCount', {
                      count: (activeApplicationSettings.fields || []).length,
                      max: PUBLIC_APPLICATION_MAX_FIELDS,
                      defaultValue: '{{count}} of {{max}} extra fields'
                    })}
                  </span>
                </>
              ) : null}
            </div>

            {applicationFormScope === 'department' && !applicationFormDepartment && (
              <div className="application-builder-inline-note">
                {t('manageJobs.selectDepartmentPrompt', 'Choose a department to edit its application form override.')}
              </div>
            )}
          </div>

          {applicationFormViewMode === 'preview' ? (
            <div className={`application-form-preview-surface dark ${applicationFormScope === 'department' && !applicationFormDepartment ? 'is-disabled' : ''}`}>
              {PUBLIC_APPLICATION_FIXED_FIELDS.map((field) => renderApplicationPreviewControl(field, { isFixed: true }))}
              {(activeApplicationSettings.fields || []).map((field) => renderApplicationPreviewControl(field, {
                onRemove: applicationFormScope === 'department' && !applicationFormDepartment
                  ? null
                  : () => handleRemoveApplicationField(field.id)
              }))}
            </div>
          ) : (
            <>
              <div className={`application-builder-form-list ${applicationFormScope === 'department' && !applicationFormDepartment ? 'is-disabled' : ''}`}>
                {PUBLIC_APPLICATION_FIXED_FIELDS.map((field) => (
                  <div key={field.id} className="application-builder-row locked">
                    <div className="application-builder-row-head">
                      <div className="application-builder-row-handle">
                        <i className="fa-solid fa-grip-lines"></i>
                      </div>
                      <div className="application-builder-row-title">
                        <strong>{getFixedApplicationFieldLabel(field.id)}</strong>
                        <span>{getApplicationFieldTypeLabel(field.type)}</span>
                      </div>
                      <div className="application-builder-row-meta">
                        <span className="application-builder-fixed-lock">{t('manageJobs.lockedField', 'Locked')}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {(activeApplicationSettings.fields || []).map((field) => renderEditableApplicationField(field))}
              </div>

              <div className="application-builder-add-inline">
                <div className="application-builder-edit-block">
                  <div className="application-builder-presets">
                    <button type="button" className="application-builder-preset" onClick={() => handleAddApplicationPresetField('coverLetter')}>
                      <i className="fa-solid fa-file-lines"></i>
                      <span>{t('manageJobs.addCoverLetter', 'Add Cover Letter')}</span>
                    </button>
                    <button type="button" className="application-builder-preset" onClick={() => handleAddApplicationPresetField('portfolioLink')}>
                      <i className="fa-solid fa-link"></i>
                      <span>{t('manageJobs.addPortfolioLink', 'Add Portfolio Link')}</span>
                    </button>
                    <button type="button" className="application-builder-preset" onClick={() => handleAddApplicationPresetField('linkedinUrl')}>
                      <i className="fa-brands fa-linkedin"></i>
                      <span>{t('manageJobs.addLinkedInUrl', 'Add LinkedIn URL')}</span>
                    </button>
                  </div>
                </div>

                <div className="application-builder-edit-block">
                  <div className="application-builder-add-inline-grid">
                    <textarea
                      rows={1}
                      value={newApplicationFieldLabel}
                      onChange={(e) => setNewApplicationFieldLabel(e.target.value)}
                      onInput={autoResizeApplicationQuestion}
                      disabled={isApplicationPresetOption(newApplicationFieldType)}
                      placeholder={isApplicationPresetOption(newApplicationFieldType)
                        ? t('manageJobs.presetQuestionLabelManaged', 'Preset label is managed automatically')
                        : t('manageJobs.fieldQuestionPlaceholder', 'Question')}
                    />
                    <select value={newApplicationFieldType} onChange={(e) => handleNewApplicationFieldTypeChange(e.target.value)}>
                      {getApplicationFieldAddOptions().map((option) => (
                        <option key={option.value} value={option.value}>{uiText(option.label)}</option>
                      ))}
                    </select>
                  </div>

                  <div className="application-builder-create-actions">
                    <label>
                      <input
                        type="checkbox"
                        checked={newApplicationFieldRequired}
                        onChange={(e) => setNewApplicationFieldRequired(e.target.checked)}
                        disabled={isApplicationPresetOption(newApplicationFieldType)}
                      />
                      <span>{t('manageJobs.requiredField', 'Required')}</span>
                    </label>
                    <button type="button" className="post-job-cta" onClick={handleAddApplicationField}>
                      <i className="fa-solid fa-plus"></i>
                      <span>{t('actions.add', 'Add')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      </div>
    </div>
  );

  const selectedPendingJobApproval = selectedJob
    ? (
      pendingJobApprovalsByJobId[selectedJob.id] ||
      (selectedJob.approvalStatus === 'pending_approval'
        ? {
          id: selectedJob.approvalId || '',
          type: 'job_posting',
          status: 'pending',
          entityId: selectedJob.id,
          entityName: selectedJob.jobOpening || selectedJob.title || 'Job Post',
          submittedBy: { uid: selectedJob.approvalSubmittedByUid || '' },
          submittedAt: selectedJob.approvalSubmittedAt || 0,
          metadata: {
            department: selectedJob.department || '',
            jobTitle: selectedJob.jobOpening || selectedJob.title || ''
          }
        }
        : null)
    )
    : null;
  const selectedApprovalSubmittedByCurrentUser = isApprovalSubmittedByCurrentUser(selectedPendingJobApproval || {});
  const canReviewSelectedJobApproval = selectedJob?.approvalStatus === 'pending_approval' && canReviewJobApproval(selectedPendingJobApproval || {});
  const canReviewSelectedJobUpdateApproval = selectedJob?.published && selectedJob?.pendingUpdateStatus === 'pending_approval' && canReviewJobApproval(selectedPendingJobApproval || {});
  const canCancelSelectedJobApproval = selectedJob?.approvalStatus === 'pending_approval' && canCancelJobApproval(selectedPendingJobApproval || {});
  const canEditSelectedJob = canEditJobRecord(selectedJob || {});
  const canDeleteSelectedJob = canCreateOrEditJobs && (selectedJob?.approvalStatus !== 'pending_approval' || canCancelSelectedJobApproval);
  const jobDraftApplicationSettings = jobApplicationDraftSettings || buildPublicApplicationSettingsForJob(formData.department || '');

  return (
    <>
      <HomeFeedback dialog={homeDialog} onDismiss={dismissHomeDialog} className="home-page" />
      <div className="home-page" style={{ height: '100vh' }}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        {/* Main Body */}
        <div className="home-body" style={{
          marginInlineStart: sidebarCollapsed ? '60px' : '240px',
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarCollapsed ? '60px' : '240px'})`,
          boxSizing: 'border-box',
          paddingTop: showApplicationFormBuilder ? '30px' : '100px',
          paddingBottom: '0px',
          paddingInlineStart: '40px',
          paddingInlineEnd: sidebarCollapsed ? '100px' : '40px',
          gap: '0',
          overflowY: showApplicationFormBuilder ? 'auto' : 'hidden',
          overflowX: 'hidden',
          display: 'flex',
          height: '100vh'
        }}>
          {showApplicationFormBuilder ? applicationFormBuilderView : (
            <>
          {/* Left: Manage Your Jobs */}
          <div className="manage-section" style={{ width: selectedJob ? 'calc(35% - 40px)' : 'calc(50% - 40px)', paddingInlineEnd: '40px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', transition: 'width 250ms ease' }}>
            <div className="manage-title-row">
              <h2 className="manage-title">{t('manageJobs.title', 'Manage your jobs')}</h2>
            </div>
            <div className="manage-actions-row">
              <button
                type="button"
                className="post-job-cta manage-post-job-cta"
                onClick={openCreateJobModal}
                disabled={!canCreateOrEditJobs}
              >
                <i className="fa-solid fa-circle-plus"></i>
                <span>{t('manageJobs.postJob', 'Post a job')}</span>
              </button>
            </div>
            <div className="job-list" style={{ marginTop: '20px', flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--app-text, #FFFFFF)', padding: '20px' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
                </div>
              ) : visibleJobPosts.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--app-text, #FFFFFF)', padding: '40px' }}>
                  <i className="fa-solid fa-briefcase" style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.5 }}></i>
                  <p style={{ fontSize: '16px', opacity: 0.7 }}>
                    {teamAccess.isScoped
                      ? uiText("No job posts are available in your assigned departments.")
                      : t('manageJobs.noJobs', 'No job posts yet')}
                  </p>
                </div>
              ) : (
                visibleJobPosts.map(job => {
                  const active = !!(selectedJob && selectedJob.id === job.id);
                  return (
                    <div key={job.id} className={`job-cell${active ? ' selected' : ''}`} onClick={() => setSelectedJob(job)}>
                      <span className="job-title" style={{ opacity: 1 }}>{job.jobOpening}</span>
                      <div className="job-cell-right">
                        {renderStatusTag(!!job.published, job.approvalStatus, job.pendingUpdateStatus)}
                        <i className="fa-solid fa-chevron-right" style={{ width: '13px', height: '20px', color: active ? 'var(--app-text, #FFFFFF)' : 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}></i>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Conditional area */}
          <div className="right-section" style={{ width: selectedJob ? 'calc(67% - 40px)' : 'calc(50% - 40px)', paddingInlineStart: '40px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '20px', height: '100%', transition: 'width 250ms ease' }}>
            {!selectedJob && (
              <>
                {/* Notifications */}
                <div className="panel-card">
                  <div className="panel-header notif-header">
                    <span>{t('notifications.title', 'Notifications')}</span>
                    <sup className="notif-badge">{notifs.length}</sup>
                    <button className="see-all" onClick={() => setShowNotifsModal(true)}>
                      {t('notifications.seeAll', 'See all')} →
                    </button>
                  </div>
                  <div className="panel-body">
                    {(notifs.slice(0, 3)).map(n => {
                      const action = getNotificationAction(n);
                      return (
                        <div
                          key={n.id}
                          className="notif-item"
                          onClick={() => action && navigateToNotification(n)}
                          style={{ cursor: action ? 'pointer' : 'default' }}
                        >
                          <i className="fa-solid fa-circle" style={{ fontSize: '6px', color: '#568ded' }}></i>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                            <span>{systemRecordText(n, 'text') || systemRecordText(n, 'title') || systemRecordText(n, 'message') || uiText('Notification')}</span>
                            {formatNotificationDate(n.timestamp) && (
                              <span style={{ fontSize: '11px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>
                                {formatNotificationDate(n.timestamp)}
                              </span>
                            )}
                          </div>
                          {action && (
                            <button
                              type="button"
                              className="see-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToNotification(n);
                              }}
                              style={{ fontSize: '11px' }}
                            >
                              {t('notifications.open', 'Open')} →
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {notifs.length > 3 && (
                      <button
                        className="see-all"
                        onClick={() => setShowNotifsModal(true)}
                        style={{
                          marginTop: '8px',
                          padding: 0,
                          fontSize: '12px',
                          color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.75)',
                          alignSelf: 'flex-start'
                        }}
                      >
                        {notifs.length - 3}{uiText("more...")}</button>
                    )}
                    {notifs.length === 0 && (
                      <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', margin: 0 }}>{t('notifications.none', 'No notifications')}</p>
                    )}
                  </div>
                </div>

                {canAccessAnalytics && (
                  <div className="panel-card">
                    <div className="panel-header">
                      <span>{t('analytics.title', 'Analytics')}</span>
                    </div>
                    <div className="panel-body">
                      <div className="analytics-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button className={`chip ${analyticsMetric === 'views' ? 'chip-active' : ''}`} onClick={() => setAnalyticsMetric('views')}>
                          <i className="fa-solid fa-eye"></i> <span>{t('analytics.views', 'Job Post Views')}</span>
                        </button>
                        <button className={`chip ${analyticsMetric === 'applies' ? 'chip-active' : ''}`} onClick={() => setAnalyticsMetric('applies')}>
                          <i className="fa-solid fa-mouse-pointer"></i> <span>{t('analytics.applies', 'Apply Clicks')}</span>
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
                        <select className="select-chip" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
                          <option value="">{t('analytics.allJobs', 'All jobs')}</option>
                          {visibleMiniAnalyticsJobs.map(j => (
                            <option key={j} value={j}>{j}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ marginTop: '14px' }}>
                        <JobAnalyticsChart
                          data={jobFilter && miniAnalytics.seriesByJob?.[jobFilter] ? miniAnalytics.seriesByJob[jobFilter] : miniAnalytics.series}
                          metric={analyticsMetric}
                          isRTL={isRTL}
                          locale={getUiLocale()}
                          label={t(analyticsMetric === 'views' ? 'analytics.views' : 'analytics.applies')}
                          emptyLabel={uiText('No data')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {selectedJob && (
              <div className="panel-card job-details" style={{
                position: 'relative',
                flex: 1,
                height: 'calc(100vh - 300px)', // Reduced height to account for margin
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '20px',
                marginBottom: '100px'
              }}>
                <div className="panel-header job-details-header">
                  <div className="job-details-title-row">
                    <span className="job-details-title">{selectedJob.jobOpening}</span>
                    {renderStatusTag(!!selectedJob.published, selectedJob.approvalStatus, selectedJob.pendingUpdateStatus)}
                  </div>
                  <button className="close-x" onClick={() => setSelectedJob(null)} title={uiText("Close")}>
                    <i className="fa-regular fa-circle-xmark"></i>
                  </button>
                </div>
                  <div className="job-share-action">
                    <button type="button" className="post-job-cta"
                      disabled={!selectedJob.published || (jobShareState.jobId === selectedJob.id && jobShareState.disabled)}
                      onClick={() => handleShareJob(selectedJob)}>
                      <i className="fa-solid fa-link" aria-hidden="true" />
                      <span aria-live="polite">{jobShareState.jobId === selectedJob.id && jobShareState.text
                        ? jobShareState.text : t('actions.copyJobLink', 'Copy link to job')}</span>
                    </button>
                    {!selectedJob.published && <span className="job-share-hint">{t('actions.publishToCopyLink', 'Publish this job to copy its public link.')}</span>}
                  </div>
                <div className="panel-body" style={{
                  color: 'var(--app-text, #FFFFFF)',
                  display: 'flex',
                  flexDirection: 'column',
                      flex: 1,
                      minHeight: 0,
	                  overflow: 'hidden'
	                }}>
	                  {selectedJob?.published && selectedJob?.pendingUpdateStatus === 'pending_approval' && (
	                    <div style={{
	                      backgroundColor: 'rgba(255, 193, 7, 0.12)',
	                      border: '1px solid rgba(255, 193, 7, 0.35)',
	                      borderRadius: '12px',
	                      padding: '16px',
	                      marginTop: '20px',
	                      marginBottom: '20px'
	                    }}>
	                      <div style={{
	                        display: 'flex',
	                        alignItems: 'flex-start',
	                        justifyContent: 'space-between',
	                        flexWrap: 'wrap',
	                        gap: '12px'
	                      }}>
	                        <div style={{ color: 'var(--app-text, #FFFFFF)', minWidth: '220px', flex: 1 }}>
	                          <div style={{ color: 'var(--app-tone-ffc107, #ffc107)', fontWeight: "var(--app-weight-strong, 700)", fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
	                            <i className="fa-solid fa-clock-rotate-left"></i>{uiText("Job Update Pending Approval")}</div>
		                          <div style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.78)', fontSize: '13px', lineHeight: 1.5 }}>
		                            {canReviewSelectedJobUpdateApproval
		                              ? uiText("Submitted edits are waiting for review. The live job post is unchanged until you approve them.")
		                              : selectedApprovalSubmittedByCurrentUser
		                                ? uiText("Your submitted edits are waiting for review. The live job post is unchanged for now.")
		                                : uiText("Submitted edits are waiting for review. The live job post is unchanged for now.")}
		                          </div>
	                          {selectedJob.pendingUpdatePreview && (
	                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
	                              {selectedJob.pendingUpdatePreview.jobTitle && (
	                                <span className="status-tag" style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))', color: 'var(--app-text, #FFFFFF)', border: "1px solid var(--app-border, rgba(255,255,255,0.16))" }}>
	                                  {selectedJob.pendingUpdatePreview.jobTitle}
	                                </span>
	                              )}
	                              {selectedJob.pendingUpdatePreview.department && (
	                                <span className="status-tag" style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))', color: 'var(--app-text, #FFFFFF)', border: "1px solid var(--app-border, rgba(255,255,255,0.16))" }}>
	                                  {selectedJob.pendingUpdatePreview.department}
	                                </span>
	                              )}
	                              {selectedJob.pendingUpdatePreview.location && (
	                                <span className="status-tag" style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))', color: 'var(--app-text, #FFFFFF)', border: "1px solid var(--app-border, rgba(255,255,255,0.16))" }}>
	                                  {selectedJob.pendingUpdatePreview.location}
	                                </span>
	                              )}
	                            </div>
	                          )}
	                        </div>
		                        {canReviewSelectedJobUpdateApproval ? (
		                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
		                            <button className="post-job-cta" onClick={() => approvePublishedJobUpdate(selectedJob)}>
	                              <i className="fa-solid fa-check"></i>
	                              <span>{uiText("Approve Update")}</span>
	                            </button>
	                            <button className="post-job-cta" onClick={() => rejectPublishedJobUpdate(selectedJob)} style={{ borderColor: '#dc3545', color: '#dc3545' }}>
	                              <i className="fa-solid fa-xmark"></i>
	                              <span>{uiText("Reject Update")}</span>
	                            </button>
	                          </div>
		                        ) : canCreateOrEditJobs && selectedApprovalSubmittedByCurrentUser ? (
		                          <button className="post-job-cta" onClick={() => handleEditJob(selectedJob)}>
	                            <i className="fa-solid fa-pen"></i>
	                            <span>{uiText("Edit Request")}</span>
	                          </button>
	                        ) : null}
	                      </div>
	                    </div>
	                  )}
	                  {/* Pending Approval UI for Approvers */}
		                  {selectedJob?.approvalStatus === 'pending_approval' && canReviewSelectedJobApproval ? (
                    <div style={{
                      backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
                      border: "1px solid var(--app-border, rgba(86, 141, 237, 0.25))",
                      borderRadius: '12px',
                      padding: '20px',
                      marginTop: '20px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '15px'
                      }}>
                        <span style={{
                          color: '#568ded',
                          fontWeight: 600,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <i className="fa-solid fa-clock"></i>{uiText("Job Pending Approval - Ready to Publish?")}</span>
		                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          {canEditSelectedJob && (
                            <button
                              onClick={() => handleEditJob(selectedJob)}
                              style={{
                                backgroundColor: 'transparent',
                                color: 'var(--app-text, #FFFFFF)',
                                border: "1px solid var(--app-border-strong, rgba(255,255,255,0.28))",
                                borderRadius: '8px',
                                padding: '8px 16px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px'
                              }}
                              data-light-control={"quiet"}>
                              <i className="fa-solid fa-pen-to-square"></i>{uiText(" Edit")}</button>
                          )}
                          <button
                            onClick={() => togglePublish(selectedJob)}
                            style={{ '--light-control-fill': '#568ded',
                              backgroundColor: '#568ded',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px'
                            }}
	                            data-light-control={"filled"}>
	                            <i className="fa-solid fa-check"></i>{uiText(" Publish")}</button>
	                          <button
	                            onClick={() => rejectJobApproval(selectedJob)}
	                            style={{
	                              backgroundColor: 'transparent',
	                              color: 'var(--app-tone-dc3545, #dc3545)',
	                              border: '1px solid rgba(220, 53, 69, 0.5)',
	                              borderRadius: '8px',
	                              padding: '8px 16px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '13px'
	                            }}
	                            data-light-control={"quiet-danger"}>
	                            <i className="fa-solid fa-xmark"></i>{uiText(" Reject")}</button>
	                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="details-actions">
	                      {canEditSelectedJob && (
	                        <button className="post-job-cta" onClick={() => handleEditJob(selectedJob)}><i className="fa-solid fa-pen-to-square"></i><span>{t('actions.edit', 'Edit')}</span></button>
	                      )}
                      {selectedJob?.published ? (
                        canCreateOrEditJobs && <button className="post-job-cta" onClick={() => togglePublish(selectedJob)}><i className="fa-solid fa-eye-slash"></i><span>{t('actions.unpublish', 'Unpublish')}</span></button>
	                      ) : selectedJob?.approvalStatus === 'pending_approval' ? (
	                        canCancelSelectedJobApproval && <button className="post-job-cta" onClick={() => cancelApprovalRequest(selectedJob)} style={{ borderColor: '#ffc107', color: '#ffc107' }}><i className="fa-solid fa-xmark"></i><span>{t('actions.cancelRequest', 'Cancel Request')}</span></button>
                      ) : requiresJobApproval ? (
                        canCreateOrEditJobs && <button className="post-job-cta" onClick={() => togglePublish(selectedJob)}><i className="fa-solid fa-paper-plane"></i><span>{t('actions.submitForApproval', 'Submit for Approval')}</span></button>
                      ) : (
                        canCreateOrEditJobs && <button className="post-job-cta" onClick={() => togglePublish(selectedJob)}><i className="fa-solid fa-eye"></i><span>{t('actions.publish', 'Publish')}</span></button>
                      )}
	                      {canDeleteSelectedJob && <button className="post-job-cta" onClick={() => handleDeleteJob(selectedJob.id)}><i className="fa-solid fa-trash-can"></i><span>{t('actions.delete', 'Delete')}</span></button>}
                      {/* <button className="post-job-cta"><i className="fa-solid fa-wand-magic-sparkles"></i><span>Smart Invite</span></button> */}
                      {canAccessAnalytics && <button className="post-job-cta" onClick={() => handleSeeAnalytics(selectedJob)}><i className="fa-solid fa-chart-line"></i><span>{t('actions.jobAnalytics', 'Job Analytics')}</span></button>}
                    </div>
                  )}
                  <a href="#" style={{
                    color: '#568DED !important',
                    textDecoration: 'underline',
                    marginBottom: '20px',
                    display: 'none', // 'inline-block',
                    flexShrink: 0
                  }}>{uiText("Having trouble filling this role?")}</a>
                  <div className="job-details-scroll" style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingRight: '4px',
                    paddingBottom: '20px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#568ded #F0F0F0'
                  }}>
                    {!showJobAnalytics ? (
                      <>
                        <p style={{ margin: 0, opacity: 1 }}>{selectedJob.companyLocation || `${companyData.city}, ${companyData.country}`}</p>
                        <p style={{ marginTop: '6px' }}>{t('salaryLabel', 'Salary')} - {selectedJob.salary}{selectedJob.negotiability ? ` (${translateValue(selectedJob.negotiability, 'negotiability', isRTL ? 'ar' : 'en')})` : ''}</p>
                        <div className="details-tag-row">
                          <span className="pill"><i className="fa-solid fa-clock"></i> {translateValue(selectedJob.schedule, 'schedule', isRTL ? 'ar' : 'en')}</span>
                          <span className="pill"><i className="fa-solid fa-laptop"></i> {translateValue(selectedJob.workspace, 'workMode', isRTL ? 'ar' : 'en')}</span>
                          <span className="pill"><i className="fa-solid fa-folder"></i> {getLocalizedFromMultilang(selectedJob.jobFunction, isRTL ? 'ar' : 'en')}</span>
                          {selectedJob.department && <span className="pill"><i className="fa-solid fa-sitemap"></i> {translateDepartment(selectedJob.department, isRTL ? 'ar' : 'en')}</span>}
                          {selectedJob.seniority && <span className="pill"><i className="fa-solid fa-user-tie"></i> {translateValue(selectedJob.seniority, 'seniority', isRTL ? 'ar' : 'en')}</span>}
                          {selectedJob.experienceYears && <span className="pill"><i className="fa-solid fa-briefcase"></i> {selectedJob.experienceYears}+ {isRTL ? 'سنوات' : 'yrs'}</span>}
                        </div>
                        {(Array.isArray(selectedJob.tagGroups) && selectedJob.tagGroups.length > 0) && (
                          <div className="job-taxonomy-section">
                            <h3 style={{ marginTop: 20, color: 'var(--app-text, #FFFFFF)', marginBottom: 10 }}>{t('formLabels.tagGroups', 'Tag Groups')}</h3>
                            <div className="job-taxonomy-group-list">
                              {selectedJob.tagGroups.map((group) => (
                                <div key={group.key || group.label} className="job-taxonomy-group-card">
                                  <div className="job-taxonomy-group-title">
                                    <i className="fa-solid fa-layer-group"></i>
                                    <span>{uiText(group.label)}</span>
                                  </div>
                                  <div className="job-taxonomy-chip-list">
                                    {(group.tags || []).map((tag) => (
                                      <span key={`${group.key || group.label}-${tag.key || tag.label}`} className="job-taxonomy-chip">
                                        {uiText(tag.label)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(Array.isArray(selectedJob.ungroupedTags) && selectedJob.ungroupedTags.length > 0) && (
                          <div className="job-taxonomy-section">
                            <h3 style={{ marginTop: 20, color: 'var(--app-text, #FFFFFF)', marginBottom: 10 }}>{t('formLabels.tags', 'Tags')}</h3>
                            <div className="job-taxonomy-chip-list">
                              {selectedJob.ungroupedTags.map((tag) => (
                                <span key={tag.key || tag.label} className="job-taxonomy-chip">
                                  {uiText(tag.label)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <h3 style={{ marginTop: 20, color: 'var(--app-text, #FFFFFF)', marginBottom: 10 }}>{t('description', 'Description')}</h3>
                        <div className="job-desc-html" style={{ color: 'var(--app-text, #FFFFFF)' }} dangerouslySetInnerHTML={{ __html: selectedJob.jobDescriptionHTML || `<p>${selectedJob.jobDescription || ''}</p>` }}></div>
                      </>
                    ) : (
                      <div style={{ minHeight: '200px' }}>
                        <button className="back-to-details" onClick={() => setShowJobAnalytics(false)}>
                          {isRTL ? '→' : '←'} {t('actions.backToDetails', 'Back to job details')}
                        </button>
                        <h3 style={{ margin: 0, color: 'var(--app-text, #FFFFFF)' }}>{t('actions.jobAnalytics', 'Job Analytics')}</h3>
                        {/* Metric toggle */}
                        <div style={{ display: 'flex', gap: '8px', margin: '8px 0 12px' }}>
                          <button className={`chip ${jobAnalyticsMetric === 'views' ? 'chip-active' : ''}`} onClick={() => setJobAnalyticsMetric('views')}>
                            <i className="fa-solid fa-eye"></i> <span>{t('actions.views', 'Views')}</span>
                          </button>
                          <button className={`chip ${jobAnalyticsMetric === 'applies' ? 'chip-active' : ''}`} onClick={() => setJobAnalyticsMetric('applies')}>
                            <i className="fa-solid fa-mouse-pointer"></i> <span>{t('actions.applyClicks', 'Apply Clicks')}</span>
                          </button>
                        </div>
                        {/* Inline chart */}
                        {renderJobAnalyticsChart()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
            </>
          )}
        </div>

        {/* Job Posting Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-content job-posting-modal job-posting-modal-page-one job-posting-modal-tabbed"
              onClick={(e) => e.stopPropagation()}
            >
              {jobPostingPhase && (
                <div className="job-posting-progress" role="status" aria-live="polite">
                  <span className="job-posting-spinner" aria-hidden="true" />
                  <strong>{jobPostingPhase === 'publishing'
                    ? t('formLabels.publishingJob', 'Publishing your job…')
                    : t('formLabels.savingJob', 'Saving your job…')}</strong>
                  <span>{t('formLabels.postingWait', 'Please wait for confirmation.')}</span>
                </div>
              )}
              <fieldset className="job-posting-fields" disabled={Boolean(jobPostingPhase)} aria-busy={Boolean(jobPostingPhase)}>
              <div className="modal-header">
                <h2>{editingMode ? t('editJob', 'Edit Job') : t('postJob', 'Post a Job')}</h2>
                <div className="close" onClick={closeModal}>&times;</div>
              </div>
              <div className="job-modal-tabs" role="tablist" aria-label={t('formLabels.jobModalTabs', 'Job post sections')}>
                {[
                  { page: 1, icon: 'fa-briefcase', label: t('formLabels.jobDetailsTab', 'Job details'), required: true },
                  { page: 2, icon: 'fa-align-left', label: t('formLabels.jobDescriptionTab', 'Job description'), required: true },
                  ...(usesHostedApplications ? [{ page: 3, icon: 'fa-file-lines', label: t('formLabels.applicationFormTab', 'Application form'), required: false }] : [])
                ].map((tab) => (
                  <button
                    key={tab.page}
                    type="button"
                    role="tab"
                    aria-selected={modalPage === tab.page}
                    aria-invalid={isJobModalStepMissing(tab.page)}
                    className={`job-modal-tab ${modalPage === tab.page ? 'active' : ''} ${isJobModalStepMissing(tab.page) ? 'has-error' : ''}`}
                    onClick={() => setModalPage(tab.page)}
                  >
                    <i className={`fa-solid ${tab.icon}`}></i>
                    <span>
                      {uiText(tab.label)}
                      {tab.required && <span className="job-modal-required-star job-modal-tab-required-star" aria-hidden="true">*</span>}
                    </span>
                  </button>
                ))}
              </div>

              {/* Page 1 */}
              {modalPage === 1 && (
                <div className="modal-page-container">
                  <div className="form-section job-posting-page-one">
                    <section className="job-modal-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-briefcase"></i>
                        <div>
                          <h3>{t('formLabels.roleOwnership', 'Role ownership')}</h3>
                          <span>{t('formLabels.roleOwnershipHint', 'Name the opening and assign the internal owner.')}</span>
                        </div>
                      </div>

                      <div className="job-modal-field-grid">
                        <div className="job-modal-field job-modal-field-full">
                          <label htmlFor="job-opening-input">{renderRequiredLabel(t('formLabels.jobOpening', 'Job Opening'))}</label>
                          <input
                            id="job-opening-input"
                            type="text"
                            className={isJobFieldMissing('jobOpening') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('jobOpening')}
                            placeholder={t('formLabels.jobOpeningPlaceholder', 'e.g., Senior Product Designer')}
                            value={formData.jobOpening}
                            onChange={(e) => handleFormChange('jobOpening', e.target.value)}
                          />
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-recruiter-select">{renderRequiredLabel(t('formLabels.recruiterOwner', 'Recruiter owner'))}</label>
                          <select
                            id="job-recruiter-select"
                            className={isJobFieldMissing('ownerRecruiterId') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('ownerRecruiterId')}
                            required
                            value={formData.ownerRecruiterId}
                            onChange={(e) => handleFormChange('ownerRecruiterId', e.target.value)}
                          >
                            <option value="">{t('formLabels.selectRecruiter', 'Assign recruiter responsible')}</option>
                            {recruiterOptions.map((recruiter) => (
                              <option key={recruiter.id} value={recruiter.id}>
                                {recruiter.name}{recruiter.email && recruiter.email !== recruiter.name ? ` (${recruiter.email})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="job-modal-field">
                          <label htmlFor="job-category-select">{renderRequiredLabel(t('formLabels.jobCategory', 'Job category'))}</label>
                          <select
                            id="job-category-select"
                            className={isJobFieldMissing('jobCategory') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('jobCategory')}
                            required
                            value={formData.jobCategory}
                            onChange={(e) => handleFormChange('jobCategory', e.target.value)}
                          >
                            <option value="">{t('formLabels.selectCategory', 'Select Job Category')}</option>
                            {jobCategories.map((category, index) => (
                              <option key={index} value={category}>{getLocalizedFromMultilang(category, isRTL ? 'ar' : 'en')}</option>
                            ))}
                          </select>
                        </div>

                        {agencyJobPostingEnabled && (
                          <div className="job-modal-field job-modal-field-full">
                            <label htmlFor="job-posting-company-input">{t('formLabels.postingCompanyName', 'Client company name')}</label>
                            <input
                              id="job-posting-company-input"
                              type="text"
                              maxLength={120}
                              placeholder={companyData.companyName || t('formLabels.postingCompanyPlaceholder', 'Your workspace company')}
                              value={formData.postingCompanyName}
                              onChange={(e) => handleFormChange('postingCompanyName', e.target.value)}
                            />
                            <p className="job-modal-field-hint">{t('formLabels.postingCompanyHint', 'Leave blank to post under your workspace company name.')}</p>
                            <label htmlFor="job-linkedin-company-id">{t('formLabels.jobLinkedInCompanyId', 'Client LinkedIn company ID (optional)')}</label>
                            <input
                              id="job-linkedin-company-id"
                              type="text"
                              value={formData.linkedinCompanyId || ''}
                              onChange={(e) => handleFormChange('linkedinCompanyId', e.target.value)}
                              placeholder="12345678"
                              aria-describedby="job-linkedin-company-hint"
                            />
                            <p id="job-linkedin-company-hint" className="job-modal-field-hint">{t('formLabels.jobLinkedInCompanyHint', 'Use the client’s numeric LinkedIn company ID or company admin URL. This applies only to this job. Client jobs need this ID to appear under the client’s LinkedIn page.')}</p>
                          </div>
                        )}
                      </div>
                    </section>


                    <section className="job-modal-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-money-check-dollar"></i>
                        <div>
                          <h3>{t('formLabels.compensationTitle', 'Compensation')}</h3>
                          <span>{t('formLabels.compensationHint', 'Candidate-facing pay details for the post.')}</span>
                        </div>
                      </div>

                      <div className="job-modal-field-grid">
                        <div className="job-modal-field">
                          <label htmlFor="job-salary-input">{renderRequiredLabel(t('formLabels.salary', 'Salary'))}</label>
                          <input
                            id="job-salary-input"
                            type="text"
                            className={isJobFieldMissing('salary') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('salary')}
                            placeholder={t('formLabels.salaryPlaceholder', 'e.g., $80,000 - $120,000 USD')}
                            value={formData.salary}
                            onChange={(e) => handleFormChange('salary', e.target.value)}
                          />
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-negotiability-select">{renderRequiredLabel(t('formLabels.negotiability', 'Negotiability'))}</label>
                          <select
                            id="job-negotiability-select"
                            className={isJobFieldMissing('negotiability') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('negotiability')}
                            required
                            value={formData.negotiability}
                            onChange={(e) => handleFormChange('negotiability', e.target.value)}
                          >
                            <option value="Negotiable">{t('formLabels.negotiable', 'Negotiable')}</option>
                            <option value="Non-Negotiable">{t('formLabels.nonNegotiable', 'Non-Negotiable')}</option>
                          </select>
                        </div>
                      </div>
                    </section>


                    <section className="job-modal-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-laptop-house"></i>
                        <div>
                          <h3>{t('formLabels.workSetupTitle', 'Work setup')}</h3>
                          <span>{t('formLabels.workSetupHint', 'Schedule and workplace expectations.')}</span>
                        </div>
                      </div>

                      <div className="job-modal-field-grid">
                        <div className="job-modal-field">
                          <label htmlFor="job-schedule-select">{renderRequiredLabel(t('formLabels.schedule', 'Schedule'))}</label>
                          <select
                            id="job-schedule-select"
                            className={isJobFieldMissing('mode1') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('mode1')}
                            required
                            value={formData.mode1}
                            onChange={(e) => handleFormChange('mode1', e.target.value)}
                          >
                            <option value="Full-Time">{t('formLabels.fullTime', 'Full-Time')}</option>
                            <option value="Part-Time">{t('formLabels.partTime', 'Part-Time')}</option>
                            <option value="Internship">{t('formLabels.internship', 'Internship')}</option>
                            <option value="Contract">{t('formLabels.contract', 'Contract')}</option>
                          </select>
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-workspace-select">{renderRequiredLabel(t('formLabels.workspace', 'Workspace'))}</label>
                          <select
                            id="job-workspace-select"
                            className={isJobFieldMissing('mode2') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('mode2')}
                            required
                            value={formData.mode2}
                            onChange={(e) => handleFormChange('mode2', e.target.value)}
                          >
                            <option value="In-Person">{t('formLabels.inPerson', 'In-Person')}</option>
                            <option value="Remote">{t('formLabels.remote', 'Remote')}</option>
                            <option value="Hybrid">{t('formLabels.hybrid', 'Hybrid')}</option>
                          </select>
                        </div>
                      </div>
                    </section>

                    <section className="job-modal-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-location-dot"></i>
                        <div>
                          <h3>{t('formLabels.jobLocation', 'Job Location')}</h3>
                          <span>
                            {t('formLabels.jobLocationHint', 'Leave blank to use company headquarters')}
                            {companyData.city || companyData.country ? ` (${companyData.city}${companyData.city && companyData.country ? ', ' : ''}${companyData.country})` : ''}
                          </span>
                        </div>
                      </div>

                      <div className="job-modal-field-grid">
                        <div className="job-modal-field">
                          <label htmlFor="job-city-input">{t('formLabels.city', 'City')}</label>
                          <input
                            id="job-city-input"
                            type="text"
                            placeholder={t('formLabels.city', 'City')}
                            value={formData.jobCity}
                            onChange={(e) => handleFormChange('jobCity', e.target.value)}
                          />
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-country-select">{t('formLabels.country', 'Country')}</label>
                          <select
                            id="job-country-select"
                            value={formData.jobCountry}
                            onChange={(e) => handleFormChange('jobCountry', e.target.value)}
                          >
                            <option value="">{t('formLabels.selectCountry', 'Select Country')}</option>
                            {countries.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </section>


                    {!usesHostedApplications && (
                      <section className="job-modal-section job-application-destination">
                        <div className="job-destination-header">
                          <label htmlFor="job-application-destination">
                            {renderRequiredLabel(currentAppType === 'whatsapp'
                              ? t('formLabels.whatsAppNumber', 'WhatsApp number with country code')
                              : t('formLabels.applicationDestination', 'Application destination'))}
                          </label>
                          <div className="job-destination-options" role="group" aria-label={t('formLabels.applicationDestinationType', 'Destination type')}>
                            {['link', 'email', 'whatsapp'].map((type) => (
                              <button
                                key={type}
                                type="button"
                                className="job-destination-option"
                                aria-pressed={currentAppType === type}
                                onClick={() => updateAppLinkInput(type)}
                              >
                                {type === 'link'
                                  ? t('formLabels.destinationLink', 'Link')
                                  : type === 'email'
                                    ? t('formLabels.destinationEmail', 'Email')
                                    : t('formLabels.destinationWhatsApp', 'WhatsApp')}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="input-group">
                          <input
                            type={currentAppType === 'whatsapp' ? 'tel' : 'text'}
                            inputMode={currentAppType === 'whatsapp' ? 'tel' : undefined}
                            id="job-application-destination"
                            className={isJobFieldMissing('appWindow') ? 'job-modal-field-error' : ''}
                            aria-invalid={isJobFieldMissing('appWindow')}
                            placeholder={currentAppType === 'whatsapp'
                              ? t('formLabels.whatsAppNumberPlaceholder', 'e.g. +961 70 123 456')
                              : currentAppType === 'email'
                                ? t('formLabels.emailForApplications', 'Email for Applications')
                                : t('formLabels.linkToApplication', 'Link to Application Window')}
                            value={formData.appWindow}
                            onChange={(e) => handleFormChange('appWindow', e.target.value)}
                          />
                        </div>
                        {currentAppType === 'whatsapp' && (
                          <p className="job-application-method-hint">{t('formLabels.whatsAppDestinationHint', 'Candidates will open a WhatsApp chat with this number.')}</p>
                        )}
                      </section>
                    )}

                    <details className="job-modal-optional">
                      <summary>
                        <strong>{t('formLabels.optionalDetails', 'Hiring & analytics details')}</strong>
                        <span>{t('formLabels.optionalDetailsHint', 'Optional · Department and seniority fill from matching category and title. Review or edit here.')}</span>
                      </summary>
                      <div className="job-modal-field-grid">
                        <div className="job-modal-field">
                          <label htmlFor="job-department-select">{t('formLabels.department', 'Department')}</label>
                          <select
                            id="job-department-select"
                            value={formData.department}
                            onChange={(e) => handleFormChange('department', e.target.value)}
                          >
                            <option value="">{t('formLabels.selectDepartment', 'Select Department')}</option>
                            {departments.map((d) => (
                              <option key={d} value={d}>{translateDepartment(d, isRTL ? 'ar' : 'en')}</option>
                            ))}
                          </select>
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-seniority-select">{t('formLabels.seniority', 'Seniority')}</label>
                          <select
                            id="job-seniority-select"
                            value={formData.seniority}
                            onChange={(e) => handleFormChange('seniority', e.target.value)}
                          >
                            <option value="">{t('formLabels.selectSeniority', 'Select Seniority')}</option>
                            <option value="Intern">{t('seniorityOptions.intern', 'Intern')}</option>
                            <option value="Junior">{t('seniorityOptions.junior', 'Junior')}</option>
                            <option value="Mid">{t('seniorityOptions.mid', 'Mid')}</option>
                            <option value="Senior">{t('seniorityOptions.senior', 'Senior')}</option>
                            <option value="Lead">{t('seniorityOptions.lead', 'Lead')}</option>
                            <option value="Manager">{t('seniorityOptions.manager', 'Manager')}</option>
                            <option value="Director">{t('seniorityOptions.director', 'Director')}</option>
                            <option value="VP">{t('seniorityOptions.vp', 'VP')}</option>
                            <option value="C-Level">{t('seniorityOptions.cLevel', 'C-Level')}</option>
                          </select>
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-experience-input">{t('formLabels.requiredExperience', 'Required Years of Experience')}</label>
                          <input
                            id="job-experience-input"
                            type="text"
                            inputMode="numeric"
                            placeholder={t('formLabels.requiredExperiencePlaceholder', 'e.g., 5')}
                            value={formData.experienceYears}
                            onChange={(e) => handleFormChange('experienceYears', e.target.value.replace(/[^0-9]/g, ''))}
                          />
                        </div>
                      </div>
                    <section className="job-modal-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-chart-line"></i>
                        <div>
                          <h3>{t('formLabels.hiringPlan', 'Hiring Plan')}</h3>
                          <span>{t('formLabels.hiringPlanHint', 'Internal only. This date feeds vacancy forecasting and is not shown on the public job post.')}</span>
                        </div>
                      </div>

                      <div className="job-modal-field-grid">
                        <div className="job-modal-field">
                          <label htmlFor="target-hire-date">{t('formLabels.targetHireDate', 'Target hire date')}</label>
                          <input
                            id="target-hire-date"
                            type="date"
                            value={formData.targetHireDate}
                            onChange={(e) => handleFormChange('targetHireDate', e.target.value)}
                          />
                        </div>
                      </div>
                    </section>                    <section className="job-modal-section job-modal-taxonomy-section">
                      <div className="job-modal-section-title">
                        <i className="fa-solid fa-tags"></i>
                        <div>
                          <h3>{t('formLabels.analyticsTags', 'Analytics Tags')}</h3>
                          <span>{t('formLabels.analyticsTagsHint', 'Add a tag, and optionally place it in an existing group or create a new one. Analytics dashboards will segment jobs by these exact values.')}</span>
                        </div>
                      </div>

                      <div className="job-modal-taxonomy-grid">
                        <div className="job-modal-field">
                          <label htmlFor="job-tag-group-select">{t('formLabels.tagGroup', 'Tag group')}</label>
                          <select
                            id="job-tag-group-select"
                            value={selectedTagGroupKey}
                            onChange={(e) => setSelectedTagGroupKey(e.target.value)}
                          >
                            <option value={UNGROUPED_JOB_TAG_KEY}>{t('formLabels.noGroupOptional', 'No group (optional)')}</option>
                            {jobTagGroups.map((group) => (
                              <option key={group.key} value={group.key}>{uiText(group.label)}</option>
                            ))}
                          </select>
                        </div>

                        <div className="job-modal-field">
                          <label htmlFor="job-tag-group-input">{t('formLabels.newGroup', 'New group')}</label>
                          <input
                            id="job-tag-group-input"
                            type="text"
                            placeholder={t('formLabels.orCreateGroup', 'Or create a new group')}
                            value={tagGroupInput}
                            onChange={(e) => setTagGroupInput(e.target.value)}
                          />
                        </div>

                        <div className="job-modal-field job-modal-tag-input">
                          <label htmlFor="job-tag-input">{t('formLabels.tag', 'Tag')}</label>
                          <input
                            id="job-tag-input"
                            type="text"
                            placeholder={t('formLabels.addTag', 'Add tag')}
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addJobTag();
                              }
                            }}
                          />
                        </div>

                        <button type="button" className="post-job-cta job-modal-add-tag-button" onClick={addJobTag}>
                          <i className="fa-solid fa-plus"></i>
                          {t('formLabels.addTagAction', 'Add Tag')}
                        </button>
                      </div>

                      {(jobTagGroups.length > 0 || jobUngroupedTags.length > 0) ? (
                        <div className="job-taxonomy-group-list">
                          {jobTagGroups.map((group) => (
                            <div key={group.key} className="job-taxonomy-group-card light">
                              <div className="job-taxonomy-group-title">
                                <span>{uiText(group.label)}</span>
                                <button type="button" className="job-taxonomy-remove-button" onClick={() => removeTagGroup(group.key)}>
                                  <i className="fa-solid fa-xmark"></i>
                                </button>
                              </div>
                              <div className="job-taxonomy-chip-list">
                                {(group.tags || []).map((tag) => (
                                  <button
                                    key={`${group.key}-${tag.key}`}
                                    type="button"
                                    className="job-taxonomy-chip removable"
                                    onClick={() => removeJobTag(tag.key, group.key)}
                                  >
                                    <span>{uiText(tag.label)}</span>
                                    <i className="fa-solid fa-xmark"></i>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}

                          {jobUngroupedTags.length > 0 && (
                            <div className="job-taxonomy-group-card light">
                              <div className="job-taxonomy-group-title">
                                <span>{t('formLabels.ungroupedTags', 'Ungrouped tags')}</span>
                              </div>
                              <div className="job-taxonomy-chip-list">
                                {jobUngroupedTags.map((tag) => (
                                  <button
                                    key={tag.key}
                                    type="button"
                                    className="job-taxonomy-chip removable"
                                    onClick={() => removeJobTag(tag.key)}
                                  >
                                    <span>{uiText(tag.label)}</span>
                                    <i className="fa-solid fa-xmark"></i>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="job-taxonomy-helper empty">
                          {t('formLabels.noAnalyticsTags', 'No tag groups or tags added yet.')}
                        </p>
                      )}
                    </section>                    </details>
                    {/* Status selection removed; publish/unpublish handled in details */}
                  </div>

                  <div className="modal-footer">
                    <button
                      onClick={handleModalNext}
                      className="btn-primary"
                    >
                      {t('formLabels.nextJobDescription', 'Next: Job Description')}
                    </button>
                  </div>
                </div>
              )}

              {/* Page 2 */}
              {modalPage === 2 && (
                <div className="modal-page-container job-description-modal-page">
                  <div className="form-section job-description-form-section">
                    <div className="quill-container">
                      <div className="quill-header">
                        <h4>{renderRequiredLabel(t('formLabels.jobDescription', 'Job Description'))}</h4>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            className="expand-btn"
                            onClick={() => handleAIGenerate('regular')}
                            title={t('formLabels.generateDescriptionWithAI', 'Generate description with AI')}
                            disabled={aiGenerating}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 'fit-content' }}
                          >
                            {aiGenerating ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin"></i>
                                <span>{t('formLabels.generating', 'Generating...')}</span>
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-wand-magic-sparkles"></i>
                                <span>{t('formLabels.generate', 'Generate')}</span>
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="expand-btn"
                            onClick={openFullscreenEditor}
                            title={uiText("Expand to fullscreen")}
                          >
                            <i className="fa-solid fa-expand"></i>
                          </button>
                        </div>
                      </div>
                      <div
                        ref={descriptionRef}
                        id="description"
                        className={`quill-editor ${isJobFieldMissing('jobDescription') ? 'job-modal-field-error' : ''}`}
                        aria-invalid={isJobFieldMissing('jobDescription')}
                      ></div>
                    </div>
                  </div>

                  {!usesHostedApplications && linkedInCompanyPageChecked && !hasLinkedInCompanyPage && !parseLinkedInCompanyId(formData.linkedinCompanyId) && (
                    <LinkedInSetupBanner hasAtsAccess={hasAtsApplications} onConnect={() => setShowLinkedInPrompt(true)} />
                  )}

                  <div className="modal-footer">
                    <button
                      onClick={handleModalPrev}
                      className="btn-secondary"
                    >
                      {t('formLabels.previous', 'Previous')}
                    </button>
                    <button
                      type="button"
                      onClick={usesHostedApplications ? handleModalNext : handleJobSubmit}
                      disabled={accessLoading}
                      className="btn-primary"
                    >
                      {usesHostedApplications
                        ? t('formLabels.nextApplicationForm', 'Next: Application Form')
                        : editingMode
                          ? t('formLabels.saveChanges', 'Save Changes')
                          : requiresJobApproval
                            ? t('formLabels.sendForApproval', 'Send for Approval')
                            : t('postJob', 'Post Job')}
                    </button>
                  </div>
                </div>
              )}

              {/* Page 3 */}
              {modalPage === 3 && usesHostedApplications && (
                <div className="modal-page-container">
                  <div className="form-section job-application-tab">
                    <div className="job-application-preview-block">
                      <div className="job-application-preview-heading">
                        <div>
                          <h3>{t('formLabels.candidatePreviewTitle', 'Candidate preview')}</h3>
                          <span>{t('formLabels.candidatePreviewHint', 'Preview of the form applicants will see.')}</span>
                        </div>
                      </div>
                      <div className="application-form-preview-surface light job-modal-application-preview">
                        {PUBLIC_APPLICATION_FIXED_FIELDS.map((field) => renderApplicationPreviewControl(field, { isFixed: true, hideBadges: true }))}
                        {(jobDraftApplicationSettings.fields || []).map((field) => renderApplicationPreviewControl(field, {
                          hideBadges: true,
                          onRemove: isCompanyOwner ? () => handleRemoveQuickApplicationField(field.id) : null
                        }))}
                      </div>
                    </div>

                    <section className="job-application-question-manager">
                      <div className="job-application-question-manager-header">
                        <div>
                          <h3>{t('formLabels.addQuestionTitle', 'Add question')}</h3>
                          <span>{t('formLabels.addQuestionHint', 'Add one job-specific question or preset field to this form.')}</span>
                        </div>
                      </div>

                      {isCompanyOwner && (
                        <div className="job-application-add-cell">
                          <textarea
                            rows={1}
                            value={quickApplicationFieldLabel}
                            onChange={(e) => setQuickApplicationFieldLabel(e.target.value)}
                            disabled={isApplicationPresetOption(quickApplicationFieldType)}
                            placeholder={isApplicationPresetOption(quickApplicationFieldType)
                              ? t('manageJobs.presetQuestionLabelManaged', 'Preset label is managed automatically')
                              : t('manageJobs.fieldQuestionPlaceholder', 'Type question here')}
                          />
                          <select value={quickApplicationFieldType} onChange={(e) => handleQuickApplicationFieldTypeChange(e.target.value)}>
                            {getApplicationFieldAddOptions().map((option) => (
                              <option key={option.value} value={option.value}>{uiText(option.label)}</option>
                            ))}
                          </select>
                          <label>
                            <input
                              type="checkbox"
                              checked={quickApplicationFieldRequired}
                              onChange={(e) => setQuickApplicationFieldRequired(e.target.checked)}
                              disabled={isApplicationPresetOption(quickApplicationFieldType)}
                            />
                            <span>{t('manageJobs.requiredField', 'Required')}</span>
                          </label>
                          <button type="button" className="post-job-cta" onClick={handleAddQuickApplicationField}>
                            <i className="fa-solid fa-plus"></i>
                            <span>{t('actions.add', 'Add')}</span>
                          </button>
                        </div>
                      )}
                    </section>



                  </div>

                  {linkedInCompanyPageChecked && !hasLinkedInCompanyPage && !parseLinkedInCompanyId(formData.linkedinCompanyId) && (
                    <LinkedInSetupBanner hasAtsAccess={hasAtsApplications} onConnect={() => setShowLinkedInPrompt(true)} />
                  )}

                  <div className="modal-footer">
                    <button
                      onClick={handleModalPrev}
                      className="btn-secondary"
                    >
                      {t('formLabels.previous', 'Previous')}
                    </button>
                    <button
                      type="button"
                      onClick={handleJobSubmit}
                      className="btn-primary"
                    >
                      {editingMode
                        ? t('formLabels.saveChanges', 'Save Changes')
                        : requiresJobApproval
                          ? t('formLabels.sendForApproval', 'Send for Approval')
                          : t('postJob', 'Post Job')
                      }
                    </button>
                  </div>
                </div>
              )}
              </fieldset>
            </div>
          </div>
        )}

        {showLinkedInPrompt && (
          <LinkedInSetupModal
            companyUID={companyUID}
            companyId={userLiId || ''}
            hasAtsAccess={hasAtsApplications}
            onClose={() => setShowLinkedInPrompt(false)}
            onSaved={(id) => {
              setUserLiId(id);
              setShowLinkedInPrompt(false);
            }}
          />
        )}

        {/* LinkedIn Integration Popup */}
        {showLIPopup && (
          <div className="li-int-popup" style={{ display: 'block' }}>
            <div className="popup-content">
              <span onClick={() => setShowLIPopup(false)} className="close-button">
                <i className="fa-solid fa-x"></i>
              </span>

              <div className="job-posts-header" style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center'
              }}>
                <i className="fa-solid fa-bolt-lightning" style={{ color: '#568ded' }}></i>
                <p style={{
                  fontFamily: 'Montserrat',
                  color: "var(--app-neutral-ink, #000000)",
                  fontSize: '30px',
                  marginRight: '2px',
                  marginLeft: '10px'
                }}>{uiText("Job Board Integration - LinkedIn")}</p>
                <p style={{
                  fontSize: '15px',
                  fontFamily: 'Montserrat',
                  color: '#568ded',
                  fontWeight: "var(--app-weight-strong, bold)",
                  marginTop: '-10px'
                }}>{uiText("new")}</p>
              </div>

              <p>{uiText("Already posting jobs on LinkedIn? Effortlessly import your LinkedIn job listings directly onto our job search platform with just a few clicks - start by adding your company page's LinkedIn URL below:")}</p>

              <input
                placeholder={uiText("Input Company LinkedIn URL Here...")}
                value={liInputValue}
                onChange={(e) => setLiInputValue(e.target.value)}
                style={{
                  backgroundColor: '#F0F0F0',
                  border: 'none',
                  marginTop: '15px',
                  marginBottom: '15px',
                  width: '60%',
                  padding: '10px 15px',
                  borderRadius: '12px',
                  color: "var(--app-neutral-ink, #000000)"
                }}
              />

              <p>{uiText("We amplify your reach to a wider pool of qualified Lebanese candidates available right here on JOBSEEQR. Try for free for a week, money back guaranteed:")}</p>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                <button style={{ '--light-control-fill': '#568DED',
                  marginRight: '20px',
                  backgroundColor: '#568DED',
                  fontWeight: "var(--app-weight-strong, bold)",
                  borderRadius: '20px',
                  fontSize: '16px',
                  border: 'none',
                  color: 'white',
                  padding: '10px 20px',
                  cursor: 'pointer'
                }}  data-light-control={"filled"}>{uiText("Monthly - $19.99/mo")}</button>
                <button style={{ '--light-control-fill': '#568DED',
                  backgroundColor: '#568DED',
                  fontWeight: "var(--app-weight-strong, bold)",
                  borderRadius: '20px',
                  fontSize: '16px',
                  border: 'none',
                  color: 'white',
                  padding: '10px 20px',
                  cursor: 'pointer'
                }}  data-light-control={"filled"}>{uiText("Yearly - $15.99/mo (20% off)")}</button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Popup */}
        <SubscriptionPopup
          isOpen={showSubscriptionPopup}
          onClose={() => setShowSubscriptionPopup(false)}
        />

        {/* Notifications Modal */}
        {showNotifsModal && (
          <div className="modal-overlay" onClick={() => setShowNotifsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{uiText("Notifications")}</h2>
                <div className="close" onClick={() => setShowNotifsModal(false)}>&times;</div>
              </div>
              {notifs.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 16px' }}>
                  <button
                    className="see-all"
                    onClick={clearNotifications}
                    style={{
                      padding: 0,
                      fontSize: '12px',
                      color: '#568ded',
                      background: 'transparent',
                      border: 'none',
                      fontWeight: 600
                    }}
                    data-light-control={"quiet"}>
                    {t('notifications.clear', 'Clear')}
                  </button>
                </div>
              )}
              <div className="form-section" style={{ paddingTop: '16px' }}>
                {notifs.length === 0 && (
                  <p style={{ color: 'var(--app-heading, #031128)' }}>{uiText("No notifications")}</p>
                )}
                {notifs.map(n => (
                  <div key={n.id} style={{
                    background: '#F8F9FA',
                    border: '1px solid #E0E0E0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '10px',
                    color: 'var(--app-heading, #031128)',
                    fontFamily: 'Montserrat'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <i className="fa-solid fa-bell" style={{ color: '#568ded' }}></i>
                      <strong>{systemRecordText(n, 'title') || uiText('Notification')}</strong>
                    </div>
                    <div style={{ opacity: 0.9 }}>
                      {systemRecordText(n, 'text') || systemRecordText(n, 'message') || ''}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        {formatNotificationDate(n.timestamp)}
                      </span>
                      {getNotificationAction(n) && (
                        <button
                          className="btn-primary"
                          onClick={() => navigateToNotification(n)}
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                        >
                          {t('notifications.open', 'Open')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Job Description Editor */}
        {showFullscreenEditor && (
          <div className="fullscreen-editor-overlay" onClick={closeFullscreenEditor}>
            <div className="fullscreen-editor-modal" onClick={(e) => e.stopPropagation()}>
              <div className="fullscreen-editor-header">
                <h2>{t('formLabels.jobDescriptionEditor', 'Job Description Editor')}</h2>
                <div className="fullscreen-editor-actions">
                  <button
                    onClick={() => handleAIGenerate('fullscreen')}
                    className="btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    disabled={aiGenerating}
                  >
                    {aiGenerating ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> {t('formLabels.generating', 'Generating...')}
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-wand-magic-sparkles"></i> {t('formLabels.generate', 'Generate')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={closeFullscreenEditor}
                    className="btn-primary"
                  >
                    <i className="fa-solid fa-check"></i> {t('formLabels.done', 'Done')}
                  </button>
                  <button
                    onClick={closeFullscreenEditor}
                    className="close"
                  >
                    &times;
                  </button>
                </div>
              </div>
              <div className="fullscreen-editor-content">
                <div
                  id="fullscreen-description"
                  className="fullscreen-quill-editor"
                  ref={fullscreenDescriptionRef}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default Home; 
